// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ModelEntity from './configuration.modelEntity';
import * as ModelMapping from './configuration.modelMapping';
import * as ModelConstraint from './configuration.modelConstraint';
import * as Export from './configuration.export';
import * as ModelExport from './configuration.modelExport';
import * as ProjectionIntegration from './configuration.projectionIntegration';

export enum AssignmentStates {
    Unassigned,
    UserAssigned,
    SystemAssigned
}

export enum EntityStates {
    Valid,
    Invalid,
    Incomplete
}

export enum OptionStates {
    Assignable,
    Conflicting,
    Blocked
}

export enum CreatedChildTypes {
    Descendant,
    Orphan
}

export enum ValidatorTypes {
    Error,
    Warning
}

export function isEntityModel(source: any): source is IEntity {
    // Method Implementation: simple
    return source.modelName === 'Entity';
}

export function isChildModel(source: any): source is IChild {
    // Method Implementation: simple
    return source.modelName === 'Child';
}

export function isLinkedMapperModel(source: any): source is ILinkedMapper {
    // Method Implementation: simple
    return source.modelName === 'LinkedMapper';
}

export function isLinkedConstraintModelModel(source: any): source is ILinkedConstraintModel {
    // Method Implementation: simple
    return source.modelName === 'LinkedConstraintModel';
}

export function isConstraintOptionStateModel(source: any): source is IConstraintOptionState {
    // Method Implementation: simple
    return source.modelName === 'ConstraintOptionState';
}

export function isConstraintValueModel(source: any): source is IConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'ConstraintValue';
}

export function isStringConstraintValueModel(source: any): source is IStringConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'StringConstraintValue';
}

export function isNumericConstraintValueModel(source: any): source is INumericConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'NumericConstraintValue';
}

export function isBooleanConstraintValueModel(source: any): source is IBooleanConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'BooleanConstraintValue';
}

export function isMultiStringConstraintValueModel(source: any): source is IMultiStringConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'MultiStringConstraintValue';
}

export function isMultiNumericConstraintValueModel(source: any): source is IMultiNumericConstraintValue {
    // Method Implementation: simple
    return source.modelName === 'MultiNumericConstraintValue';
}

export function isConstraintValidationMessageModel(source: any): source is IConstraintValidationMessage {
    // Method Implementation: simple
    return source.modelName === 'ConstraintValidationMessage';
}

export function isVariableModel(source: any): source is IVariable {
    // Method Implementation: simple
    return source.modelName === 'Variable';
}

export function isStringVariableModel(source: any): source is IStringVariable {
    // Method Implementation: simple
    return source.modelName === 'StringVariable';
}

export function isNumericVariableModel(source: any): source is INumericVariable {
    // Method Implementation: simple
    return source.modelName === 'NumericVariable';
}

export function isBooleanVariableModel(source: any): source is IBooleanVariable {
    // Method Implementation: simple
    return source.modelName === 'BooleanVariable';
}

export function isStringEnumerableVariableModel(source: any): source is IStringEnumerableVariable {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableVariable';
}

export function isNumericEnumerableVariableModel(source: any): source is INumericEnumerableVariable {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableVariable';
}

export function isMultiStringVariableModel(source: any): source is IMultiStringVariable {
    // Method Implementation: simple
    return source.modelName === 'MultiStringVariable';
}

export function isMultiNumericVariableModel(source: any): source is IMultiNumericVariable {
    // Method Implementation: simple
    return source.modelName === 'MultiNumericVariable';
}

export function isVariableOptionModel(source: any): source is IVariableOption {
    // Method Implementation: simple
    return source.modelName === 'VariableOption';
}

export function isStringVariableOptionModel(source: any): source is IStringVariableOption {
    // Method Implementation: simple
    return source.modelName === 'StringVariableOption';
}

export function isNumericVariableOptionModel(source: any): source is INumericVariableOption {
    // Method Implementation: simple
    return source.modelName === 'NumericVariableOption';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export function isStringFieldModel(source: any): source is IStringField {
    // Method Implementation: simple
    return source.modelName === 'StringField';
}

export function isNumericFieldModel(source: any): source is INumericField {
    // Method Implementation: simple
    return source.modelName === 'NumericField';
}

export function isBooleanFieldModel(source: any): source is IBooleanField {
    // Method Implementation: simple
    return source.modelName === 'BooleanField';
}

export function isStringEnumerableFieldModel(source: any): source is IStringEnumerableField {
    // Method Implementation: simple
    return source.modelName === 'StringEnumerableField';
}

export function isNumericEnumerableFieldModel(source: any): source is INumericEnumerableField {
    // Method Implementation: simple
    return source.modelName === 'NumericEnumerableField';
}

export function isFieldOptionModel(source: any): source is IFieldOption {
    // Method Implementation: simple
    return source.modelName === 'FieldOption';
}

export function isStringFieldOptionModel(source: any): source is IStringFieldOption {
    // Method Implementation: simple
    return source.modelName === 'StringFieldOption';
}

export function isNumericFieldOptionModel(source: any): source is INumericFieldOption {
    // Method Implementation: simple
    return source.modelName === 'NumericFieldOption';
}

export function isValidatorModel(source: any): source is IValidator {
    // Method Implementation: simple
    return source.modelName === 'Validator';
}

export function isUpdateResultModel(source: any): source is IUpdateResult {
    // Method Implementation: simple
    return source.modelName === 'UpdateResult';
}

export function isTransientActionModel(source: any): source is ITransientAction {
    // Method Implementation: simple
    return source.modelName === 'TransientAction';
}

export function isConflictScenarioModel(source: any): source is IConflictScenario {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenario';
}

export function isConflictScenarioChangedVariableModel(source: any): source is IConflictScenarioChangedVariable {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenarioChangedVariable';
}

export function isConflictScenarioConflictedVariableModel(source: any): source is IConflictScenarioConflictedVariable {
    // Method Implementation: simple
    return source.modelName === 'ConflictScenarioConflictedVariable';
}

export function isEntityStateManagerModel(source: any): source is IEntityStateManager {
    // Method Implementation: simple
    return source.modelName === 'EntityStateManager';
}

export function isEntityStateModel(source: any): source is IEntityState {
    // Method Implementation: simple
    return source.modelName === 'EntityState';
}

export function isEntityDefaultModel(source: any): source is IEntityDefault {
    // Method Implementation: simple
    return source.modelName === 'EntityDefault';
}

export function isMacroTestResultModel(source: any): source is IMacroTestResult {
    // Method Implementation: simple
    return source.modelName === 'MacroTestResult';
}

export interface EntityZodSchemaShape {
    entityId?: string;

    businessUnit?: string;

    mappers?: LinkedMapperZodSchemaShape[];

    constraintModels?: LinkedConstraintModelZodSchemaShape[];

    constraintModelTemplates?: ModelEntity.ConstraintModelTemplateZodSchemaShape[];

    childTemplates?: ModelEntity.ChildTemplateZodSchemaShape[];

    variables?: VariableZodSchemaShape[];

    fields?: FieldZodSchemaShape[];

    children?: ChildZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    conflict?: ConflictScenarioZodSchemaShape;

    validators?: ValidatorZodSchemaShape[];

    stateKey?: string;

    stateIndex?: number;

    variableAssignments?: string[];
}

export interface ChildZodSchemaShape {
    name?: string;

    type?: string;

    entity?: EntityZodSchemaShape;
}

export interface LinkedMapperZodSchemaShape {
    name?: string;

    mapper?: ModelMapping.MapperZodSchemaShape;
}

export interface LinkedConstraintModelZodSchemaShape {
    name?: string;

    priority?: string;

    constraintModel?: ModelConstraint.ConstraintModelZodSchemaShape;

    enabled?: string;
}

export interface ConstraintOptionStateZodSchemaShape {
    constraintModel?: string;

    priority?: number;

    state?: 'Assignable' | 'Conflicting' | 'Blocked';

    messages?: string[];
}

export interface ConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;
}

export interface StringConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;

    value?: string;
}

export interface NumericConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;

    value?: number;
}

export interface BooleanConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;

    value?: boolean;
}

export interface MultiStringConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;

    values?: string[];
}

export interface MultiNumericConstraintValueZodSchemaShape {
    constraintValueType?: string;

    constraintEngine?: 'Configit' | 'Mapper' | 'Reflection' | 'FanSelection' | 'Macro' | 'Recommendation' | 'Static' | 'AssignedValue';

    constraintModel?: string;

    priority?: number;

    important?: boolean;

    values?: number[];
}

export interface ConstraintValidationMessageZodSchemaShape {
    constraintModel?: string;

    priority?: number;

    message?: string;
}

export interface VariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;
}

export interface StringVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: StringConstraintValueZodSchemaShape[];
}

export interface NumericVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: NumericConstraintValueZodSchemaShape[];

    precision?: number;

    min?: string;

    max?: string;
}

export interface BooleanVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: BooleanConstraintValueZodSchemaShape[];
}

export interface StringEnumerableVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: StringConstraintValueZodSchemaShape[];

    options?: StringVariableOptionZodSchemaShape[];
}

export interface NumericEnumerableVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: NumericConstraintValueZodSchemaShape[];

    options?: NumericVariableOptionZodSchemaShape[];

    precision?: number;

    min?: string;

    max?: string;
}

export interface MultiStringVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: MultiStringConstraintValueZodSchemaShape[];
}

export interface MultiNumericVariableZodSchemaShape {
    variableType?: string;

    name?: string;

    constraint?: string;

    defaultExpression?: string;

    required?: string;

    strict?: string;

    frozen?: boolean;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    assignedValue?: string;

    constraintValues?: MultiNumericConstraintValueZodSchemaShape[];

    precision?: number;

    min?: string;

    max?: string;
}

export interface VariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    constraintValue?: string;

    constraintStates?: ConstraintOptionStateZodSchemaShape[];

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;
}

export interface StringVariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    constraintValue?: string;

    constraintStates?: ConstraintOptionStateZodSchemaShape[];

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;

    value?: string;
}

export interface NumericVariableOptionZodSchemaShape {
    variableOptionType?: string;

    name?: string;

    constraintValue?: string;

    constraintStates?: ConstraintOptionStateZodSchemaShape[];

    onSelectedActions?: TransientActionZodSchemaShape[];

    applicable?: string;

    value?: number;
}

export interface FieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];
}

export interface StringFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    value?: string;
}

export interface NumericFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    value?: number;
}

export interface BooleanFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    value?: boolean;
}

export interface StringEnumerableFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    value?: string;

    options?: StringFieldOptionZodSchemaShape[];
}

export interface NumericEnumerableFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    source?: string;

    required?: string;

    validators?: ValidatorZodSchemaShape[];

    constraintValidationMessages?: ConstraintValidationMessageZodSchemaShape[];

    value?: number;

    options?: NumericFieldOptionZodSchemaShape[];
}

export interface FieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;
}

export interface StringFieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;

    value?: string;
}

export interface NumericFieldOptionZodSchemaShape {
    fieldOptionType?: string;

    name?: string;

    applicable?: string;

    value?: number;
}

export interface ValidatorZodSchemaShape {
    name?: string;

    condition?: string;

    type?: 'Error' | 'Warning';
}

export interface UpdateResultZodSchemaShape {
    actions?: TransientActionZodSchemaShape[];

    messages?: string[];

    conflict?: ConflictScenarioZodSchemaShape;
}

export interface TransientActionZodSchemaShape {
    name?: string;

    type?: string;

    properties?: any;
}

export interface ConflictScenarioZodSchemaShape {
    changedVariables?: ConflictScenarioChangedVariableZodSchemaShape[];

    conflictedVariables?: ConflictScenarioConflictedVariableZodSchemaShape[];
}

export interface ConflictScenarioChangedVariableZodSchemaShape {
    source?: string;

    oldValue?: string;

    newValue?: string;
}

export interface ConflictScenarioConflictedVariableZodSchemaShape {
    source?: string;

    messages?: string[];
}

export interface EntityStateManagerZodSchemaShape {
    currentStateIndex?: number;

    states?: EntityStateZodSchemaShape[];
}

export interface EntityStateZodSchemaShape {
    key?: string;
}

export interface EntityDefaultZodSchemaShape {
    variable?: string;

    value?: string;

    restrict?: boolean;

    userAssign?: boolean;
}

export interface MacroTestResultZodSchemaShape {
    resolvedValue?: string;

    duration?: number;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityZodSchema: Zod.ZodSchema<EntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    businessUnit: Zod.string().optional(),
    mappers: Zod.array(LinkedMapperZodSchema).optional(),
    constraintModels: Zod.array(LinkedConstraintModelZodSchema).optional(),
    constraintModelTemplates: Zod.array(ModelEntity.ConstraintModelTemplateZodSchema).optional(),
    childTemplates: Zod.array(ModelEntity.ChildTemplateZodSchema).optional(),
    variables: Zod.array(VariableZodSchema).optional(),
    fields: Zod.array(FieldZodSchema).optional(),
    children: Zod.array(ChildZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    conflict: ConflictScenarioZodSchema.optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    stateKey: Zod.string().optional(),
    stateIndex: Zod.number().optional(),
    variableAssignments: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ChildZodSchema: Zod.ZodSchema<ChildZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    entity: EntityZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedMapperZodSchema: Zod.ZodSchema<LinkedMapperZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    mapper: ModelMapping.MapperZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const LinkedConstraintModelZodSchema: Zod.ZodSchema<LinkedConstraintModelZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    priority: Zod.string().optional(),
    constraintModel: ModelConstraint.ConstraintModelZodSchema.optional(),
    enabled: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConstraintOptionStateZodSchema: Zod.ZodSchema<ConstraintOptionStateZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    state: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional(),
    messages: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConstraintValueZodSchema: Zod.ZodSchema<ConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringConstraintValueZodSchema: Zod.ZodSchema<StringConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericConstraintValueZodSchema: Zod.ZodSchema<NumericConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanConstraintValueZodSchema: Zod.ZodSchema<BooleanConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiStringConstraintValueZodSchema: Zod.ZodSchema<MultiStringConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional(),
    values: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiNumericConstraintValueZodSchema: Zod.ZodSchema<MultiNumericConstraintValueZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintValueType: Zod.string(),
    constraintEngine: Zod.enum([ 'Configit', 'Mapper', 'Reflection', 'FanSelection', 'Macro', 'Recommendation', 'Static', 'AssignedValue' ]).optional(),
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    important: Zod.boolean().optional(),
    values: Zod.array(Zod.number()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConstraintValidationMessageZodSchema: Zod.ZodSchema<ConstraintValidationMessageZodSchemaShape> = Zod.lazy(() => Zod.object({
    constraintModel: Zod.string().optional(),
    priority: Zod.number().optional(),
    message: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const VariableZodSchema: Zod.ZodSchema<VariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringVariableZodSchema: Zod.ZodSchema<StringVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(StringConstraintValueZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericVariableZodSchema: Zod.ZodSchema<NumericVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(NumericConstraintValueZodSchema).optional(),
    precision: Zod.number().optional(),
    min: Zod.string().optional(),
    max: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanVariableZodSchema: Zod.ZodSchema<BooleanVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(BooleanConstraintValueZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableVariableZodSchema: Zod.ZodSchema<StringEnumerableVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(StringConstraintValueZodSchema).optional(),
    options: Zod.array(StringVariableOptionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableVariableZodSchema: Zod.ZodSchema<NumericEnumerableVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(NumericConstraintValueZodSchema).optional(),
    options: Zod.array(NumericVariableOptionZodSchema).optional(),
    precision: Zod.number().optional(),
    min: Zod.string().optional(),
    max: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiStringVariableZodSchema: Zod.ZodSchema<MultiStringVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(MultiStringConstraintValueZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MultiNumericVariableZodSchema: Zod.ZodSchema<MultiNumericVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableType: Zod.string(),
    name: Zod.string().optional(),
    constraint: Zod.string().optional(),
    defaultExpression: Zod.string().optional(),
    required: Zod.string().optional(),
    strict: Zod.string().optional(),
    frozen: Zod.boolean().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    assignedValue: Zod.string().optional(),
    constraintValues: Zod.array(MultiNumericConstraintValueZodSchema).optional(),
    precision: Zod.number().optional(),
    min: Zod.string().optional(),
    max: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const VariableOptionZodSchema: Zod.ZodSchema<VariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    constraintValue: Zod.string().optional(),
    constraintStates: Zod.array(ConstraintOptionStateZodSchema).optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringVariableOptionZodSchema: Zod.ZodSchema<StringVariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    constraintValue: Zod.string().optional(),
    constraintStates: Zod.array(ConstraintOptionStateZodSchema).optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericVariableOptionZodSchema: Zod.ZodSchema<NumericVariableOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    variableOptionType: Zod.string(),
    name: Zod.string().optional(),
    constraintValue: Zod.string().optional(),
    constraintStates: Zod.array(ConstraintOptionStateZodSchema).optional(),
    onSelectedActions: Zod.array(TransientActionZodSchema).optional(),
    applicable: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldZodSchema: Zod.ZodSchema<StringFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericFieldZodSchema: Zod.ZodSchema<NumericFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanFieldZodSchema: Zod.ZodSchema<BooleanFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    value: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringEnumerableFieldZodSchema: Zod.ZodSchema<StringEnumerableFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    value: Zod.string().optional(),
    options: Zod.array(StringFieldOptionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericEnumerableFieldZodSchema: Zod.ZodSchema<NumericEnumerableFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    source: Zod.string().optional(),
    required: Zod.string().optional(),
    validators: Zod.array(ValidatorZodSchema).optional(),
    constraintValidationMessages: Zod.array(ConstraintValidationMessageZodSchema).optional(),
    value: Zod.number().optional(),
    options: Zod.array(NumericFieldOptionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldOptionZodSchema: Zod.ZodSchema<FieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldOptionZodSchema: Zod.ZodSchema<StringFieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const NumericFieldOptionZodSchema: Zod.ZodSchema<NumericFieldOptionZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldOptionType: Zod.string(),
    name: Zod.string().optional(),
    applicable: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValidatorZodSchema: Zod.ZodSchema<ValidatorZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    condition: Zod.string().optional(),
    type: Zod.enum([ 'Error', 'Warning' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const UpdateResultZodSchema: Zod.ZodSchema<UpdateResultZodSchemaShape> = Zod.lazy(() => Zod.object({
    actions: Zod.array(TransientActionZodSchema).optional(),
    messages: Zod.array(Zod.string()).optional(),
    conflict: ConflictScenarioZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const TransientActionZodSchema: Zod.ZodSchema<TransientActionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    type: Zod.string().optional(),
    properties: Zod.any().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioZodSchema: Zod.ZodSchema<ConflictScenarioZodSchemaShape> = Zod.lazy(() => Zod.object({
    changedVariables: Zod.array(ConflictScenarioChangedVariableZodSchema).optional(),
    conflictedVariables: Zod.array(ConflictScenarioConflictedVariableZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioChangedVariableZodSchema: Zod.ZodSchema<ConflictScenarioChangedVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    oldValue: Zod.string().optional(),
    newValue: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ConflictScenarioConflictedVariableZodSchema: Zod.ZodSchema<ConflictScenarioConflictedVariableZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    messages: Zod.array(Zod.string()).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityStateManagerZodSchema: Zod.ZodSchema<EntityStateManagerZodSchemaShape> = Zod.lazy(() => Zod.object({
    currentStateIndex: Zod.number().optional(),
    states: Zod.array(EntityStateZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityStateZodSchema: Zod.ZodSchema<EntityStateZodSchemaShape> = Zod.lazy(() => Zod.object({
    key: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityDefaultZodSchema: Zod.ZodSchema<EntityDefaultZodSchemaShape> = Zod.lazy(() => Zod.object({
    variable: Zod.string().optional(),
    value: Zod.string().optional(),
    restrict: Zod.boolean().optional(),
    userAssign: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MacroTestResultZodSchema: Zod.ZodSchema<MacroTestResultZodSchemaShape> = Zod.lazy(() => Zod.object({
    resolvedValue: Zod.string().optional(),
    duration: Zod.number().optional()
}).nonstrict());

export interface IEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityId: string;

    readonly businessUnit: string;

    readonly mappers: LinkedMapper[];

    readonly constraintModels: LinkedConstraintModel[];

    readonly constraintModelTemplates: ModelEntity.ConstraintModelTemplate[];

    readonly childTemplates: ModelEntity.AChildTemplate[];

    readonly variables: AVariable[];

    readonly fields: AField[];

    readonly children: Child[];

    readonly constraintValidationMessages: ConstraintValidationMessage[];

    readonly conflict: ConflictScenario;

    readonly validators: Validator[];

    readonly stateKey: string;

    readonly stateIndex: number;

    readonly variableAssignments: string[];

    readonly isEntityIdDefined: boolean;

    readonly isBusinessUnitDefined: boolean;

    readonly isMappersDefined: boolean;

    readonly isConstraintModelsDefined: boolean;

    readonly isConstraintModelTemplatesDefined: boolean;

    readonly isChildTemplatesDefined: boolean;

    readonly isVariablesDefined: boolean;

    readonly isFieldsDefined: boolean;

    readonly isChildrenDefined: boolean;

    readonly isConstraintValidationMessagesDefined: boolean;

    readonly isConflictDefined: boolean;

    readonly isValidatorsDefined: boolean;

    readonly isStateKeyDefined: boolean;

    readonly isStateIndexDefined: boolean;

    readonly isVariableAssignmentsDefined: boolean;
}

export interface IChild extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: string;

    readonly entity: Entity;

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isEntityDefined: boolean;
}

export interface ILinkedMapper extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly mapper: ModelMapping.Mapper;

    readonly isNameDefined: boolean;

    readonly isMapperDefined: boolean;
}

export interface ILinkedConstraintModel extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly priority: string;

    readonly constraintModel: ModelConstraint.ConstraintModel;

    readonly enabled: string;

    readonly isNameDefined: boolean;

    readonly isPriorityDefined: boolean;

    readonly isConstraintModelDefined: boolean;

    readonly isEnabledDefined: boolean;
}

export interface IConstraintOptionState extends Wellspring.IModel {
    readonly modelName: string;

    readonly constraintModel: string;

    readonly priority: number;

    readonly state: OptionStates;

    readonly messages: string[];

    readonly isConstraintModelDefined: boolean;

    readonly isPriorityDefined: boolean;

    readonly isStateDefined: boolean;

    readonly isMessagesDefined: boolean;
}

export interface IConstraintValue extends Wellspring.IModel {
    readonly modelName: string;

    readonly constraintEngine: ModelConstraint.ConstraintEngineTypes;

    readonly constraintModel: string;

    readonly priority: number;

    readonly important: boolean;

    readonly isConstraintEngineDefined: boolean;

    readonly isConstraintModelDefined: boolean;

    readonly isPriorityDefined: boolean;

    readonly isImportantDefined: boolean;
}

export interface IStringConstraintValue extends IConstraintValue, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericConstraintValue extends IConstraintValue, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IBooleanConstraintValue extends IConstraintValue, Wellspring.IModel {
    readonly modelName: string;

    readonly value: boolean;

    readonly isValueDefined: boolean;
}

export interface IMultiStringConstraintValue extends IConstraintValue, Wellspring.IModel {
    readonly modelName: string;

    readonly values: string[];

    readonly isValuesDefined: boolean;
}

export interface IMultiNumericConstraintValue extends IConstraintValue, Wellspring.IModel {
    readonly modelName: string;

    readonly values: number[];

    readonly isValuesDefined: boolean;
}

export interface IConstraintValidationMessage extends Wellspring.IModel {
    readonly modelName: string;

    readonly constraintModel: string;

    readonly priority: number;

    readonly message: string;

    readonly isConstraintModelDefined: boolean;

    readonly isPriorityDefined: boolean;

    readonly isMessageDefined: boolean;
}

export interface IVariable extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly constraint: string;

    readonly defaultExpression: string;

    readonly required: string;

    readonly strict: string;

    readonly frozen: boolean;

    readonly validators: Validator[];

    readonly constraintValidationMessages: ConstraintValidationMessage[];

    readonly assignedValue: string;

    readonly isNameDefined: boolean;

    readonly isConstraintDefined: boolean;

    readonly isDefaultExpressionDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isStrictDefined: boolean;

    readonly isFrozenDefined: boolean;

    readonly isValidatorsDefined: boolean;

    readonly isConstraintValidationMessagesDefined: boolean;

    readonly isAssignedValueDefined: boolean;
}

export interface IStringVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: StringConstraintValue[];

    readonly isConstraintValuesDefined: boolean;
}

export interface INumericVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: NumericConstraintValue[];

    readonly precision: number;

    readonly min: string;

    readonly max: string;

    readonly isConstraintValuesDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IBooleanVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: BooleanConstraintValue[];

    readonly isConstraintValuesDefined: boolean;
}

export interface IStringEnumerableVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: StringConstraintValue[];

    readonly options: StringVariableOption[];

    readonly isConstraintValuesDefined: boolean;

    readonly isOptionsDefined: boolean;
}

export interface INumericEnumerableVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: NumericConstraintValue[];

    readonly options: NumericVariableOption[];

    readonly precision: number;

    readonly min: string;

    readonly max: string;

    readonly isConstraintValuesDefined: boolean;

    readonly isOptionsDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IMultiStringVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: MultiStringConstraintValue[];

    readonly isConstraintValuesDefined: boolean;
}

export interface IMultiNumericVariable extends IVariable, Wellspring.IModel {
    readonly modelName: string;

    readonly constraintValues: MultiNumericConstraintValue[];

    readonly precision: number;

    readonly min: string;

    readonly max: string;

    readonly isConstraintValuesDefined: boolean;

    readonly isPrecisionDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;
}

export interface IVariableOption extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly constraintValue: string;

    readonly constraintStates: ConstraintOptionState[];

    readonly onSelectedActions: TransientAction[];

    readonly applicable: string;

    readonly isNameDefined: boolean;

    readonly isConstraintValueDefined: boolean;

    readonly isConstraintStatesDefined: boolean;

    readonly isOnSelectedActionsDefined: boolean;

    readonly isApplicableDefined: boolean;
}

export interface IStringVariableOption extends IVariableOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericVariableOption extends IVariableOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly source: string;

    readonly required: string;

    readonly validators: Validator[];

    readonly constraintValidationMessages: ConstraintValidationMessage[];

    readonly isNameDefined: boolean;

    readonly isSourceDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isValidatorsDefined: boolean;

    readonly isConstraintValidationMessagesDefined: boolean;
}

export interface IStringField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IBooleanField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: boolean;

    readonly isValueDefined: boolean;
}

export interface IStringEnumerableField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly options: StringFieldOption[];

    readonly isValueDefined: boolean;

    readonly isOptionsDefined: boolean;
}

export interface INumericEnumerableField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly options: NumericFieldOption[];

    readonly isValueDefined: boolean;

    readonly isOptionsDefined: boolean;
}

export interface IFieldOption extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly applicable: string;

    readonly isNameDefined: boolean;

    readonly isApplicableDefined: boolean;
}

export interface IStringFieldOption extends IFieldOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface INumericFieldOption extends IFieldOption, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IValidator extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly condition: string;

    readonly type: ValidatorTypes;

    readonly isNameDefined: boolean;

    readonly isConditionDefined: boolean;

    readonly isTypeDefined: boolean;
}

export interface IUpdateResult extends Wellspring.IModel {
    readonly modelName: string;

    readonly actions: TransientAction[];

    readonly messages: string[];

    readonly conflict: ConflictScenario;

    readonly isActionsDefined: boolean;

    readonly isMessagesDefined: boolean;

    readonly isConflictDefined: boolean;
}

export interface ITransientAction extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly type: string;

    readonly properties: Record<string, string>;

    readonly isNameDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isPropertiesDefined: boolean;
}

export interface IConflictScenario extends Wellspring.IModel {
    readonly modelName: string;

    readonly changedVariables: ConflictScenarioChangedVariable[];

    readonly conflictedVariables: ConflictScenarioConflictedVariable[];

    readonly isChangedVariablesDefined: boolean;

    readonly isConflictedVariablesDefined: boolean;
}

export interface IConflictScenarioChangedVariable extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly oldValue: string;

    readonly newValue: string;

    readonly isSourceDefined: boolean;

    readonly isOldValueDefined: boolean;

    readonly isNewValueDefined: boolean;
}

export interface IConflictScenarioConflictedVariable extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly messages: string[];

    readonly isSourceDefined: boolean;

    readonly isMessagesDefined: boolean;
}

export interface IEntityStateManager extends Wellspring.IModel {
    readonly modelName: string;

    readonly currentStateIndex: number;

    readonly states: EntityState[];

    readonly isCurrentStateIndexDefined: boolean;

    readonly isStatesDefined: boolean;
}

export interface IEntityState extends Wellspring.IModel {
    readonly modelName: string;

    readonly key: string;

    readonly isKeyDefined: boolean;
}

export interface IEntityDefault extends Wellspring.IModel {
    readonly modelName: string;

    readonly variable: string;

    readonly value: string;

    readonly restrict: boolean;

    readonly userAssign: boolean;

    readonly isVariableDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isRestrictDefined: boolean;

    readonly isUserAssignDefined: boolean;
}

export interface IMacroTestResult extends Wellspring.IModel {
    readonly modelName: string;

    readonly resolvedValue: string;

    readonly duration: number;

    readonly isResolvedValueDefined: boolean;

    readonly isDurationDefined: boolean;
}

export class Entity implements IEntity {
    public static readonly modelName: string = 'Entity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityId', 'businessUnit', 'mappers', 'constraintModels', 'constraintModelTemplates', 'childTemplates', 'variables', 'fields', 'children', 'constraintValidationMessages', 'conflict', 'validators', 'stateKey', 'stateIndex', 'variableAssignments' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Entity.modelName;
    }

    private _entityId: string;

    private _businessUnit: string;

    private _mappers: LinkedMapper[];

    private _constraintModels: LinkedConstraintModel[];

    private _constraintModelTemplates: ModelEntity.ConstraintModelTemplate[];

    private _childTemplates: ModelEntity.AChildTemplate[];

    private _variables: AVariable[];

    private _fields: AField[];

    private _children: Child[];

    private _constraintValidationMessages: ConstraintValidationMessage[];

    private _conflict: ConflictScenario;

    private _validators: Validator[];

    private _stateKey: string;

    private _stateIndex: number;

    private _variableAssignments: string[];

    public get entityId(): string {
        // Method Implementation: simple
        return this._entityId;
    }

    public get businessUnit(): string {
        // Method Implementation: simple
        return this._businessUnit;
    }

    public get mappers(): LinkedMapper[] {
        // Method Implementation: simple
        return this._mappers;
    }

    public get constraintModels(): LinkedConstraintModel[] {
        // Method Implementation: simple
        return this._constraintModels;
    }

    public get constraintModelTemplates(): ModelEntity.ConstraintModelTemplate[] {
        // Method Implementation: simple
        return this._constraintModelTemplates;
    }

    public get childTemplates(): ModelEntity.AChildTemplate[] {
        // Method Implementation: simple
        return this._childTemplates;
    }

    public get variables(): AVariable[] {
        // Method Implementation: simple
        return this._variables;
    }

    public get fields(): AField[] {
        // Method Implementation: simple
        return this._fields;
    }

    public get children(): Child[] {
        // Method Implementation: simple
        return this._children;
    }

    public get constraintValidationMessages(): ConstraintValidationMessage[] {
        // Method Implementation: simple
        return this._constraintValidationMessages;
    }

    public get conflict(): ConflictScenario {
        // Method Implementation: simple
        return this._conflict;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public get stateKey(): string {
        // Method Implementation: simple
        return this._stateKey;
    }

    public get stateIndex(): number {
        // Method Implementation: simple
        return this._stateIndex;
    }

    public get variableAssignments(): string[] {
        // Method Implementation: simple
        return this._variableAssignments;
    }

    public set entityId(value: string) {
        // Method Implementation: simple
        this._entityId = value;
        this._isEntityIdDefined = true;
    }

    public set businessUnit(value: string) {
        // Method Implementation: simple
        this._businessUnit = value;
        this._isBusinessUnitDefined = true;
    }

    public set mappers(value: LinkedMapper[]) {
        // Method Implementation: simple
        this._mappers = value;
        this._isMappersDefined = true;
    }

    public set constraintModels(value: LinkedConstraintModel[]) {
        // Method Implementation: simple
        this._constraintModels = value;
        this._isConstraintModelsDefined = true;
    }

    public set constraintModelTemplates(value: ModelEntity.ConstraintModelTemplate[]) {
        // Method Implementation: simple
        this._constraintModelTemplates = value;
        this._isConstraintModelTemplatesDefined = true;
    }

    public set childTemplates(value: ModelEntity.AChildTemplate[]) {
        // Method Implementation: simple
        this._childTemplates = value;
        this._isChildTemplatesDefined = true;
    }

    public set variables(value: AVariable[]) {
        // Method Implementation: simple
        this._variables = value;
        this._isVariablesDefined = true;
    }

    public set fields(value: AField[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    public set children(value: Child[]) {
        // Method Implementation: simple
        this._children = value;
        this._isChildrenDefined = true;
    }

    public set constraintValidationMessages(value: ConstraintValidationMessage[]) {
        // Method Implementation: simple
        this._constraintValidationMessages = value;
        this._isConstraintValidationMessagesDefined = true;
    }

    public set conflict(value: ConflictScenario) {
        // Method Implementation: simple
        this._conflict = value;
        this._isConflictDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    public set stateKey(value: string) {
        // Method Implementation: simple
        this._stateKey = value;
        this._isStateKeyDefined = true;
    }

    public set stateIndex(value: number) {
        // Method Implementation: simple
        this._stateIndex = value;
        this._isStateIndexDefined = true;
    }

    public set variableAssignments(value: string[]) {
        // Method Implementation: simple
        this._variableAssignments = value;
        this._isVariableAssignmentsDefined = true;
    }

    private _isEntityIdDefined: boolean;

    private _isBusinessUnitDefined: boolean;

    private _isMappersDefined: boolean;

    private _isConstraintModelsDefined: boolean;

    private _isConstraintModelTemplatesDefined: boolean;

    private _isChildTemplatesDefined: boolean;

    private _isVariablesDefined: boolean;

    private _isFieldsDefined: boolean;

    private _isChildrenDefined: boolean;

    private _isConstraintValidationMessagesDefined: boolean;

    private _isConflictDefined: boolean;

    private _isValidatorsDefined: boolean;

    private _isStateKeyDefined: boolean;

    private _isStateIndexDefined: boolean;

    private _isVariableAssignmentsDefined: boolean;

    public get isEntityIdDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityIdDefined;
    }

    public get isBusinessUnitDefined(): boolean {
        // Method Implementation: simple
        return this._isBusinessUnitDefined;
    }

    public get isMappersDefined(): boolean {
        // Method Implementation: simple
        return this._isMappersDefined;
    }

    public get isConstraintModelsDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelsDefined;
    }

    public get isConstraintModelTemplatesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelTemplatesDefined;
    }

    public get isChildTemplatesDefined(): boolean {
        // Method Implementation: simple
        return this._isChildTemplatesDefined;
    }

    public get isVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isVariablesDefined;
    }

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public get isChildrenDefined(): boolean {
        // Method Implementation: simple
        return this._isChildrenDefined;
    }

    public get isConstraintValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValidationMessagesDefined;
    }

    public get isConflictDefined(): boolean {
        // Method Implementation: simple
        return this._isConflictDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public get isStateKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isStateKeyDefined;
    }

    public get isStateIndexDefined(): boolean {
        // Method Implementation: simple
        return this._isStateIndexDefined;
    }

    public get isVariableAssignmentsDefined(): boolean {
        // Method Implementation: simple
        return this._isVariableAssignmentsDefined;
    }

    public constructor();
    public constructor(source: IEntity);
    public constructor(source: Partial<Pick<Entity, 'entityId' | 'businessUnit' | 'mappers' | 'constraintModels' | 'constraintModelTemplates' | 'childTemplates' | 'variables' | 'fields' | 'children' | 'constraintValidationMessages' | 'conflict' | 'validators' | 'stateKey' | 'stateIndex' | 'variableAssignments'>>);
    public constructor(source?: IEntity | Partial<Pick<Entity, 'entityId' | 'businessUnit' | 'mappers' | 'constraintModels' | 'constraintModelTemplates' | 'childTemplates' | 'variables' | 'fields' | 'children' | 'constraintValidationMessages' | 'conflict' | 'validators' | 'stateKey' | 'stateIndex' | 'variableAssignments'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityModel(source)) {
                if (source.isEntityIdDefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.isBusinessUnitDefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.isMappersDefined) {
                    this._mappers = source.mappers;
                    this._isMappersDefined = true;
                }
                else {
                    this._mappers = [];
                    this._isMappersDefined = true;
                }
                
                if (source.isConstraintModelsDefined) {
                    this._constraintModels = source.constraintModels;
                    this._isConstraintModelsDefined = true;
                }
                else {
                    this._constraintModels = [];
                    this._isConstraintModelsDefined = true;
                }
                
                if (source.isConstraintModelTemplatesDefined) {
                    this._constraintModelTemplates = source.constraintModelTemplates;
                    this._isConstraintModelTemplatesDefined = true;
                }
                else {
                    this._constraintModelTemplates = [];
                    this._isConstraintModelTemplatesDefined = true;
                }
                
                if (source.isChildTemplatesDefined) {
                    this._childTemplates = source.childTemplates;
                    this._isChildTemplatesDefined = true;
                }
                else {
                    this._childTemplates = [];
                    this._isChildTemplatesDefined = true;
                }
                
                if (source.isVariablesDefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
                
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.isChildrenDefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.isConstraintValidationMessagesDefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
                
                if (source.isConflictDefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.isStateKeyDefined) {
                    this._stateKey = source.stateKey;
                    this._isStateKeyDefined = true;
                }
                else {
                    this._stateKey = '';
                    this._isStateKeyDefined = false;
                }
                
                if (source.isStateIndexDefined) {
                    this._stateIndex = source.stateIndex;
                    this._isStateIndexDefined = true;
                }
                else {
                    this._stateIndex = 0;
                    this._isStateIndexDefined = false;
                }
                
                if (source.isVariableAssignmentsDefined) {
                    this._variableAssignments = source.variableAssignments;
                    this._isVariableAssignmentsDefined = true;
                }
                else {
                    this._variableAssignments = [];
                    this._isVariableAssignmentsDefined = true;
                }
            }
            else {
                if (source.entityId !== undefined) {
                    this._entityId = source.entityId;
                    this._isEntityIdDefined = true;
                }
                else {
                    this._entityId = '';
                    this._isEntityIdDefined = false;
                }
                
                if (source.businessUnit !== undefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.mappers !== undefined) {
                    this._mappers = source.mappers;
                    this._isMappersDefined = true;
                }
                else {
                    this._mappers = [];
                    this._isMappersDefined = true;
                }
                
                if (source.constraintModels !== undefined) {
                    this._constraintModels = source.constraintModels;
                    this._isConstraintModelsDefined = true;
                }
                else {
                    this._constraintModels = [];
                    this._isConstraintModelsDefined = true;
                }
                
                if (source.constraintModelTemplates !== undefined) {
                    this._constraintModelTemplates = source.constraintModelTemplates;
                    this._isConstraintModelTemplatesDefined = true;
                }
                else {
                    this._constraintModelTemplates = [];
                    this._isConstraintModelTemplatesDefined = true;
                }
                
                if (source.childTemplates !== undefined) {
                    this._childTemplates = source.childTemplates;
                    this._isChildTemplatesDefined = true;
                }
                else {
                    this._childTemplates = [];
                    this._isChildTemplatesDefined = true;
                }
                
                if (source.variables !== undefined) {
                    this._variables = source.variables;
                    this._isVariablesDefined = true;
                }
                else {
                    this._variables = [];
                    this._isVariablesDefined = true;
                }
                
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
                
                if (source.children !== undefined) {
                    this._children = source.children;
                    this._isChildrenDefined = true;
                }
                else {
                    this._children = [];
                    this._isChildrenDefined = true;
                }
                
                if (source.constraintValidationMessages !== undefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
                
                if (source.conflict !== undefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.stateKey !== undefined) {
                    this._stateKey = source.stateKey;
                    this._isStateKeyDefined = true;
                }
                else {
                    this._stateKey = '';
                    this._isStateKeyDefined = false;
                }
                
                if (source.stateIndex !== undefined) {
                    this._stateIndex = source.stateIndex;
                    this._isStateIndexDefined = true;
                }
                else {
                    this._stateIndex = 0;
                    this._isStateIndexDefined = false;
                }
                
                if (source.variableAssignments !== undefined) {
                    this._variableAssignments = source.variableAssignments;
                    this._isVariableAssignmentsDefined = true;
                }
                else {
                    this._variableAssignments = [];
                    this._isVariableAssignmentsDefined = true;
                }
            }
        }
        else {
            this._entityId = '';
            this._isEntityIdDefined = false;
        
            this._businessUnit = '';
            this._isBusinessUnitDefined = false;
        
            this._mappers = [];
            this._isMappersDefined = true;
        
            this._constraintModels = [];
            this._isConstraintModelsDefined = true;
        
            this._constraintModelTemplates = [];
            this._isConstraintModelTemplatesDefined = true;
        
            this._childTemplates = [];
            this._isChildTemplatesDefined = true;
        
            this._variables = [];
            this._isVariablesDefined = true;
        
            this._fields = [];
            this._isFieldsDefined = true;
        
            this._children = [];
            this._isChildrenDefined = true;
        
            this._constraintValidationMessages = [];
            this._isConstraintValidationMessagesDefined = true;
        
            this._conflict = new ConflictScenario();
            this._isConflictDefined = true;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        
            this._stateKey = '';
            this._isStateKeyDefined = false;
        
            this._stateIndex = 0;
            this._isStateIndexDefined = false;
        
            this._variableAssignments = [];
            this._isVariableAssignmentsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Entity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityIdDefined && this.entityId !== undefined)
            definedProperties.push('entityId');
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined)
            definedProperties.push('businessUnit');
        if (this.isMappersDefined && this.mappers !== undefined)
            definedProperties.push('mappers');
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined)
            definedProperties.push('constraintModels');
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined)
            definedProperties.push('constraintModelTemplates');
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined)
            definedProperties.push('childTemplates');
        if (this.isVariablesDefined && this.variables !== undefined)
            definedProperties.push('variables');
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        if (this.isChildrenDefined && this.children !== undefined)
            definedProperties.push('children');
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined)
            definedProperties.push('constraintValidationMessages');
        if (this.isConflictDefined && this.conflict !== undefined)
            definedProperties.push('conflict');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        if (this.isStateKeyDefined && this.stateKey !== undefined)
            definedProperties.push('stateKey');
        if (this.isStateIndexDefined && this.stateIndex !== undefined)
            definedProperties.push('stateIndex');
        if (this.isVariableAssignmentsDefined && this.variableAssignments !== undefined)
            definedProperties.push('variableAssignments');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityId':
                return this.entityId;
            case 'businessUnit':
                return this.businessUnit;
            case 'mappers':
                return this.mappers;
            case 'constraintModels':
                return this.constraintModels;
            case 'constraintModelTemplates':
                return this.constraintModelTemplates;
            case 'childTemplates':
                return this.childTemplates;
            case 'variables':
                return this.variables;
            case 'fields':
                return this.fields;
            case 'children':
                return this.children;
            case 'constraintValidationMessages':
                return this.constraintValidationMessages;
            case 'conflict':
                return this.conflict;
            case 'validators':
                return this.validators;
            case 'stateKey':
                return this.stateKey;
            case 'stateIndex':
                return this.stateIndex;
            case 'variableAssignments':
                return this.variableAssignments;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            if (this.entityId !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityId);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityId', 'null'));
            }
        }
        
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            if (this.businessUnit !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.businessUnit);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'null'));
            }
        }
        
        if (this.isMappersDefined && this.mappers !== undefined) {
            if (this.mappers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.mappers.length));
                for (let i = 0; i < this.mappers.length; i++) {
                    const item = this.mappers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappers', 'null'));
            }
        }
        
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined) {
            if (this.constraintModels !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModels', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintModels.length));
                for (let i = 0; i < this.constraintModels.length; i++) {
                    const item = this.constraintModels[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModels', 'null'));
            }
        }
        
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined) {
            if (this.constraintModelTemplates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModelTemplates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintModelTemplates.length));
                for (let i = 0; i < this.constraintModelTemplates.length; i++) {
                    const item = this.constraintModelTemplates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModelTemplates', 'null'));
            }
        }
        
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined) {
            if (this.childTemplates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childTemplates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.childTemplates.length));
                for (let i = 0; i < this.childTemplates.length; i++) {
                    const item = this.childTemplates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('childTemplates', 'null'));
            }
        }
        
        if (this.isVariablesDefined && this.variables !== undefined) {
            if (this.variables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variables.length));
                for (let i = 0; i < this.variables.length; i++) {
                    const item = this.variables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variables', 'null'));
            }
        }
        
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
        
        if (this.isChildrenDefined && this.children !== undefined) {
            if (this.children !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.children.length));
                for (let i = 0; i < this.children.length; i++) {
                    const item = this.children[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('children', 'null'));
            }
        }
        
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            if (this.constraintValidationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValidationMessages.length));
                for (let i = 0; i < this.constraintValidationMessages.length; i++) {
                    const item = this.constraintValidationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'null'));
            }
        }
        
        if (this.isConflictDefined && this.conflict !== undefined) {
            if (this.conflict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.conflict.getModelName(), this.conflict.getDefinedPropertyNames().length));
                this.conflict.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
        
        if (this.isStateKeyDefined && this.stateKey !== undefined) {
            if (this.stateKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('stateKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.stateKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('stateKey', 'null'));
            }
        }
        
        if (this.isStateIndexDefined && this.stateIndex !== undefined) {
            if (this.stateIndex !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('stateIndex', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.stateIndex);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('stateIndex', 'null'));
            }
        }
        
        if (this.isVariableAssignmentsDefined && this.variableAssignments !== undefined) {
            if (this.variableAssignments !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variableAssignments', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.variableAssignments.length));
                for (let i = 0; i < this.variableAssignments.length; i++) {
                    const item = this.variableAssignments[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variableAssignments', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityId':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityId = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityId' cannot be null`);
                    this.entityId = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityId'`);
                }
        
                return true;
            case 'businessUnit':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.businessUnit = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'businessUnit' cannot be null`);
                    this.businessUnit = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'businessUnit'`);
                }
        
                return true;
            case 'mappers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.mappers = [];
                
                    let item: LinkedMapper = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LinkedMapper.modelName) {
                                item = new LinkedMapper();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LinkedMapper.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'mappers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'mappers'`);
                        }
                
                        this.mappers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mappers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mappers'`);
                }
        
                return true;
            case 'constraintModels':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintModels = [];
                
                    let item: LinkedConstraintModel = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === LinkedConstraintModel.modelName) {
                                item = new LinkedConstraintModel();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${LinkedConstraintModel.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintModels' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintModels'`);
                        }
                
                        this.constraintModels.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModels' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModels'`);
                }
        
                return true;
            case 'constraintModelTemplates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintModelTemplates = [];
                
                    let item: ModelEntity.ConstraintModelTemplate = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ModelEntity.ConstraintModelTemplate.modelName) {
                                item = new ModelEntity.ConstraintModelTemplate();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelEntity.ConstraintModelTemplate.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintModelTemplates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintModelTemplates'`);
                        }
                
                        this.constraintModelTemplates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModelTemplates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModelTemplates'`);
                }
        
                return true;
            case 'childTemplates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.childTemplates = [];
                
                    let item: ModelEntity.AChildTemplate = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelEntity.ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'childTemplates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'childTemplates'`);
                        }
                
                        this.childTemplates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'childTemplates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'childTemplates'`);
                }
        
                return true;
            case 'variables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variables = [];
                
                    let item: AVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'variables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variables'`);
                        }
                
                        this.variables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variables'`);
                }
        
                return true;
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: AField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            case 'children':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.children = [];
                
                    let item: Child = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Child.modelName) {
                                item = new Child();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Child.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'children' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'children'`);
                        }
                
                        this.children.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'children' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'children'`);
                }
        
                return true;
            case 'constraintValidationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValidationMessages = [];
                
                    let item: ConstraintValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConstraintValidationMessage.modelName) {
                                item = new ConstraintValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConstraintValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValidationMessages'`);
                        }
                
                        this.constraintValidationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValidationMessages'`);
                }
        
                return true;
            case 'conflict':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConflictScenario.modelName) {
                        this.conflict = new ConflictScenario();
                        this.conflict.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenario.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'conflict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'conflict'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            case 'stateKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.stateKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'stateKey' cannot be null`);
                    this.stateKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'stateKey'`);
                }
        
                return true;
            case 'stateIndex':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.stateIndex = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'stateIndex' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'stateIndex'`);
                }
        
                return true;
            case 'variableAssignments':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.variableAssignments = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'variableAssignments' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'variableAssignments'`);
                        }
                
                        this.variableAssignments.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'variableAssignments' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variableAssignments'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityId
        if (this.isEntityIdDefined && this.entityId !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityId === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityId);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // businessUnit
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.businessUnit === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.businessUnit);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mappers
        if (this.isMappersDefined && this.mappers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.mappers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.mappers.length);
            
                for (const item of this.mappers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModels
        if (this.isConstraintModelsDefined && this.constraintModels !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintModels === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintModels.length);
            
                for (const item of this.constraintModels) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModelTemplates
        if (this.isConstraintModelTemplatesDefined && this.constraintModelTemplates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintModelTemplates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintModelTemplates.length);
            
                for (const item of this.constraintModelTemplates) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // childTemplates
        if (this.isChildTemplatesDefined && this.childTemplates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.childTemplates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.childTemplates.length);
            
                for (const item of this.childTemplates) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof ModelEntity.InlineChildTemplate)
                            writer.writeShortValue(0);
                        else if (item instanceof ModelEntity.ReferenceChildTemplate)
                            writer.writeShortValue(1);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variables
        if (this.isVariablesDefined && this.variables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variables.length);
            
                for (const item of this.variables) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringVariable)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericVariable)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanVariable)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableVariable)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableVariable)
                            writer.writeShortValue(4);
                        else if (item instanceof MultiStringVariable)
                            writer.writeShortValue(5);
                        else if (item instanceof MultiNumericVariable)
                            writer.writeShortValue(6);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringField)
                            writer.writeShortValue(0);
                        else if (item instanceof NumericField)
                            writer.writeShortValue(1);
                        else if (item instanceof BooleanField)
                            writer.writeShortValue(2);
                        else if (item instanceof StringEnumerableField)
                            writer.writeShortValue(3);
                        else if (item instanceof NumericEnumerableField)
                            writer.writeShortValue(4);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // children
        if (this.isChildrenDefined && this.children !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.children === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.children.length);
            
                for (const item of this.children) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintValidationMessages
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValidationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValidationMessages.length);
            
                for (const item of this.constraintValidationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // conflict
        if (this.isConflictDefined && this.conflict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.conflict === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.conflict.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // stateKey
        if (this.isStateKeyDefined && this.stateKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.stateKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.stateKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // stateIndex
        if (this.isStateIndexDefined && this.stateIndex !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.stateIndex);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // variableAssignments
        if (this.isVariableAssignmentsDefined && this.variableAssignments !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.variableAssignments === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.variableAssignments.length);
            
                for (const item of this.variableAssignments) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityId
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityId = <any>null;
                    break;
                case 1:
                    this.entityId = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // businessUnit
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.businessUnit = <any>null;
                    break;
                case 1:
                    this.businessUnit = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mappers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: LinkedMapper;
            
                    this.mappers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new LinkedMapper();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.mappers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModels
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: LinkedConstraintModel;
            
                    this.constraintModels = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new LinkedConstraintModel();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintModels.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModelTemplates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ModelEntity.ConstraintModelTemplate;
            
                    this.constraintModelTemplates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ModelEntity.ConstraintModelTemplate();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintModelTemplates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // childTemplates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ModelEntity.AChildTemplate;
            
                    this.childTemplates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new ModelEntity.InlineChildTemplate();
                                        break;
                                    case 1:
                                        item = new ModelEntity.ReferenceChildTemplate();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.childTemplates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // variables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AVariable;
            
                    this.variables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringVariable();
                                        break;
                                    case 1:
                                        item = new NumericVariable();
                                        break;
                                    case 2:
                                        item = new BooleanVariable();
                                        break;
                                    case 3:
                                        item = new StringEnumerableVariable();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableVariable();
                                        break;
                                    case 5:
                                        item = new MultiStringVariable();
                                        break;
                                    case 6:
                                        item = new MultiNumericVariable();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.variables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AField;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringField();
                                        break;
                                    case 1:
                                        item = new NumericField();
                                        break;
                                    case 2:
                                        item = new BooleanField();
                                        break;
                                    case 3:
                                        item = new StringEnumerableField();
                                        break;
                                    case 4:
                                        item = new NumericEnumerableField();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // children
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Child;
            
                    this.children = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Child();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.children.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintValidationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConstraintValidationMessage;
            
                    this.constraintValidationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConstraintValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValidationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // conflict
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.conflict = new ConflictScenario();
                    this.conflict.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // stateKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.stateKey = <any>null;
                    break;
                case 1:
                    this.stateKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // stateIndex
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.stateIndex = reader.readIntegerValue();
        }
        
        // variableAssignments
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.variableAssignments = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.variableAssignments.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Entity {
        // Method Implementation: simple
        const schema = EntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityZodSchemaShape): Entity {
        // Method Implementation: parseZodSchema
        return new Entity({
            entityId: schema.entityId,
            businessUnit: schema.businessUnit,
            mappers: schema.mappers?.map(i => LinkedMapper.parseZodSchema(i)),
            constraintModels: schema.constraintModels?.map(i => LinkedConstraintModel.parseZodSchema(i)),
            constraintModelTemplates: schema.constraintModelTemplates?.map(i => ModelEntity.ConstraintModelTemplate.parseZodSchema(i)),
            childTemplates: schema.childTemplates?.map(i => ModelEntity.AChildTemplate.parseZodSchema(i)),
            variables: schema.variables?.map(i => AVariable.parseZodSchema(i)),
            fields: schema.fields?.map(i => AField.parseZodSchema(i)),
            children: schema.children?.map(i => Child.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            conflict: schema.conflict && ConflictScenario.parseZodSchema(schema.conflict),
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            stateKey: schema.stateKey,
            stateIndex: schema.stateIndex,
            variableAssignments: schema.variableAssignments?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityId: this.isEntityIdDefined ? ((this.entityId === null ? undefined : this.entityId)) : undefined,
            businessUnit: this.isBusinessUnitDefined ? ((this.businessUnit === null ? undefined : this.businessUnit)) : undefined,
            mappers: this.isMappersDefined ? (this.mappers?.map(item => item)) : undefined,
            constraintModels: this.isConstraintModelsDefined ? (this.constraintModels?.map(item => item)) : undefined,
            constraintModelTemplates: this.isConstraintModelTemplatesDefined ? (this.constraintModelTemplates?.map(item => item)) : undefined,
            childTemplates: this.isChildTemplatesDefined ? (this.childTemplates?.map(item => item)) : undefined,
            variables: this.isVariablesDefined ? (this.variables?.map(item => item)) : undefined,
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
            children: this.isChildrenDefined ? (this.children?.map(item => item)) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? (this.constraintValidationMessages?.map(item => item)) : undefined,
            conflict: this.isConflictDefined ? (this.conflict) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
            stateKey: this.isStateKeyDefined ? ((this.stateKey === null ? undefined : this.stateKey)) : undefined,
            stateIndex: this.isStateIndexDefined ? (this.stateIndex) : undefined,
            variableAssignments: this.isVariableAssignmentsDefined ? (this.variableAssignments?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): Entity {
        // Method Implementation: clone
        return new Entity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            businessUnit: this.isBusinessUnitDefined ? this.businessUnit : undefined,
            mappers: this.isMappersDefined ? this.mappers?.map(i => i.clone()) : undefined,
            constraintModels: this.isConstraintModelsDefined ? this.constraintModels?.map(i => i.clone()) : undefined,
            constraintModelTemplates: this.isConstraintModelTemplatesDefined ? this.constraintModelTemplates?.map(i => i.clone()) : undefined,
            childTemplates: this.isChildTemplatesDefined ? this.childTemplates?.map(i => i.clone()) : undefined,
            variables: this.isVariablesDefined ? this.variables?.map(i => i.clone()) : undefined,
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined,
            children: this.isChildrenDefined ? this.children?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            conflict: this.isConflictDefined ? this.conflict.clone() : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            stateKey: this.isStateKeyDefined ? this.stateKey : undefined,
            stateIndex: this.isStateIndexDefined ? this.stateIndex : undefined,
            variableAssignments: this.isVariableAssignmentsDefined ? this.variableAssignments?.map(i => i) : undefined
        });
    }
}

export class Child implements IChild {
    public static readonly modelName: string = 'Child';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type', 'entity' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Child.modelName;
    }

    private _name: string;

    private _type: string;

    private _entity: Entity;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public get entity(): Entity {
        // Method Implementation: simple
        return this._entity;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set entity(value: Entity) {
        // Method Implementation: simple
        this._entity = value;
        this._isEntityDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    private _isEntityDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityDefined;
    }

    public constructor();
    public constructor(source: IChild);
    public constructor(source: Partial<Pick<Child, 'name' | 'type' | 'entity'>>);
    public constructor(source?: IChild | Partial<Pick<Child, 'name' | 'type' | 'entity'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isChildModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.isEntityDefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.entity !== undefined) {
                    this._entity = source.entity;
                    this._isEntityDefined = true;
                }
                else {
                    this._entity = new Entity();
                    this._isEntityDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        
            this._entity = new Entity();
            this._isEntityDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Child.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isEntityDefined && this.entity !== undefined)
            definedProperties.push('entity');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            case 'entity':
                return this.entity;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isEntityDefined && this.entity !== undefined) {
            if (this.entity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.entity.getModelName(), this.entity.getDefinedPropertyNames().length));
                this.entity.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entity', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'entity':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === Entity.modelName) {
                        this.entity = new Entity();
                        this.entity.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'entity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entity'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // entity
        if (this.isEntityDefined && this.entity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.entity === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.entity.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // entity
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.entity = new Entity();
                    this.entity.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Child {
        // Method Implementation: simple
        const schema = ChildZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ChildZodSchemaShape): Child {
        // Method Implementation: parseZodSchema
        return new Child({
            name: schema.name,
            type: schema.type,
            entity: schema.entity && Entity.parseZodSchema(schema.entity)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
            entity: this.isEntityDefined ? (this.entity) : undefined,
        };
    }

    public clone(): Child {
        // Method Implementation: clone
        return new Child({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            entity: this.isEntityDefined ? this.entity.clone() : undefined
        });
    }
}

export class LinkedMapper implements ILinkedMapper {
    public static readonly modelName: string = 'LinkedMapper';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'mapper' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LinkedMapper.modelName;
    }

    private _name: string;

    private _mapper: ModelMapping.Mapper;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get mapper(): ModelMapping.Mapper {
        // Method Implementation: simple
        return this._mapper;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set mapper(value: ModelMapping.Mapper) {
        // Method Implementation: simple
        this._mapper = value;
        this._isMapperDefined = true;
    }

    private _isNameDefined: boolean;

    private _isMapperDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isMapperDefined(): boolean {
        // Method Implementation: simple
        return this._isMapperDefined;
    }

    public constructor();
    public constructor(source: ILinkedMapper);
    public constructor(source: Partial<Pick<LinkedMapper, 'name' | 'mapper'>>);
    public constructor(source?: ILinkedMapper | Partial<Pick<LinkedMapper, 'name' | 'mapper'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedMapperModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isMapperDefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.mapper !== undefined) {
                    this._mapper = source.mapper;
                    this._isMapperDefined = true;
                }
                else {
                    this._mapper = new ModelMapping.Mapper();
                    this._isMapperDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._mapper = new ModelMapping.Mapper();
            this._isMapperDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LinkedMapper.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isMapperDefined && this.mapper !== undefined)
            definedProperties.push('mapper');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'mapper':
                return this.mapper;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isMapperDefined && this.mapper !== undefined) {
            if (this.mapper !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.mapper.getModelName(), this.mapper.getDefinedPropertyNames().length));
                this.mapper.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mapper', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'mapper':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelMapping.Mapper.modelName) {
                        this.mapper = new ModelMapping.Mapper();
                        this.mapper.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelMapping.Mapper.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mapper' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mapper'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mapper
        if (this.isMapperDefined && this.mapper !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.mapper === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.mapper.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // mapper
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.mapper = new ModelMapping.Mapper();
                    this.mapper.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LinkedMapper {
        // Method Implementation: simple
        const schema = LinkedMapperZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedMapperZodSchemaShape): LinkedMapper {
        // Method Implementation: parseZodSchema
        return new LinkedMapper({
            name: schema.name,
            mapper: schema.mapper && ModelMapping.Mapper.parseZodSchema(schema.mapper)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            mapper: this.isMapperDefined ? (this.mapper) : undefined,
        };
    }

    public clone(): LinkedMapper {
        // Method Implementation: clone
        return new LinkedMapper({
            name: this.isNameDefined ? this.name : undefined,
            mapper: this.isMapperDefined ? this.mapper.clone() : undefined
        });
    }
}

export class LinkedConstraintModel implements ILinkedConstraintModel {
    public static readonly modelName: string = 'LinkedConstraintModel';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'priority', 'constraintModel', 'enabled' ];

    public get modelName(): string {
        // Method Implementation: simple
        return LinkedConstraintModel.modelName;
    }

    private _name: string;

    private _priority: string;

    private _constraintModel: ModelConstraint.ConstraintModel;

    private _enabled: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get priority(): string {
        // Method Implementation: simple
        return this._priority;
    }

    public get constraintModel(): ModelConstraint.ConstraintModel {
        // Method Implementation: simple
        return this._constraintModel;
    }

    public get enabled(): string {
        // Method Implementation: simple
        return this._enabled;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set priority(value: string) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    public set constraintModel(value: ModelConstraint.ConstraintModel) {
        // Method Implementation: simple
        this._constraintModel = value;
        this._isConstraintModelDefined = true;
    }

    public set enabled(value: string) {
        // Method Implementation: simple
        this._enabled = value;
        this._isEnabledDefined = true;
    }

    private _isNameDefined: boolean;

    private _isPriorityDefined: boolean;

    private _isConstraintModelDefined: boolean;

    private _isEnabledDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public get isConstraintModelDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelDefined;
    }

    public get isEnabledDefined(): boolean {
        // Method Implementation: simple
        return this._isEnabledDefined;
    }

    public constructor();
    public constructor(source: ILinkedConstraintModel);
    public constructor(source: Partial<Pick<LinkedConstraintModel, 'name' | 'priority' | 'constraintModel' | 'enabled'>>);
    public constructor(source?: ILinkedConstraintModel | Partial<Pick<LinkedConstraintModel, 'name' | 'priority' | 'constraintModel' | 'enabled'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLinkedConstraintModelModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
                
                if (source.isConstraintModelDefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = new ModelConstraint.ConstraintModel();
                    this._isConstraintModelDefined = true;
                }
                
                if (source.isEnabledDefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = '';
                    this._isPriorityDefined = false;
                }
                
                if (source.constraintModel !== undefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = new ModelConstraint.ConstraintModel();
                    this._isConstraintModelDefined = true;
                }
                
                if (source.enabled !== undefined) {
                    this._enabled = source.enabled;
                    this._isEnabledDefined = true;
                }
                else {
                    this._enabled = '';
                    this._isEnabledDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._priority = '';
            this._isPriorityDefined = false;
        
            this._constraintModel = new ModelConstraint.ConstraintModel();
            this._isConstraintModelDefined = true;
        
            this._enabled = '';
            this._isEnabledDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return LinkedConstraintModel.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        if (this.isConstraintModelDefined && this.constraintModel !== undefined)
            definedProperties.push('constraintModel');
        if (this.isEnabledDefined && this.enabled !== undefined)
            definedProperties.push('enabled');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'priority':
                return this.priority;
            case 'constraintModel':
                return this.constraintModel;
            case 'enabled':
                return this.enabled;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
        
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            if (this.constraintModel !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.constraintModel.getModelName(), this.constraintModel.getDefinedPropertyNames().length));
                this.constraintModel.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'null'));
            }
        }
        
        if (this.isEnabledDefined && this.enabled !== undefined) {
            if (this.enabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.enabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('enabled', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.priority = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'priority' cannot be null`);
                    this.priority = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            case 'constraintModel':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ModelConstraint.ConstraintModel.modelName) {
                        this.constraintModel = new ModelConstraint.ConstraintModel();
                        this.constraintModel.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ModelConstraint.ConstraintModel.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintModel' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModel'`);
                }
        
                return true;
            case 'enabled':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.enabled = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'enabled' cannot be null`);
                    this.enabled = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'enabled'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.priority === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.priority);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModel
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.constraintModel === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.constraintModel.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // enabled
        if (this.isEnabledDefined && this.enabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.enabled === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.enabled);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.priority = <any>null;
                    break;
                case 1:
                    this.priority = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintModel
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.constraintModel = new ModelConstraint.ConstraintModel();
                    this.constraintModel.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // enabled
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.enabled = <any>null;
                    break;
                case 1:
                    this.enabled = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): LinkedConstraintModel {
        // Method Implementation: simple
        const schema = LinkedConstraintModelZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LinkedConstraintModelZodSchemaShape): LinkedConstraintModel {
        // Method Implementation: parseZodSchema
        return new LinkedConstraintModel({
            name: schema.name,
            priority: schema.priority,
            constraintModel: schema.constraintModel && ModelConstraint.ConstraintModel.parseZodSchema(schema.constraintModel),
            enabled: schema.enabled
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            priority: this.isPriorityDefined ? ((this.priority === null ? undefined : this.priority)) : undefined,
            constraintModel: this.isConstraintModelDefined ? (this.constraintModel) : undefined,
            enabled: this.isEnabledDefined ? ((this.enabled === null ? undefined : this.enabled)) : undefined,
        };
    }

    public clone(): LinkedConstraintModel {
        // Method Implementation: clone
        return new LinkedConstraintModel({
            name: this.isNameDefined ? this.name : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel.clone() : undefined,
            enabled: this.isEnabledDefined ? this.enabled : undefined
        });
    }
}

export class ConstraintOptionState implements IConstraintOptionState {
    public static readonly modelName: string = 'ConstraintOptionState';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintModel', 'priority', 'state', 'messages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConstraintOptionState.modelName;
    }

    private _constraintModel: string;

    private _priority: number;

    private _state: OptionStates;

    private _messages: string[];

    public get constraintModel(): string {
        // Method Implementation: simple
        return this._constraintModel;
    }

    public get priority(): number {
        // Method Implementation: simple
        return this._priority;
    }

    public get state(): OptionStates {
        // Method Implementation: simple
        return this._state;
    }

    public get messages(): string[] {
        // Method Implementation: simple
        return this._messages;
    }

    public set constraintModel(value: string) {
        // Method Implementation: simple
        this._constraintModel = value;
        this._isConstraintModelDefined = true;
    }

    public set priority(value: number) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    public set state(value: OptionStates) {
        // Method Implementation: simple
        this._state = value;
        this._isStateDefined = true;
    }

    public set messages(value: string[]) {
        // Method Implementation: simple
        this._messages = value;
        this._isMessagesDefined = true;
    }

    private _isConstraintModelDefined: boolean;

    private _isPriorityDefined: boolean;

    private _isStateDefined: boolean;

    private _isMessagesDefined: boolean;

    public get isConstraintModelDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public get isStateDefined(): boolean {
        // Method Implementation: simple
        return this._isStateDefined;
    }

    public get isMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isMessagesDefined;
    }

    public constructor();
    public constructor(source: IConstraintOptionState);
    public constructor(source: Partial<Pick<ConstraintOptionState, 'constraintModel' | 'priority' | 'state' | 'messages'>>);
    public constructor(source?: IConstraintOptionState | Partial<Pick<ConstraintOptionState, 'constraintModel' | 'priority' | 'state' | 'messages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConstraintOptionStateModel(source)) {
                if (source.isConstraintModelDefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.isStateDefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = OptionStates.Assignable;
                    this._isStateDefined = false;
                }
                
                if (source.isMessagesDefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
            else {
                if (source.constraintModel !== undefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.state !== undefined) {
                    this._state = source.state;
                    this._isStateDefined = true;
                }
                else {
                    this._state = OptionStates.Assignable;
                    this._isStateDefined = false;
                }
                
                if (source.messages !== undefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
        }
        else {
            this._constraintModel = '';
            this._isConstraintModelDefined = false;
        
            this._priority = 0;
            this._isPriorityDefined = false;
        
            this._state = OptionStates.Assignable;
            this._isStateDefined = false;
        
            this._messages = [];
            this._isMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConstraintOptionState.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isConstraintModelDefined && this.constraintModel !== undefined)
            definedProperties.push('constraintModel');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        if (this.isStateDefined && this.state !== undefined)
            definedProperties.push('state');
        if (this.isMessagesDefined && this.messages !== undefined)
            definedProperties.push('messages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintModel':
                return this.constraintModel;
            case 'priority':
                return this.priority;
            case 'state':
                return this.state;
            case 'messages':
                return this.messages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            if (this.constraintModel !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.constraintModel);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
        
        if (this.isStateDefined && this.state !== undefined) {
            if (this.state !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.state);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('state', 'null'));
            }
        }
        
        if (this.isMessagesDefined && this.messages !== undefined) {
            if (this.messages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.messages.length));
                for (let i = 0; i < this.messages.length; i++) {
                    const item = this.messages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintModel':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.constraintModel = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'constraintModel' cannot be null`);
                    this.constraintModel = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModel'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.priority = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'priority' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            case 'state':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.state = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'state' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'state'`);
                }
        
                return true;
            case 'messages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.messages = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'messages' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'messages'`);
                        }
                
                        this.messages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'messages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'messages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // constraintModel
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.constraintModel === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.constraintModel);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.priority);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // state
        if (this.isStateDefined && this.state !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.state);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // messages
        if (this.isMessagesDefined && this.messages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.messages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.messages.length);
            
                for (const item of this.messages) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // constraintModel
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.constraintModel = <any>null;
                    break;
                case 1:
                    this.constraintModel = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.priority = reader.readIntegerValue();
        }
        
        // state
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.state = reader.readEnumValue();
        }
        
        // messages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.messages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.messages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConstraintOptionState {
        // Method Implementation: simple
        const schema = ConstraintOptionStateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConstraintOptionStateZodSchemaShape): ConstraintOptionState {
        // Method Implementation: parseZodSchema
        return new ConstraintOptionState({
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Assignable':
                    return OptionStates.Assignable;
                case 'Conflicting':
                    return OptionStates.Conflicting;
                case 'Blocked':
                    return OptionStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            messages: schema.messages?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintModel: this.isConstraintModelDefined ? ((this.constraintModel === null ? undefined : this.constraintModel)) : undefined,
            priority: this.isPriorityDefined ? (this.priority) : undefined,
            state: this.isStateDefined ? (OptionStates[this.state]) : undefined,
            messages: this.isMessagesDefined ? (this.messages?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): ConstraintOptionState {
        // Method Implementation: clone
        return new ConstraintOptionState({
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            state: this.isStateDefined ? this.state : undefined,
            messages: this.isMessagesDefined ? this.messages?.map(i => i) : undefined
        });
    }
}

export abstract class AConstraintValue implements IConstraintValue {
    public static readonly modelName: string = 'ConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintEngine', 'constraintModel', 'priority', 'important' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AConstraintValue.modelName;
    }

    private _constraintEngine: ModelConstraint.ConstraintEngineTypes;

    private _constraintModel: string;

    private _priority: number;

    private _important: boolean;

    public get constraintEngine(): ModelConstraint.ConstraintEngineTypes {
        // Method Implementation: simple
        return this._constraintEngine;
    }

    public get constraintModel(): string {
        // Method Implementation: simple
        return this._constraintModel;
    }

    public get priority(): number {
        // Method Implementation: simple
        return this._priority;
    }

    public get important(): boolean {
        // Method Implementation: simple
        return this._important;
    }

    public set constraintEngine(value: ModelConstraint.ConstraintEngineTypes) {
        // Method Implementation: simple
        this._constraintEngine = value;
        this._isConstraintEngineDefined = true;
    }

    public set constraintModel(value: string) {
        // Method Implementation: simple
        this._constraintModel = value;
        this._isConstraintModelDefined = true;
    }

    public set priority(value: number) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    public set important(value: boolean) {
        // Method Implementation: simple
        this._important = value;
        this._isImportantDefined = true;
    }

    private _isConstraintEngineDefined: boolean;

    private _isConstraintModelDefined: boolean;

    private _isPriorityDefined: boolean;

    private _isImportantDefined: boolean;

    public get isConstraintEngineDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintEngineDefined;
    }

    public get isConstraintModelDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public get isImportantDefined(): boolean {
        // Method Implementation: simple
        return this._isImportantDefined;
    }

    public constructor();
    public constructor(source: IConstraintValue);
    public constructor(source: Partial<Pick<AConstraintValue, 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: IConstraintValue | Partial<Pick<AConstraintValue, 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConstraintValueModel(source)) {
                if (source.isConstraintEngineDefined) {
                    this._constraintEngine = source.constraintEngine;
                    this._isConstraintEngineDefined = true;
                }
                else {
                    this._constraintEngine = ModelConstraint.ConstraintEngineTypes.Configit;
                    this._isConstraintEngineDefined = false;
                }
                
                if (source.isConstraintModelDefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.isImportantDefined) {
                    this._important = source.important;
                    this._isImportantDefined = true;
                }
                else {
                    this._important = false;
                    this._isImportantDefined = false;
                }
            }
            else {
                if (source.constraintEngine !== undefined) {
                    this._constraintEngine = source.constraintEngine;
                    this._isConstraintEngineDefined = true;
                }
                else {
                    this._constraintEngine = ModelConstraint.ConstraintEngineTypes.Configit;
                    this._isConstraintEngineDefined = false;
                }
                
                if (source.constraintModel !== undefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.important !== undefined) {
                    this._important = source.important;
                    this._isImportantDefined = true;
                }
                else {
                    this._important = false;
                    this._isImportantDefined = false;
                }
            }
        }
        else {
            this._constraintEngine = ModelConstraint.ConstraintEngineTypes.Configit;
            this._isConstraintEngineDefined = false;
        
            this._constraintModel = '';
            this._isConstraintModelDefined = false;
        
            this._priority = 0;
            this._isPriorityDefined = false;
        
            this._important = false;
            this._isImportantDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AConstraintValue.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isConstraintEngineDefined && this.constraintEngine !== undefined)
            definedProperties.push('constraintEngine');
        if (this.isConstraintModelDefined && this.constraintModel !== undefined)
            definedProperties.push('constraintModel');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        if (this.isImportantDefined && this.important !== undefined)
            definedProperties.push('important');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintEngine':
                return this.constraintEngine;
            case 'constraintModel':
                return this.constraintModel;
            case 'priority':
                return this.priority;
            case 'important':
                return this.important;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isConstraintEngineDefined && this.constraintEngine !== undefined) {
            if (this.constraintEngine !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintEngine', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.constraintEngine);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintEngine', 'null'));
            }
        }
        
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            if (this.constraintModel !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.constraintModel);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
        
        if (this.isImportantDefined && this.important !== undefined) {
            if (this.important !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('important', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.important);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('important', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintEngine':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.constraintEngine = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'constraintEngine' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintEngine'`);
                }
        
                return true;
            case 'constraintModel':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.constraintModel = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'constraintModel' cannot be null`);
                    this.constraintModel = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModel'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.priority = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'priority' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            case 'important':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.important = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'important' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'important'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // constraintEngine
        if (this.isConstraintEngineDefined && this.constraintEngine !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.constraintEngine);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintModel
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.constraintModel === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.constraintModel);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.priority);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // important
        if (this.isImportantDefined && this.important !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.important);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // constraintEngine
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.constraintEngine = reader.readEnumValue();
        }
        
        // constraintModel
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.constraintModel = <any>null;
                    break;
                case 1:
                    this.constraintModel = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.priority = reader.readIntegerValue();
        }
        
        // important
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.important = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): AConstraintValue {
        // Method Implementation: simple
        const schema = ConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConstraintValueZodSchemaShape): AConstraintValue {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.constraintValueType)
        {
            case 'stringConstraintValue':
            case 'string':
                return StringConstraintValue.parse(schema);
            case 'numericConstraintValue':
            case 'numeric':
                return NumericConstraintValue.parse(schema);
            case 'booleanConstraintValue':
            case 'boolean':
                return BooleanConstraintValue.parse(schema);
            case 'multiStringConstraintValue':
            case 'multiString':
                return MultiStringConstraintValue.parse(schema);
            case 'multiNumericConstraintValue':
            case 'multiNumeric':
                return MultiNumericConstraintValue.parse(schema);
            default:
                throw new Error(`constraintValueType of ${ schema.constraintValueType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintEngine: this.isConstraintEngineDefined ? (ModelConstraint.ConstraintEngineTypes[this.constraintEngine]) : undefined,
            constraintModel: this.isConstraintModelDefined ? ((this.constraintModel === null ? undefined : this.constraintModel)) : undefined,
            priority: this.isPriorityDefined ? (this.priority) : undefined,
            important: this.isImportantDefined ? (this.important) : undefined,
        };
    }

    public abstract clone(): AConstraintValue;
}

export class StringConstraintValue extends AConstraintValue implements IStringConstraintValue {
    public static readonly modelName: string = 'StringConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringConstraintValue.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringConstraintValue);
    public constructor(source: Partial<Pick<StringConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: IStringConstraintValue | Partial<Pick<StringConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringConstraintValueModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringConstraintValue.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringConstraintValue {
        // Method Implementation: simple
        const schema = StringConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringConstraintValueZodSchemaShape): StringConstraintValue {
        // Method Implementation: parseZodSchema
        return new StringConstraintValue({
            constraintEngine: (() => {
            if (!schema.constraintEngine)
                return;
        
            switch (schema.constraintEngine) {
                case 'Configit':
                    return ModelConstraint.ConstraintEngineTypes.Configit;
                case 'Mapper':
                    return ModelConstraint.ConstraintEngineTypes.Mapper;
                case 'Reflection':
                    return ModelConstraint.ConstraintEngineTypes.Reflection;
                case 'FanSelection':
                    return ModelConstraint.ConstraintEngineTypes.FanSelection;
                case 'Macro':
                    return ModelConstraint.ConstraintEngineTypes.Macro;
                case 'Recommendation':
                    return ModelConstraint.ConstraintEngineTypes.Recommendation;
                case 'Static':
                    return ModelConstraint.ConstraintEngineTypes.Static;
                case 'AssignedValue':
                    return ModelConstraint.ConstraintEngineTypes.AssignedValue;
                default:
                    throw new Error(`Enum type "schema.constraintEngine" not recognized`);
            }
        })(),
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            important: schema.important,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintValueType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringConstraintValue {
        // Method Implementation: clone
        return new StringConstraintValue({
            constraintEngine: this.isConstraintEngineDefined ? this.constraintEngine : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericConstraintValue extends AConstraintValue implements INumericConstraintValue {
    public static readonly modelName: string = 'NumericConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericConstraintValue.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericConstraintValue);
    public constructor(source: Partial<Pick<NumericConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: INumericConstraintValue | Partial<Pick<NumericConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericConstraintValueModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericConstraintValue.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericConstraintValue {
        // Method Implementation: simple
        const schema = NumericConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericConstraintValueZodSchemaShape): NumericConstraintValue {
        // Method Implementation: parseZodSchema
        return new NumericConstraintValue({
            constraintEngine: (() => {
            if (!schema.constraintEngine)
                return;
        
            switch (schema.constraintEngine) {
                case 'Configit':
                    return ModelConstraint.ConstraintEngineTypes.Configit;
                case 'Mapper':
                    return ModelConstraint.ConstraintEngineTypes.Mapper;
                case 'Reflection':
                    return ModelConstraint.ConstraintEngineTypes.Reflection;
                case 'FanSelection':
                    return ModelConstraint.ConstraintEngineTypes.FanSelection;
                case 'Macro':
                    return ModelConstraint.ConstraintEngineTypes.Macro;
                case 'Recommendation':
                    return ModelConstraint.ConstraintEngineTypes.Recommendation;
                case 'Static':
                    return ModelConstraint.ConstraintEngineTypes.Static;
                case 'AssignedValue':
                    return ModelConstraint.ConstraintEngineTypes.AssignedValue;
                default:
                    throw new Error(`Enum type "schema.constraintEngine" not recognized`);
            }
        })(),
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            important: schema.important,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintValueType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericConstraintValue {
        // Method Implementation: clone
        return new NumericConstraintValue({
            constraintEngine: this.isConstraintEngineDefined ? this.constraintEngine : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class BooleanConstraintValue extends AConstraintValue implements IBooleanConstraintValue {
    public static readonly modelName: string = 'BooleanConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanConstraintValue.modelName;
    }

    private _value: boolean;

    public get value(): boolean {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanConstraintValue);
    public constructor(source: Partial<Pick<BooleanConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: IBooleanConstraintValue | Partial<Pick<BooleanConstraintValue, 'value' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanConstraintValueModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanConstraintValue.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanConstraintValue {
        // Method Implementation: simple
        const schema = BooleanConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanConstraintValueZodSchemaShape): BooleanConstraintValue {
        // Method Implementation: parseZodSchema
        return new BooleanConstraintValue({
            constraintEngine: (() => {
            if (!schema.constraintEngine)
                return;
        
            switch (schema.constraintEngine) {
                case 'Configit':
                    return ModelConstraint.ConstraintEngineTypes.Configit;
                case 'Mapper':
                    return ModelConstraint.ConstraintEngineTypes.Mapper;
                case 'Reflection':
                    return ModelConstraint.ConstraintEngineTypes.Reflection;
                case 'FanSelection':
                    return ModelConstraint.ConstraintEngineTypes.FanSelection;
                case 'Macro':
                    return ModelConstraint.ConstraintEngineTypes.Macro;
                case 'Recommendation':
                    return ModelConstraint.ConstraintEngineTypes.Recommendation;
                case 'Static':
                    return ModelConstraint.ConstraintEngineTypes.Static;
                case 'AssignedValue':
                    return ModelConstraint.ConstraintEngineTypes.AssignedValue;
                default:
                    throw new Error(`Enum type "schema.constraintEngine" not recognized`);
            }
        })(),
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            important: schema.important,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintValueType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanConstraintValue {
        // Method Implementation: clone
        return new BooleanConstraintValue({
            constraintEngine: this.isConstraintEngineDefined ? this.constraintEngine : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class MultiStringConstraintValue extends AConstraintValue implements IMultiStringConstraintValue {
    public static readonly modelName: string = 'MultiStringConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiStringConstraintValue.modelName;
    }

    private _values: string[];

    public get values(): string[] {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: string[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiStringConstraintValue);
    public constructor(source: Partial<Pick<MultiStringConstraintValue, 'values' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: IMultiStringConstraintValue | Partial<Pick<MultiStringConstraintValue, 'values' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiStringConstraintValueModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiStringConstraintValue.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'values' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiStringConstraintValue {
        // Method Implementation: simple
        const schema = MultiStringConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiStringConstraintValueZodSchemaShape): MultiStringConstraintValue {
        // Method Implementation: parseZodSchema
        return new MultiStringConstraintValue({
            constraintEngine: (() => {
            if (!schema.constraintEngine)
                return;
        
            switch (schema.constraintEngine) {
                case 'Configit':
                    return ModelConstraint.ConstraintEngineTypes.Configit;
                case 'Mapper':
                    return ModelConstraint.ConstraintEngineTypes.Mapper;
                case 'Reflection':
                    return ModelConstraint.ConstraintEngineTypes.Reflection;
                case 'FanSelection':
                    return ModelConstraint.ConstraintEngineTypes.FanSelection;
                case 'Macro':
                    return ModelConstraint.ConstraintEngineTypes.Macro;
                case 'Recommendation':
                    return ModelConstraint.ConstraintEngineTypes.Recommendation;
                case 'Static':
                    return ModelConstraint.ConstraintEngineTypes.Static;
                case 'AssignedValue':
                    return ModelConstraint.ConstraintEngineTypes.AssignedValue;
                default:
                    throw new Error(`Enum type "schema.constraintEngine" not recognized`);
            }
        })(),
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            important: schema.important,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintValueType: 'multiString',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): MultiStringConstraintValue {
        // Method Implementation: clone
        return new MultiStringConstraintValue({
            constraintEngine: this.isConstraintEngineDefined ? this.constraintEngine : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export class MultiNumericConstraintValue extends AConstraintValue implements IMultiNumericConstraintValue {
    public static readonly modelName: string = 'MultiNumericConstraintValue';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'values' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiNumericConstraintValue.modelName;
    }

    private _values: number[];

    public get values(): number[] {
        // Method Implementation: simple
        return this._values;
    }

    public set values(value: number[]) {
        // Method Implementation: simple
        this._values = value;
        this._isValuesDefined = true;
    }

    private _isValuesDefined: boolean;

    public get isValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiNumericConstraintValue);
    public constructor(source: Partial<Pick<MultiNumericConstraintValue, 'values' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>);
    public constructor(source?: IMultiNumericConstraintValue | Partial<Pick<MultiNumericConstraintValue, 'values' | 'constraintEngine' | 'constraintModel' | 'priority' | 'important'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiNumericConstraintValueModel(source)) {
                if (source.isValuesDefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
            else {
                if (source.values !== undefined) {
                    this._values = source.values;
                    this._isValuesDefined = true;
                }
                else {
                    this._values = [];
                    this._isValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._values = [];
            this._isValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiNumericConstraintValue.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValuesDefined && this.values !== undefined)
            definedProperties.push('values');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'values':
                return this.values;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValuesDefined && this.values !== undefined) {
            if (this.values !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.values.length));
                for (let i = 0; i < this.values.length; i++) {
                    const item = this.values[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('double'));
                
                        // Implementation: axon/write/double
                        writer.writeDoubleValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('values', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'values':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.values = [];
                
                    let item: number = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: double
                        if (arrayItemHeader.type === 'double') {
                            item = reader.readDoubleValue();
                        }
                        else if (arrayItemHeader.type === 'single') {
                            item = reader.readFloatValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'values' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'values'`);
                        }
                
                        this.values.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'values' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'values'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // values
        if (this.isValuesDefined && this.values !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.values === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.values.length);
            
                for (const item of this.values) {
                    // Item Implementation: double
                    writer.writeDoubleValue(item);
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // values
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: number;
            
                    this.values = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: double
                        item = reader.readDoubleValue();
            
                        this.values.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiNumericConstraintValue {
        // Method Implementation: simple
        const schema = MultiNumericConstraintValueZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiNumericConstraintValueZodSchemaShape): MultiNumericConstraintValue {
        // Method Implementation: parseZodSchema
        return new MultiNumericConstraintValue({
            constraintEngine: (() => {
            if (!schema.constraintEngine)
                return;
        
            switch (schema.constraintEngine) {
                case 'Configit':
                    return ModelConstraint.ConstraintEngineTypes.Configit;
                case 'Mapper':
                    return ModelConstraint.ConstraintEngineTypes.Mapper;
                case 'Reflection':
                    return ModelConstraint.ConstraintEngineTypes.Reflection;
                case 'FanSelection':
                    return ModelConstraint.ConstraintEngineTypes.FanSelection;
                case 'Macro':
                    return ModelConstraint.ConstraintEngineTypes.Macro;
                case 'Recommendation':
                    return ModelConstraint.ConstraintEngineTypes.Recommendation;
                case 'Static':
                    return ModelConstraint.ConstraintEngineTypes.Static;
                case 'AssignedValue':
                    return ModelConstraint.ConstraintEngineTypes.AssignedValue;
                default:
                    throw new Error(`Enum type "schema.constraintEngine" not recognized`);
            }
        })(),
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            important: schema.important,
            values: schema.values?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintValueType: 'multiNumeric',
            ...super.toJSON(),
            values: this.isValuesDefined ? (this.values?.map(item => item)) : undefined,
        };
    }

    public clone(): MultiNumericConstraintValue {
        // Method Implementation: clone
        return new MultiNumericConstraintValue({
            constraintEngine: this.isConstraintEngineDefined ? this.constraintEngine : undefined,
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            important: this.isImportantDefined ? this.important : undefined,
            values: this.isValuesDefined ? this.values?.map(i => i) : undefined
        });
    }
}

export class ConstraintValidationMessage implements IConstraintValidationMessage {
    public static readonly modelName: string = 'ConstraintValidationMessage';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintModel', 'priority', 'message' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConstraintValidationMessage.modelName;
    }

    private _constraintModel: string;

    private _priority: number;

    private _message: string;

    public get constraintModel(): string {
        // Method Implementation: simple
        return this._constraintModel;
    }

    public get priority(): number {
        // Method Implementation: simple
        return this._priority;
    }

    public get message(): string {
        // Method Implementation: simple
        return this._message;
    }

    public set constraintModel(value: string) {
        // Method Implementation: simple
        this._constraintModel = value;
        this._isConstraintModelDefined = true;
    }

    public set priority(value: number) {
        // Method Implementation: simple
        this._priority = value;
        this._isPriorityDefined = true;
    }

    public set message(value: string) {
        // Method Implementation: simple
        this._message = value;
        this._isMessageDefined = true;
    }

    private _isConstraintModelDefined: boolean;

    private _isPriorityDefined: boolean;

    private _isMessageDefined: boolean;

    public get isConstraintModelDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintModelDefined;
    }

    public get isPriorityDefined(): boolean {
        // Method Implementation: simple
        return this._isPriorityDefined;
    }

    public get isMessageDefined(): boolean {
        // Method Implementation: simple
        return this._isMessageDefined;
    }

    public constructor();
    public constructor(source: IConstraintValidationMessage);
    public constructor(source: Partial<Pick<ConstraintValidationMessage, 'constraintModel' | 'priority' | 'message'>>);
    public constructor(source?: IConstraintValidationMessage | Partial<Pick<ConstraintValidationMessage, 'constraintModel' | 'priority' | 'message'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConstraintValidationMessageModel(source)) {
                if (source.isConstraintModelDefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.isPriorityDefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.isMessageDefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
            }
            else {
                if (source.constraintModel !== undefined) {
                    this._constraintModel = source.constraintModel;
                    this._isConstraintModelDefined = true;
                }
                else {
                    this._constraintModel = '';
                    this._isConstraintModelDefined = false;
                }
                
                if (source.priority !== undefined) {
                    this._priority = source.priority;
                    this._isPriorityDefined = true;
                }
                else {
                    this._priority = 0;
                    this._isPriorityDefined = false;
                }
                
                if (source.message !== undefined) {
                    this._message = source.message;
                    this._isMessageDefined = true;
                }
                else {
                    this._message = '';
                    this._isMessageDefined = false;
                }
            }
        }
        else {
            this._constraintModel = '';
            this._isConstraintModelDefined = false;
        
            this._priority = 0;
            this._isPriorityDefined = false;
        
            this._message = '';
            this._isMessageDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConstraintValidationMessage.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isConstraintModelDefined && this.constraintModel !== undefined)
            definedProperties.push('constraintModel');
        if (this.isPriorityDefined && this.priority !== undefined)
            definedProperties.push('priority');
        if (this.isMessageDefined && this.message !== undefined)
            definedProperties.push('message');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintModel':
                return this.constraintModel;
            case 'priority':
                return this.priority;
            case 'message':
                return this.message;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            if (this.constraintModel !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.constraintModel);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintModel', 'null'));
            }
        }
        
        if (this.isPriorityDefined && this.priority !== undefined) {
            if (this.priority !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.priority);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('priority', 'null'));
            }
        }
        
        if (this.isMessageDefined && this.message !== undefined) {
            if (this.message !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.message);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('message', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintModel':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.constraintModel = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'constraintModel' cannot be null`);
                    this.constraintModel = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintModel'`);
                }
        
                return true;
            case 'priority':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.priority = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'priority' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'priority'`);
                }
        
                return true;
            case 'message':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.message = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'message' cannot be null`);
                    this.message = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'message'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // constraintModel
        if (this.isConstraintModelDefined && this.constraintModel !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.constraintModel === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.constraintModel);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // priority
        if (this.isPriorityDefined && this.priority !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.priority);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // message
        if (this.isMessageDefined && this.message !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.message === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.message);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // constraintModel
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.constraintModel = <any>null;
                    break;
                case 1:
                    this.constraintModel = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // priority
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.priority = reader.readIntegerValue();
        }
        
        // message
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.message = <any>null;
                    break;
                case 1:
                    this.message = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConstraintValidationMessage {
        // Method Implementation: simple
        const schema = ConstraintValidationMessageZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConstraintValidationMessageZodSchemaShape): ConstraintValidationMessage {
        // Method Implementation: parseZodSchema
        return new ConstraintValidationMessage({
            constraintModel: schema.constraintModel,
            priority: schema.priority,
            message: schema.message
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            constraintModel: this.isConstraintModelDefined ? ((this.constraintModel === null ? undefined : this.constraintModel)) : undefined,
            priority: this.isPriorityDefined ? (this.priority) : undefined,
            message: this.isMessageDefined ? ((this.message === null ? undefined : this.message)) : undefined,
        };
    }

    public clone(): ConstraintValidationMessage {
        // Method Implementation: clone
        return new ConstraintValidationMessage({
            constraintModel: this.isConstraintModelDefined ? this.constraintModel : undefined,
            priority: this.isPriorityDefined ? this.priority : undefined,
            message: this.isMessageDefined ? this.message : undefined
        });
    }
}

export abstract class AVariable implements IVariable {
    public static readonly modelName: string = 'Variable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'constraint', 'defaultExpression', 'required', 'strict', 'frozen', 'validators', 'constraintValidationMessages', 'assignedValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AVariable.modelName;
    }

    private _name: string;

    private _constraint: string;

    private _defaultExpression: string;

    private _required: string;

    private _strict: string;

    private _frozen: boolean;

    private _validators: Validator[];

    private _constraintValidationMessages: ConstraintValidationMessage[];

    private _assignedValue: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get constraint(): string {
        // Method Implementation: simple
        return this._constraint;
    }

    public get defaultExpression(): string {
        // Method Implementation: simple
        return this._defaultExpression;
    }

    public get required(): string {
        // Method Implementation: simple
        return this._required;
    }

    public get strict(): string {
        // Method Implementation: simple
        return this._strict;
    }

    public get frozen(): boolean {
        // Method Implementation: simple
        return this._frozen;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public get constraintValidationMessages(): ConstraintValidationMessage[] {
        // Method Implementation: simple
        return this._constraintValidationMessages;
    }

    public get assignedValue(): string {
        // Method Implementation: simple
        return this._assignedValue;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set constraint(value: string) {
        // Method Implementation: simple
        this._constraint = value;
        this._isConstraintDefined = true;
    }

    public set defaultExpression(value: string) {
        // Method Implementation: simple
        this._defaultExpression = value;
        this._isDefaultExpressionDefined = true;
    }

    public set required(value: string) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set strict(value: string) {
        // Method Implementation: simple
        this._strict = value;
        this._isStrictDefined = true;
    }

    public set frozen(value: boolean) {
        // Method Implementation: simple
        this._frozen = value;
        this._isFrozenDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    public set constraintValidationMessages(value: ConstraintValidationMessage[]) {
        // Method Implementation: simple
        this._constraintValidationMessages = value;
        this._isConstraintValidationMessagesDefined = true;
    }

    public set assignedValue(value: string) {
        // Method Implementation: simple
        this._assignedValue = value;
        this._isAssignedValueDefined = true;
    }

    private _isNameDefined: boolean;

    private _isConstraintDefined: boolean;

    private _isDefaultExpressionDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isStrictDefined: boolean;

    private _isFrozenDefined: boolean;

    private _isValidatorsDefined: boolean;

    private _isConstraintValidationMessagesDefined: boolean;

    private _isAssignedValueDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isConstraintDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintDefined;
    }

    public get isDefaultExpressionDefined(): boolean {
        // Method Implementation: simple
        return this._isDefaultExpressionDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isStrictDefined(): boolean {
        // Method Implementation: simple
        return this._isStrictDefined;
    }

    public get isFrozenDefined(): boolean {
        // Method Implementation: simple
        return this._isFrozenDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public get isConstraintValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValidationMessagesDefined;
    }

    public get isAssignedValueDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignedValueDefined;
    }

    public constructor();
    public constructor(source: IVariable);
    public constructor(source: Partial<Pick<AVariable, 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IVariable | Partial<Pick<AVariable, 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isVariableModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isConstraintDefined) {
                    this._constraint = source.constraint;
                    this._isConstraintDefined = true;
                }
                else {
                    this._constraint = '';
                    this._isConstraintDefined = false;
                }
                
                if (source.isDefaultExpressionDefined) {
                    this._defaultExpression = source.defaultExpression;
                    this._isDefaultExpressionDefined = true;
                }
                else {
                    this._defaultExpression = '';
                    this._isDefaultExpressionDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.isStrictDefined) {
                    this._strict = source.strict;
                    this._isStrictDefined = true;
                }
                else {
                    this._strict = '';
                    this._isStrictDefined = false;
                }
                
                if (source.isFrozenDefined) {
                    this._frozen = source.frozen;
                    this._isFrozenDefined = true;
                }
                else {
                    this._frozen = false;
                    this._isFrozenDefined = false;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.isConstraintValidationMessagesDefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
                
                if (source.isAssignedValueDefined) {
                    this._assignedValue = source.assignedValue;
                    this._isAssignedValueDefined = true;
                }
                else {
                    this._assignedValue = '';
                    this._isAssignedValueDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.constraint !== undefined) {
                    this._constraint = source.constraint;
                    this._isConstraintDefined = true;
                }
                else {
                    this._constraint = '';
                    this._isConstraintDefined = false;
                }
                
                if (source.defaultExpression !== undefined) {
                    this._defaultExpression = source.defaultExpression;
                    this._isDefaultExpressionDefined = true;
                }
                else {
                    this._defaultExpression = '';
                    this._isDefaultExpressionDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.strict !== undefined) {
                    this._strict = source.strict;
                    this._isStrictDefined = true;
                }
                else {
                    this._strict = '';
                    this._isStrictDefined = false;
                }
                
                if (source.frozen !== undefined) {
                    this._frozen = source.frozen;
                    this._isFrozenDefined = true;
                }
                else {
                    this._frozen = false;
                    this._isFrozenDefined = false;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.constraintValidationMessages !== undefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
                
                if (source.assignedValue !== undefined) {
                    this._assignedValue = source.assignedValue;
                    this._isAssignedValueDefined = true;
                }
                else {
                    this._assignedValue = '';
                    this._isAssignedValueDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._constraint = '';
            this._isConstraintDefined = false;
        
            this._defaultExpression = '';
            this._isDefaultExpressionDefined = false;
        
            this._required = '';
            this._isRequiredDefined = false;
        
            this._strict = '';
            this._isStrictDefined = false;
        
            this._frozen = false;
            this._isFrozenDefined = false;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        
            this._constraintValidationMessages = [];
            this._isConstraintValidationMessagesDefined = true;
        
            this._assignedValue = '';
            this._isAssignedValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AVariable.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isConstraintDefined && this.constraint !== undefined)
            definedProperties.push('constraint');
        if (this.isDefaultExpressionDefined && this.defaultExpression !== undefined)
            definedProperties.push('defaultExpression');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isStrictDefined && this.strict !== undefined)
            definedProperties.push('strict');
        if (this.isFrozenDefined && this.frozen !== undefined)
            definedProperties.push('frozen');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined)
            definedProperties.push('constraintValidationMessages');
        if (this.isAssignedValueDefined && this.assignedValue !== undefined)
            definedProperties.push('assignedValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'constraint':
                return this.constraint;
            case 'defaultExpression':
                return this.defaultExpression;
            case 'required':
                return this.required;
            case 'strict':
                return this.strict;
            case 'frozen':
                return this.frozen;
            case 'validators':
                return this.validators;
            case 'constraintValidationMessages':
                return this.constraintValidationMessages;
            case 'assignedValue':
                return this.assignedValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isConstraintDefined && this.constraint !== undefined) {
            if (this.constraint !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraint', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.constraint);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraint', 'null'));
            }
        }
        
        if (this.isDefaultExpressionDefined && this.defaultExpression !== undefined) {
            if (this.defaultExpression !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaultExpression', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.defaultExpression);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('defaultExpression', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isStrictDefined && this.strict !== undefined) {
            if (this.strict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('strict', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.strict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('strict', 'null'));
            }
        }
        
        if (this.isFrozenDefined && this.frozen !== undefined) {
            if (this.frozen !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('frozen', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.frozen);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('frozen', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
        
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            if (this.constraintValidationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValidationMessages.length));
                for (let i = 0; i < this.constraintValidationMessages.length; i++) {
                    const item = this.constraintValidationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'null'));
            }
        }
        
        if (this.isAssignedValueDefined && this.assignedValue !== undefined) {
            if (this.assignedValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignedValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.assignedValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignedValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'constraint':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.constraint = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'constraint' cannot be null`);
                    this.constraint = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraint'`);
                }
        
                return true;
            case 'defaultExpression':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.defaultExpression = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'defaultExpression' cannot be null`);
                    this.defaultExpression = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'defaultExpression'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.required = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'required' cannot be null`);
                    this.required = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'strict':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.strict = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'strict' cannot be null`);
                    this.strict = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'strict'`);
                }
        
                return true;
            case 'frozen':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.frozen = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'frozen' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'frozen'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            case 'constraintValidationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValidationMessages = [];
                
                    let item: ConstraintValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConstraintValidationMessage.modelName) {
                                item = new ConstraintValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConstraintValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValidationMessages'`);
                        }
                
                        this.constraintValidationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValidationMessages'`);
                }
        
                return true;
            case 'assignedValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.assignedValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'assignedValue' cannot be null`);
                    this.assignedValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignedValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraint
        if (this.isConstraintDefined && this.constraint !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.constraint === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.constraint);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // defaultExpression
        if (this.isDefaultExpressionDefined && this.defaultExpression !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.defaultExpression === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.defaultExpression);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.required === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.required);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // strict
        if (this.isStrictDefined && this.strict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.strict === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.strict);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // frozen
        if (this.isFrozenDefined && this.frozen !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.frozen);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintValidationMessages
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValidationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValidationMessages.length);
            
                for (const item of this.constraintValidationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignedValue
        if (this.isAssignedValueDefined && this.assignedValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.assignedValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.assignedValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraint
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.constraint = <any>null;
                    break;
                case 1:
                    this.constraint = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // defaultExpression
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.defaultExpression = <any>null;
                    break;
                case 1:
                    this.defaultExpression = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.required = <any>null;
                    break;
                case 1:
                    this.required = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // strict
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.strict = <any>null;
                    break;
                case 1:
                    this.strict = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // frozen
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.frozen = reader.readBooleanValue();
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintValidationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConstraintValidationMessage;
            
                    this.constraintValidationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConstraintValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValidationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // assignedValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.assignedValue = <any>null;
                    break;
                case 1:
                    this.assignedValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AVariable {
        // Method Implementation: simple
        const schema = VariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: VariableZodSchemaShape): AVariable {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.variableType)
        {
            case 'stringVariable':
            case 'string':
                return StringVariable.parse(schema);
            case 'numericVariable':
            case 'numeric':
                return NumericVariable.parse(schema);
            case 'booleanVariable':
            case 'boolean':
                return BooleanVariable.parse(schema);
            case 'stringEnumerableVariable':
            case 'stringEnumerable':
                return StringEnumerableVariable.parse(schema);
            case 'numericEnumerableVariable':
            case 'numericEnumerable':
                return NumericEnumerableVariable.parse(schema);
            case 'multiStringVariable':
            case 'multiString':
                return MultiStringVariable.parse(schema);
            case 'multiNumericVariable':
            case 'multiNumeric':
                return MultiNumericVariable.parse(schema);
            default:
                throw new Error(`variableType of ${ schema.variableType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            constraint: this.isConstraintDefined ? ((this.constraint === null ? undefined : this.constraint)) : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? ((this.defaultExpression === null ? undefined : this.defaultExpression)) : undefined,
            required: this.isRequiredDefined ? ((this.required === null ? undefined : this.required)) : undefined,
            strict: this.isStrictDefined ? ((this.strict === null ? undefined : this.strict)) : undefined,
            frozen: this.isFrozenDefined ? (this.frozen) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? (this.constraintValidationMessages?.map(item => item)) : undefined,
            assignedValue: this.isAssignedValueDefined ? ((this.assignedValue === null ? undefined : this.assignedValue)) : undefined,
        };
    }

    public abstract clone(): AVariable;
}

export class StringVariable extends AVariable implements IStringVariable {
    public static readonly modelName: string = 'StringVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringVariable.modelName;
    }

    private _constraintValues: StringConstraintValue[];

    public get constraintValues(): StringConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public set constraintValues(value: StringConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public constructor();
    public constructor(source: IStringVariable);
    public constructor(source: Partial<Pick<StringVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IStringVariable | Partial<Pick<StringVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: StringConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringConstraintValue.modelName) {
                                item = new StringConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringVariable {
        // Method Implementation: simple
        const schema = StringVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringVariableZodSchemaShape): StringVariable {
        // Method Implementation: parseZodSchema
        return new StringVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => StringConstraintValue.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'string',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
        };
    }

    public clone(): StringVariable {
        // Method Implementation: clone
        return new StringVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericVariable extends AVariable implements INumericVariable {
    public static readonly modelName: string = 'NumericVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericVariable.modelName;
    }

    private _constraintValues: NumericConstraintValue[];

    private _precision: number;

    private _min: string;

    private _max: string;

    public get constraintValues(): NumericConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): string {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): string {
        // Method Implementation: simple
        return this._max;
    }

    public set constraintValues(value: NumericConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: string) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: string) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericVariable);
    public constructor(source: Partial<Pick<NumericVariable, 'constraintValues' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: INumericVariable | Partial<Pick<NumericVariable, 'constraintValues' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = '';
            this._isMinDefined = false;
        
            this._max = '';
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: NumericConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericConstraintValue.modelName) {
                                item = new NumericConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.min = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'min' cannot be null`);
                    this.min = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.max = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'max' cannot be null`);
                    this.max = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.min === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.min);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.max === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.max);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.min = <any>null;
                    break;
                case 1:
                    this.min = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.max = <any>null;
                    break;
                case 1:
                    this.max = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericVariable {
        // Method Implementation: simple
        const schema = NumericVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericVariableZodSchemaShape): NumericVariable {
        // Method Implementation: parseZodSchema
        return new NumericVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => NumericConstraintValue.parseZodSchema(i)),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'numeric',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? ((this.min === null ? undefined : this.min)) : undefined,
            max: this.isMaxDefined ? ((this.max === null ? undefined : this.max)) : undefined,
        };
    }

    public clone(): NumericVariable {
        // Method Implementation: clone
        return new NumericVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class BooleanVariable extends AVariable implements IBooleanVariable {
    public static readonly modelName: string = 'BooleanVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanVariable.modelName;
    }

    private _constraintValues: BooleanConstraintValue[];

    public get constraintValues(): BooleanConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public set constraintValues(value: BooleanConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public constructor();
    public constructor(source: IBooleanVariable);
    public constructor(source: Partial<Pick<BooleanVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IBooleanVariable | Partial<Pick<BooleanVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: BooleanConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === BooleanConstraintValue.modelName) {
                                item = new BooleanConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${BooleanConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: BooleanConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new BooleanConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): BooleanVariable {
        // Method Implementation: simple
        const schema = BooleanVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanVariableZodSchemaShape): BooleanVariable {
        // Method Implementation: parseZodSchema
        return new BooleanVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => BooleanConstraintValue.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'boolean',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
        };
    }

    public clone(): BooleanVariable {
        // Method Implementation: clone
        return new BooleanVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined
        });
    }
}

export class StringEnumerableVariable extends AVariable implements IStringEnumerableVariable {
    public static readonly modelName: string = 'StringEnumerableVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues', 'options' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableVariable.modelName;
    }

    private _constraintValues: StringConstraintValue[];

    private _options: StringVariableOption[];

    public get constraintValues(): StringConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public get options(): StringVariableOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public set constraintValues(value: StringConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    public set options(value: StringVariableOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    private _isOptionsDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableVariable);
    public constructor(source: Partial<Pick<StringEnumerableVariable, 'constraintValues' | 'options' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IStringEnumerableVariable | Partial<Pick<StringEnumerableVariable, 'constraintValues' | 'options' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        
            this._options = [];
            this._isOptionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            case 'options':
                return this.options;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: StringConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringConstraintValue.modelName) {
                                item = new StringConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: StringVariableOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringVariableOption.modelName) {
                                item = new StringVariableOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringVariableOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringVariableOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringVariableOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableVariable {
        // Method Implementation: simple
        const schema = StringEnumerableVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableVariableZodSchemaShape): StringEnumerableVariable {
        // Method Implementation: parseZodSchema
        return new StringEnumerableVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => StringConstraintValue.parseZodSchema(i)),
            options: schema.options?.map(i => StringVariableOption.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'stringEnumerable',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableVariable {
        // Method Implementation: clone
        return new StringEnumerableVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableVariable extends AVariable implements INumericEnumerableVariable {
    public static readonly modelName: string = 'NumericEnumerableVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues', 'options', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableVariable.modelName;
    }

    private _constraintValues: NumericConstraintValue[];

    private _options: NumericVariableOption[];

    private _precision: number;

    private _min: string;

    private _max: string;

    public get constraintValues(): NumericConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public get options(): NumericVariableOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): string {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): string {
        // Method Implementation: simple
        return this._max;
    }

    public set constraintValues(value: NumericConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    public set options(value: NumericVariableOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: string) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: string) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    private _isOptionsDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableVariable);
    public constructor(source: Partial<Pick<NumericEnumerableVariable, 'constraintValues' | 'options' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: INumericEnumerableVariable | Partial<Pick<NumericEnumerableVariable, 'constraintValues' | 'options' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        
            this._options = [];
            this._isOptionsDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = '';
            this._isMinDefined = false;
        
            this._max = '';
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            case 'options':
                return this.options;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: NumericConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericConstraintValue.modelName) {
                                item = new NumericConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: NumericVariableOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericVariableOption.modelName) {
                                item = new NumericVariableOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericVariableOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.min = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'min' cannot be null`);
                    this.min = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.max = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'max' cannot be null`);
                    this.max = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.min === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.min);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.max === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.max);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericVariableOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericVariableOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.min = <any>null;
                    break;
                case 1:
                    this.min = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.max = <any>null;
                    break;
                case 1:
                    this.max = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericEnumerableVariable {
        // Method Implementation: simple
        const schema = NumericEnumerableVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableVariableZodSchemaShape): NumericEnumerableVariable {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => NumericConstraintValue.parseZodSchema(i)),
            options: schema.options?.map(i => NumericVariableOption.parseZodSchema(i)),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'numericEnumerable',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? ((this.min === null ? undefined : this.min)) : undefined,
            max: this.isMaxDefined ? ((this.max === null ? undefined : this.max)) : undefined,
        };
    }

    public clone(): NumericEnumerableVariable {
        // Method Implementation: clone
        return new NumericEnumerableVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export class MultiStringVariable extends AVariable implements IMultiStringVariable {
    public static readonly modelName: string = 'MultiStringVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiStringVariable.modelName;
    }

    private _constraintValues: MultiStringConstraintValue[];

    public get constraintValues(): MultiStringConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public set constraintValues(value: MultiStringConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public constructor();
    public constructor(source: IMultiStringVariable);
    public constructor(source: Partial<Pick<MultiStringVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IMultiStringVariable | Partial<Pick<MultiStringVariable, 'constraintValues' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiStringVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiStringVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: MultiStringConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === MultiStringConstraintValue.modelName) {
                                item = new MultiStringConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${MultiStringConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: MultiStringConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new MultiStringConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiStringVariable {
        // Method Implementation: simple
        const schema = MultiStringVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiStringVariableZodSchemaShape): MultiStringVariable {
        // Method Implementation: parseZodSchema
        return new MultiStringVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => MultiStringConstraintValue.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'multiString',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
        };
    }

    public clone(): MultiStringVariable {
        // Method Implementation: clone
        return new MultiStringVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined
        });
    }
}

export class MultiNumericVariable extends AVariable implements IMultiNumericVariable {
    public static readonly modelName: string = 'MultiNumericVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'constraintValues', 'precision', 'min', 'max' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MultiNumericVariable.modelName;
    }

    private _constraintValues: MultiNumericConstraintValue[];

    private _precision: number;

    private _min: string;

    private _max: string;

    public get constraintValues(): MultiNumericConstraintValue[] {
        // Method Implementation: simple
        return this._constraintValues;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public get min(): string {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): string {
        // Method Implementation: simple
        return this._max;
    }

    public set constraintValues(value: MultiNumericConstraintValue[]) {
        // Method Implementation: simple
        this._constraintValues = value;
        this._isConstraintValuesDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    public set min(value: string) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: string) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    private _isConstraintValuesDefined: boolean;

    private _isPrecisionDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    public get isConstraintValuesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValuesDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public constructor();
    public constructor(source: IMultiNumericVariable);
    public constructor(source: Partial<Pick<MultiNumericVariable, 'constraintValues' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>);
    public constructor(source?: IMultiNumericVariable | Partial<Pick<MultiNumericVariable, 'constraintValues' | 'precision' | 'min' | 'max' | 'name' | 'constraint' | 'defaultExpression' | 'required' | 'strict' | 'frozen' | 'validators' | 'constraintValidationMessages' | 'assignedValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isMultiNumericVariableModel(source)) {
                if (source.isConstraintValuesDefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
            else {
                if (source.constraintValues !== undefined) {
                    this._constraintValues = source.constraintValues;
                    this._isConstraintValuesDefined = true;
                }
                else {
                    this._constraintValues = [];
                    this._isConstraintValuesDefined = true;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = '';
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = '';
                    this._isMaxDefined = false;
                }
            }
        }
        else {
            super();
        
            this._constraintValues = [];
            this._isConstraintValuesDefined = true;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        
            this._min = '';
            this._isMinDefined = false;
        
            this._max = '';
            this._isMaxDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...MultiNumericVariable.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined)
            definedProperties.push('constraintValues');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'constraintValues':
                return this.constraintValues;
            case 'precision':
                return this.precision;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            if (this.constraintValues !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValues.length));
                for (let i = 0; i < this.constraintValues.length; i++) {
                    const item = this.constraintValues[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValues', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'constraintValues':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValues = [];
                
                    let item: MultiNumericConstraintValue = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === MultiNumericConstraintValue.modelName) {
                                item = new MultiNumericConstraintValue();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${MultiNumericConstraintValue.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValues' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValues'`);
                        }
                
                        this.constraintValues.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValues' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValues'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.min = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'min' cannot be null`);
                    this.min = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.max = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'max' cannot be null`);
                    this.max = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // constraintValues
        if (this.isConstraintValuesDefined && this.constraintValues !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValues === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValues.length);
            
                for (const item of this.constraintValues) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.min === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.min);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.max === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.max);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // constraintValues
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: MultiNumericConstraintValue;
            
                    this.constraintValues = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new MultiNumericConstraintValue();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValues.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.min = <any>null;
                    break;
                case 1:
                    this.min = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.max = <any>null;
                    break;
                case 1:
                    this.max = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): MultiNumericVariable {
        // Method Implementation: simple
        const schema = MultiNumericVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MultiNumericVariableZodSchemaShape): MultiNumericVariable {
        // Method Implementation: parseZodSchema
        return new MultiNumericVariable({
            name: schema.name,
            constraint: schema.constraint,
            defaultExpression: schema.defaultExpression,
            required: schema.required,
            strict: schema.strict,
            frozen: schema.frozen,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            assignedValue: schema.assignedValue,
            constraintValues: schema.constraintValues?.map(i => MultiNumericConstraintValue.parseZodSchema(i)),
            precision: schema.precision,
            min: schema.min,
            max: schema.max
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableType: 'multiNumeric',
            ...super.toJSON(),
            constraintValues: this.isConstraintValuesDefined ? (this.constraintValues?.map(item => item)) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
            min: this.isMinDefined ? ((this.min === null ? undefined : this.min)) : undefined,
            max: this.isMaxDefined ? ((this.max === null ? undefined : this.max)) : undefined,
        };
    }

    public clone(): MultiNumericVariable {
        // Method Implementation: clone
        return new MultiNumericVariable({
            name: this.isNameDefined ? this.name : undefined,
            constraint: this.isConstraintDefined ? this.constraint : undefined,
            defaultExpression: this.isDefaultExpressionDefined ? this.defaultExpression : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            strict: this.isStrictDefined ? this.strict : undefined,
            frozen: this.isFrozenDefined ? this.frozen : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            assignedValue: this.isAssignedValueDefined ? this.assignedValue : undefined,
            constraintValues: this.isConstraintValuesDefined ? this.constraintValues?.map(i => i.clone()) : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined
        });
    }
}

export abstract class AVariableOption implements IVariableOption {
    public static readonly modelName: string = 'VariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'constraintValue', 'constraintStates', 'onSelectedActions', 'applicable' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AVariableOption.modelName;
    }

    private _name: string;

    private _constraintValue: string;

    private _constraintStates: ConstraintOptionState[];

    private _onSelectedActions: TransientAction[];

    private _applicable: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get constraintValue(): string {
        // Method Implementation: simple
        return this._constraintValue;
    }

    public get constraintStates(): ConstraintOptionState[] {
        // Method Implementation: simple
        return this._constraintStates;
    }

    public get onSelectedActions(): TransientAction[] {
        // Method Implementation: simple
        return this._onSelectedActions;
    }

    public get applicable(): string {
        // Method Implementation: simple
        return this._applicable;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set constraintValue(value: string) {
        // Method Implementation: simple
        this._constraintValue = value;
        this._isConstraintValueDefined = true;
    }

    public set constraintStates(value: ConstraintOptionState[]) {
        // Method Implementation: simple
        this._constraintStates = value;
        this._isConstraintStatesDefined = true;
    }

    public set onSelectedActions(value: TransientAction[]) {
        // Method Implementation: simple
        this._onSelectedActions = value;
        this._isOnSelectedActionsDefined = true;
    }

    public set applicable(value: string) {
        // Method Implementation: simple
        this._applicable = value;
        this._isApplicableDefined = true;
    }

    private _isNameDefined: boolean;

    private _isConstraintValueDefined: boolean;

    private _isConstraintStatesDefined: boolean;

    private _isOnSelectedActionsDefined: boolean;

    private _isApplicableDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isConstraintValueDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValueDefined;
    }

    public get isConstraintStatesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintStatesDefined;
    }

    public get isOnSelectedActionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOnSelectedActionsDefined;
    }

    public get isApplicableDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicableDefined;
    }

    public constructor();
    public constructor(source: IVariableOption);
    public constructor(source: Partial<Pick<AVariableOption, 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: IVariableOption | Partial<Pick<AVariableOption, 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isVariableOptionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isConstraintValueDefined) {
                    this._constraintValue = source.constraintValue;
                    this._isConstraintValueDefined = true;
                }
                else {
                    this._constraintValue = '';
                    this._isConstraintValueDefined = false;
                }
                
                if (source.isConstraintStatesDefined) {
                    this._constraintStates = source.constraintStates;
                    this._isConstraintStatesDefined = true;
                }
                else {
                    this._constraintStates = [];
                    this._isConstraintStatesDefined = true;
                }
                
                if (source.isOnSelectedActionsDefined) {
                    this._onSelectedActions = source.onSelectedActions;
                    this._isOnSelectedActionsDefined = true;
                }
                else {
                    this._onSelectedActions = [];
                    this._isOnSelectedActionsDefined = true;
                }
                
                if (source.isApplicableDefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.constraintValue !== undefined) {
                    this._constraintValue = source.constraintValue;
                    this._isConstraintValueDefined = true;
                }
                else {
                    this._constraintValue = '';
                    this._isConstraintValueDefined = false;
                }
                
                if (source.constraintStates !== undefined) {
                    this._constraintStates = source.constraintStates;
                    this._isConstraintStatesDefined = true;
                }
                else {
                    this._constraintStates = [];
                    this._isConstraintStatesDefined = true;
                }
                
                if (source.onSelectedActions !== undefined) {
                    this._onSelectedActions = source.onSelectedActions;
                    this._isOnSelectedActionsDefined = true;
                }
                else {
                    this._onSelectedActions = [];
                    this._isOnSelectedActionsDefined = true;
                }
                
                if (source.applicable !== undefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._constraintValue = '';
            this._isConstraintValueDefined = false;
        
            this._constraintStates = [];
            this._isConstraintStatesDefined = true;
        
            this._onSelectedActions = [];
            this._isOnSelectedActionsDefined = true;
        
            this._applicable = '';
            this._isApplicableDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AVariableOption.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isConstraintValueDefined && this.constraintValue !== undefined)
            definedProperties.push('constraintValue');
        if (this.isConstraintStatesDefined && this.constraintStates !== undefined)
            definedProperties.push('constraintStates');
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined)
            definedProperties.push('onSelectedActions');
        if (this.isApplicableDefined && this.applicable !== undefined)
            definedProperties.push('applicable');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'constraintValue':
                return this.constraintValue;
            case 'constraintStates':
                return this.constraintStates;
            case 'onSelectedActions':
                return this.onSelectedActions;
            case 'applicable':
                return this.applicable;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isConstraintValueDefined && this.constraintValue !== undefined) {
            if (this.constraintValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.constraintValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValue', 'null'));
            }
        }
        
        if (this.isConstraintStatesDefined && this.constraintStates !== undefined) {
            if (this.constraintStates !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintStates', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintStates.length));
                for (let i = 0; i < this.constraintStates.length; i++) {
                    const item = this.constraintStates[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintStates', 'null'));
            }
        }
        
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined) {
            if (this.onSelectedActions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('onSelectedActions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.onSelectedActions.length));
                for (let i = 0; i < this.onSelectedActions.length; i++) {
                    const item = this.onSelectedActions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('onSelectedActions', 'null'));
            }
        }
        
        if (this.isApplicableDefined && this.applicable !== undefined) {
            if (this.applicable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.applicable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'constraintValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.constraintValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'constraintValue' cannot be null`);
                    this.constraintValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValue'`);
                }
        
                return true;
            case 'constraintStates':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintStates = [];
                
                    let item: ConstraintOptionState = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConstraintOptionState.modelName) {
                                item = new ConstraintOptionState();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConstraintOptionState.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintStates' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintStates'`);
                        }
                
                        this.constraintStates.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintStates' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintStates'`);
                }
        
                return true;
            case 'onSelectedActions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.onSelectedActions = [];
                
                    let item: TransientAction = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === TransientAction.modelName) {
                                item = new TransientAction();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${TransientAction.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'onSelectedActions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'onSelectedActions'`);
                        }
                
                        this.onSelectedActions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'onSelectedActions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'onSelectedActions'`);
                }
        
                return true;
            case 'applicable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.applicable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'applicable' cannot be null`);
                    this.applicable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'applicable'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintValue
        if (this.isConstraintValueDefined && this.constraintValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.constraintValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.constraintValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintStates
        if (this.isConstraintStatesDefined && this.constraintStates !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintStates === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintStates.length);
            
                for (const item of this.constraintStates) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // onSelectedActions
        if (this.isOnSelectedActionsDefined && this.onSelectedActions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.onSelectedActions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.onSelectedActions.length);
            
                for (const item of this.onSelectedActions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // applicable
        if (this.isApplicableDefined && this.applicable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.applicable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.applicable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.constraintValue = <any>null;
                    break;
                case 1:
                    this.constraintValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintStates
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConstraintOptionState;
            
                    this.constraintStates = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConstraintOptionState();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintStates.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // onSelectedActions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: TransientAction;
            
                    this.onSelectedActions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new TransientAction();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.onSelectedActions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // applicable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.applicable = <any>null;
                    break;
                case 1:
                    this.applicable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AVariableOption {
        // Method Implementation: simple
        const schema = VariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: VariableOptionZodSchemaShape): AVariableOption {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.variableOptionType)
        {
            case 'stringVariableOption':
            case 'string':
                return StringVariableOption.parse(schema);
            case 'numericVariableOption':
            case 'numeric':
                return NumericVariableOption.parse(schema);
            default:
                throw new Error(`variableOptionType of ${ schema.variableOptionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            constraintValue: this.isConstraintValueDefined ? ((this.constraintValue === null ? undefined : this.constraintValue)) : undefined,
            constraintStates: this.isConstraintStatesDefined ? (this.constraintStates?.map(item => item)) : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? (this.onSelectedActions?.map(item => item)) : undefined,
            applicable: this.isApplicableDefined ? ((this.applicable === null ? undefined : this.applicable)) : undefined,
        };
    }

    public abstract clone(): AVariableOption;
}

export class StringVariableOption extends AVariableOption implements IStringVariableOption {
    public static readonly modelName: string = 'StringVariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringVariableOption.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringVariableOption);
    public constructor(source: Partial<Pick<StringVariableOption, 'value' | 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: IStringVariableOption | Partial<Pick<StringVariableOption, 'value' | 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringVariableOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringVariableOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringVariableOption {
        // Method Implementation: simple
        const schema = StringVariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringVariableOptionZodSchemaShape): StringVariableOption {
        // Method Implementation: parseZodSchema
        return new StringVariableOption({
            name: schema.name,
            constraintValue: schema.constraintValue,
            constraintStates: schema.constraintStates?.map(i => ConstraintOptionState.parseZodSchema(i)),
            onSelectedActions: schema.onSelectedActions?.map(i => TransientAction.parseZodSchema(i)),
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableOptionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringVariableOption {
        // Method Implementation: clone
        return new StringVariableOption({
            name: this.isNameDefined ? this.name : undefined,
            constraintValue: this.isConstraintValueDefined ? this.constraintValue : undefined,
            constraintStates: this.isConstraintStatesDefined ? this.constraintStates?.map(i => i.clone()) : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? this.onSelectedActions?.map(i => i.clone()) : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericVariableOption extends AVariableOption implements INumericVariableOption {
    public static readonly modelName: string = 'NumericVariableOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericVariableOption.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericVariableOption);
    public constructor(source: Partial<Pick<NumericVariableOption, 'value' | 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>);
    public constructor(source?: INumericVariableOption | Partial<Pick<NumericVariableOption, 'value' | 'name' | 'constraintValue' | 'constraintStates' | 'onSelectedActions' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericVariableOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericVariableOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericVariableOption {
        // Method Implementation: simple
        const schema = NumericVariableOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericVariableOptionZodSchemaShape): NumericVariableOption {
        // Method Implementation: parseZodSchema
        return new NumericVariableOption({
            name: schema.name,
            constraintValue: schema.constraintValue,
            constraintStates: schema.constraintStates?.map(i => ConstraintOptionState.parseZodSchema(i)),
            onSelectedActions: schema.onSelectedActions?.map(i => TransientAction.parseZodSchema(i)),
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variableOptionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericVariableOption {
        // Method Implementation: clone
        return new NumericVariableOption({
            name: this.isNameDefined ? this.name : undefined,
            constraintValue: this.isConstraintValueDefined ? this.constraintValue : undefined,
            constraintStates: this.isConstraintStatesDefined ? this.constraintStates?.map(i => i.clone()) : undefined,
            onSelectedActions: this.isOnSelectedActionsDefined ? this.onSelectedActions?.map(i => i.clone()) : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export abstract class AField implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'source', 'required', 'validators', 'constraintValidationMessages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AField.modelName;
    }

    private _name: string;

    private _source: string;

    private _required: string;

    private _validators: Validator[];

    private _constraintValidationMessages: ConstraintValidationMessage[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get required(): string {
        // Method Implementation: simple
        return this._required;
    }

    public get validators(): Validator[] {
        // Method Implementation: simple
        return this._validators;
    }

    public get constraintValidationMessages(): ConstraintValidationMessage[] {
        // Method Implementation: simple
        return this._constraintValidationMessages;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set required(value: string) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set validators(value: Validator[]) {
        // Method Implementation: simple
        this._validators = value;
        this._isValidatorsDefined = true;
    }

    public set constraintValidationMessages(value: ConstraintValidationMessage[]) {
        // Method Implementation: simple
        this._constraintValidationMessages = value;
        this._isConstraintValidationMessagesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isSourceDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isValidatorsDefined: boolean;

    private _isConstraintValidationMessagesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isValidatorsDefined(): boolean {
        // Method Implementation: simple
        return this._isValidatorsDefined;
    }

    public get isConstraintValidationMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isConstraintValidationMessagesDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<AField, 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: IField | Partial<Pick<AField, 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.isValidatorsDefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.isConstraintValidationMessagesDefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = '';
                    this._isRequiredDefined = false;
                }
                
                if (source.validators !== undefined) {
                    this._validators = source.validators;
                    this._isValidatorsDefined = true;
                }
                else {
                    this._validators = [];
                    this._isValidatorsDefined = true;
                }
                
                if (source.constraintValidationMessages !== undefined) {
                    this._constraintValidationMessages = source.constraintValidationMessages;
                    this._isConstraintValidationMessagesDefined = true;
                }
                else {
                    this._constraintValidationMessages = [];
                    this._isConstraintValidationMessagesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._source = '';
            this._isSourceDefined = false;
        
            this._required = '';
            this._isRequiredDefined = false;
        
            this._validators = [];
            this._isValidatorsDefined = true;
        
            this._constraintValidationMessages = [];
            this._isConstraintValidationMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isValidatorsDefined && this.validators !== undefined)
            definedProperties.push('validators');
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined)
            definedProperties.push('constraintValidationMessages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'source':
                return this.source;
            case 'required':
                return this.required;
            case 'validators':
                return this.validators;
            case 'constraintValidationMessages':
                return this.constraintValidationMessages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isValidatorsDefined && this.validators !== undefined) {
            if (this.validators !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.validators.length));
                for (let i = 0; i < this.validators.length; i++) {
                    const item = this.validators[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('validators', 'null'));
            }
        }
        
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            if (this.constraintValidationMessages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.constraintValidationMessages.length));
                for (let i = 0; i < this.constraintValidationMessages.length; i++) {
                    const item = this.constraintValidationMessages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('constraintValidationMessages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.required = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'required' cannot be null`);
                    this.required = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'validators':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.validators = [];
                
                    let item: Validator = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Validator.modelName) {
                                item = new Validator();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Validator.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'validators' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'validators'`);
                        }
                
                        this.validators.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'validators' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'validators'`);
                }
        
                return true;
            case 'constraintValidationMessages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.constraintValidationMessages = [];
                
                    let item: ConstraintValidationMessage = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConstraintValidationMessage.modelName) {
                                item = new ConstraintValidationMessage();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConstraintValidationMessage.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'constraintValidationMessages'`);
                        }
                
                        this.constraintValidationMessages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'constraintValidationMessages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'constraintValidationMessages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.required === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.required);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // validators
        if (this.isValidatorsDefined && this.validators !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.validators === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.validators.length);
            
                for (const item of this.validators) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // constraintValidationMessages
        if (this.isConstraintValidationMessagesDefined && this.constraintValidationMessages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.constraintValidationMessages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.constraintValidationMessages.length);
            
                for (const item of this.constraintValidationMessages) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.required = <any>null;
                    break;
                case 1:
                    this.required = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // validators
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Validator;
            
                    this.validators = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Validator();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.validators.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // constraintValidationMessages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConstraintValidationMessage;
            
                    this.constraintValidationMessages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConstraintValidationMessage();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.constraintValidationMessages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AField {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): AField {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldType)
        {
            case 'stringField':
            case 'string':
                return StringField.parse(schema);
            case 'numericField':
            case 'numeric':
                return NumericField.parse(schema);
            case 'booleanField':
            case 'boolean':
                return BooleanField.parse(schema);
            case 'stringEnumerableField':
            case 'stringEnumerable':
                return StringEnumerableField.parse(schema);
            case 'numericEnumerableField':
            case 'numericEnumerable':
                return NumericEnumerableField.parse(schema);
            default:
                throw new Error(`fieldType of ${ schema.fieldType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            required: this.isRequiredDefined ? ((this.required === null ? undefined : this.required)) : undefined,
            validators: this.isValidatorsDefined ? (this.validators?.map(item => item)) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? (this.constraintValidationMessages?.map(item => item)) : undefined,
        };
    }

    public abstract clone(): AField;
}

export class StringField extends AField implements IStringField {
    public static readonly modelName: string = 'StringField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringField.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringField);
    public constructor(source: Partial<Pick<StringField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: IStringField | Partial<Pick<StringField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringField {
        // Method Implementation: simple
        const schema = StringFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldZodSchemaShape): StringField {
        // Method Implementation: parseZodSchema
        return new StringField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringField {
        // Method Implementation: clone
        return new StringField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericField extends AField implements INumericField {
    public static readonly modelName: string = 'NumericField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericField.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericField);
    public constructor(source: Partial<Pick<NumericField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: INumericField | Partial<Pick<NumericField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericField {
        // Method Implementation: simple
        const schema = NumericFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericFieldZodSchemaShape): NumericField {
        // Method Implementation: parseZodSchema
        return new NumericField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericField {
        // Method Implementation: clone
        return new NumericField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class BooleanField extends AField implements IBooleanField {
    public static readonly modelName: string = 'BooleanField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanField.modelName;
    }

    private _value: boolean;

    public get value(): boolean {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanField);
    public constructor(source: Partial<Pick<BooleanField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: IBooleanField | Partial<Pick<BooleanField, 'value' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanField {
        // Method Implementation: simple
        const schema = BooleanFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanFieldZodSchemaShape): BooleanField {
        // Method Implementation: parseZodSchema
        return new BooleanField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanField {
        // Method Implementation: clone
        return new BooleanField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class StringEnumerableField extends AField implements IStringEnumerableField {
    public static readonly modelName: string = 'StringEnumerableField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'options' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringEnumerableField.modelName;
    }

    private _value: string;

    private _options: StringFieldOption[];

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get options(): StringFieldOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set options(value: StringFieldOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    private _isValueDefined: boolean;

    private _isOptionsDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public constructor();
    public constructor(source: IStringEnumerableField);
    public constructor(source: Partial<Pick<StringEnumerableField, 'value' | 'options' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: IStringEnumerableField | Partial<Pick<StringEnumerableField, 'value' | 'options' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringEnumerableFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        
            this._options = [];
            this._isOptionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringEnumerableField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'options':
                return this.options;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: StringFieldOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === StringFieldOption.modelName) {
                                item = new StringFieldOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${StringFieldOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: StringFieldOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new StringFieldOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringEnumerableField {
        // Method Implementation: simple
        const schema = StringEnumerableFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringEnumerableFieldZodSchemaShape): StringEnumerableField {
        // Method Implementation: parseZodSchema
        return new StringEnumerableField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            value: schema.value,
            options: schema.options?.map(i => StringFieldOption.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'stringEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
        };
    }

    public clone(): StringEnumerableField {
        // Method Implementation: clone
        return new StringEnumerableField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            value: this.isValueDefined ? this.value : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined
        });
    }
}

export class NumericEnumerableField extends AField implements INumericEnumerableField {
    public static readonly modelName: string = 'NumericEnumerableField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value', 'options' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericEnumerableField.modelName;
    }

    private _value: number;

    private _options: NumericFieldOption[];

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public get options(): NumericFieldOption[] {
        // Method Implementation: simple
        return this._options;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set options(value: NumericFieldOption[]) {
        // Method Implementation: simple
        this._options = value;
        this._isOptionsDefined = true;
    }

    private _isValueDefined: boolean;

    private _isOptionsDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isOptionsDefined(): boolean {
        // Method Implementation: simple
        return this._isOptionsDefined;
    }

    public constructor();
    public constructor(source: INumericEnumerableField);
    public constructor(source: Partial<Pick<NumericEnumerableField, 'value' | 'options' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>);
    public constructor(source?: INumericEnumerableField | Partial<Pick<NumericEnumerableField, 'value' | 'options' | 'name' | 'source' | 'required' | 'validators' | 'constraintValidationMessages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericEnumerableFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.isOptionsDefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
                
                if (source.options !== undefined) {
                    this._options = source.options;
                    this._isOptionsDefined = true;
                }
                else {
                    this._options = [];
                    this._isOptionsDefined = true;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        
            this._options = [];
            this._isOptionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericEnumerableField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isOptionsDefined && this.options !== undefined)
            definedProperties.push('options');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            case 'options':
                return this.options;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isOptionsDefined && this.options !== undefined) {
            if (this.options !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.options.length));
                for (let i = 0; i < this.options.length; i++) {
                    const item = this.options[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('options', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'options':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.options = [];
                
                    let item: NumericFieldOption = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === NumericFieldOption.modelName) {
                                item = new NumericFieldOption();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${NumericFieldOption.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'options' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'options'`);
                        }
                
                        this.options.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'options' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'options'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // options
        if (this.isOptionsDefined && this.options !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.options === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.options.length);
            
                for (const item of this.options) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
        
        // options
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: NumericFieldOption;
            
                    this.options = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new NumericFieldOption();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.options.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): NumericEnumerableField {
        // Method Implementation: simple
        const schema = NumericEnumerableFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericEnumerableFieldZodSchemaShape): NumericEnumerableField {
        // Method Implementation: parseZodSchema
        return new NumericEnumerableField({
            name: schema.name,
            source: schema.source,
            required: schema.required,
            validators: schema.validators?.map(i => Validator.parseZodSchema(i)),
            constraintValidationMessages: schema.constraintValidationMessages?.map(i => ConstraintValidationMessage.parseZodSchema(i)),
            value: schema.value,
            options: schema.options?.map(i => NumericFieldOption.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'numericEnumerable',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
            options: this.isOptionsDefined ? (this.options?.map(item => item)) : undefined,
        };
    }

    public clone(): NumericEnumerableField {
        // Method Implementation: clone
        return new NumericEnumerableField({
            name: this.isNameDefined ? this.name : undefined,
            source: this.isSourceDefined ? this.source : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            validators: this.isValidatorsDefined ? this.validators?.map(i => i.clone()) : undefined,
            constraintValidationMessages: this.isConstraintValidationMessagesDefined ? this.constraintValidationMessages?.map(i => i.clone()) : undefined,
            value: this.isValueDefined ? this.value : undefined,
            options: this.isOptionsDefined ? this.options?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AFieldOption implements IFieldOption {
    public static readonly modelName: string = 'FieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'applicable' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AFieldOption.modelName;
    }

    private _name: string;

    private _applicable: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get applicable(): string {
        // Method Implementation: simple
        return this._applicable;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set applicable(value: string) {
        // Method Implementation: simple
        this._applicable = value;
        this._isApplicableDefined = true;
    }

    private _isNameDefined: boolean;

    private _isApplicableDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isApplicableDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicableDefined;
    }

    public constructor();
    public constructor(source: IFieldOption);
    public constructor(source: Partial<Pick<AFieldOption, 'name' | 'applicable'>>);
    public constructor(source?: IFieldOption | Partial<Pick<AFieldOption, 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldOptionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isApplicableDefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.applicable !== undefined) {
                    this._applicable = source.applicable;
                    this._isApplicableDefined = true;
                }
                else {
                    this._applicable = '';
                    this._isApplicableDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._applicable = '';
            this._isApplicableDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AFieldOption.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isApplicableDefined && this.applicable !== undefined)
            definedProperties.push('applicable');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'applicable':
                return this.applicable;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isApplicableDefined && this.applicable !== undefined) {
            if (this.applicable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.applicable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('applicable', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'applicable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.applicable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'applicable' cannot be null`);
                    this.applicable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'applicable'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // applicable
        if (this.isApplicableDefined && this.applicable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.applicable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.applicable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // applicable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.applicable = <any>null;
                    break;
                case 1:
                    this.applicable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AFieldOption {
        // Method Implementation: simple
        const schema = FieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldOptionZodSchemaShape): AFieldOption {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldOptionType)
        {
            case 'stringFieldOption':
            case 'string':
                return StringFieldOption.parse(schema);
            case 'numericFieldOption':
            case 'numeric':
                return NumericFieldOption.parse(schema);
            default:
                throw new Error(`fieldOptionType of ${ schema.fieldOptionType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            applicable: this.isApplicableDefined ? ((this.applicable === null ? undefined : this.applicable)) : undefined,
        };
    }

    public abstract clone(): AFieldOption;
}

export class StringFieldOption extends AFieldOption implements IStringFieldOption {
    public static readonly modelName: string = 'StringFieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringFieldOption.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringFieldOption);
    public constructor(source: Partial<Pick<StringFieldOption, 'value' | 'name' | 'applicable'>>);
    public constructor(source?: IStringFieldOption | Partial<Pick<StringFieldOption, 'value' | 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringFieldOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringFieldOption {
        // Method Implementation: simple
        const schema = StringFieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldOptionZodSchemaShape): StringFieldOption {
        // Method Implementation: parseZodSchema
        return new StringFieldOption({
            name: schema.name,
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldOptionType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringFieldOption {
        // Method Implementation: clone
        return new StringFieldOption({
            name: this.isNameDefined ? this.name : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class NumericFieldOption extends AFieldOption implements INumericFieldOption {
    public static readonly modelName: string = 'NumericFieldOption';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return NumericFieldOption.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: INumericFieldOption);
    public constructor(source: Partial<Pick<NumericFieldOption, 'value' | 'name' | 'applicable'>>);
    public constructor(source?: INumericFieldOption | Partial<Pick<NumericFieldOption, 'value' | 'name' | 'applicable'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isNumericFieldOptionModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...NumericFieldOption.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): NumericFieldOption {
        // Method Implementation: simple
        const schema = NumericFieldOptionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: NumericFieldOptionZodSchemaShape): NumericFieldOption {
        // Method Implementation: parseZodSchema
        return new NumericFieldOption({
            name: schema.name,
            applicable: schema.applicable,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldOptionType: 'numeric',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): NumericFieldOption {
        // Method Implementation: clone
        return new NumericFieldOption({
            name: this.isNameDefined ? this.name : undefined,
            applicable: this.isApplicableDefined ? this.applicable : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class Validator implements IValidator {
    public static readonly modelName: string = 'Validator';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'condition', 'type' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Validator.modelName;
    }

    private _name: string;

    private _condition: string;

    private _type: ValidatorTypes;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get condition(): string {
        // Method Implementation: simple
        return this._condition;
    }

    public get type(): ValidatorTypes {
        // Method Implementation: simple
        return this._type;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set condition(value: string) {
        // Method Implementation: simple
        this._condition = value;
        this._isConditionDefined = true;
    }

    public set type(value: ValidatorTypes) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    private _isNameDefined: boolean;

    private _isConditionDefined: boolean;

    private _isTypeDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isConditionDefined(): boolean {
        // Method Implementation: simple
        return this._isConditionDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public constructor();
    public constructor(source: IValidator);
    public constructor(source: Partial<Pick<Validator, 'name' | 'condition' | 'type'>>);
    public constructor(source?: IValidator | Partial<Pick<Validator, 'name' | 'condition' | 'type'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValidatorModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isConditionDefined) {
                    this._condition = source.condition;
                    this._isConditionDefined = true;
                }
                else {
                    this._condition = '';
                    this._isConditionDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidatorTypes.Error;
                    this._isTypeDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.condition !== undefined) {
                    this._condition = source.condition;
                    this._isConditionDefined = true;
                }
                else {
                    this._condition = '';
                    this._isConditionDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = ValidatorTypes.Error;
                    this._isTypeDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._condition = '';
            this._isConditionDefined = false;
        
            this._type = ValidatorTypes.Error;
            this._isTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Validator.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isConditionDefined && this.condition !== undefined)
            definedProperties.push('condition');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'condition':
                return this.condition;
            case 'type':
                return this.type;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isConditionDefined && this.condition !== undefined) {
            if (this.condition !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('condition', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.condition);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('condition', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'condition':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.condition = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'condition' cannot be null`);
                    this.condition = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'condition'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // condition
        if (this.isConditionDefined && this.condition !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.condition === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.condition);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // condition
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.condition = <any>null;
                    break;
                case 1:
                    this.condition = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): Validator {
        // Method Implementation: simple
        const schema = ValidatorZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValidatorZodSchemaShape): Validator {
        // Method Implementation: parseZodSchema
        return new Validator({
            name: schema.name,
            condition: schema.condition,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'Error':
                    return ValidatorTypes.Error;
                case 'Warning':
                    return ValidatorTypes.Warning;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            condition: this.isConditionDefined ? ((this.condition === null ? undefined : this.condition)) : undefined,
            type: this.isTypeDefined ? (ValidatorTypes[this.type]) : undefined,
        };
    }

    public clone(): Validator {
        // Method Implementation: clone
        return new Validator({
            name: this.isNameDefined ? this.name : undefined,
            condition: this.isConditionDefined ? this.condition : undefined,
            type: this.isTypeDefined ? this.type : undefined
        });
    }
}

export class UpdateResult implements IUpdateResult {
    public static readonly modelName: string = 'UpdateResult';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'actions', 'messages', 'conflict' ];

    public get modelName(): string {
        // Method Implementation: simple
        return UpdateResult.modelName;
    }

    private _actions: TransientAction[];

    private _messages: string[];

    private _conflict: ConflictScenario;

    public get actions(): TransientAction[] {
        // Method Implementation: simple
        return this._actions;
    }

    public get messages(): string[] {
        // Method Implementation: simple
        return this._messages;
    }

    public get conflict(): ConflictScenario {
        // Method Implementation: simple
        return this._conflict;
    }

    public set actions(value: TransientAction[]) {
        // Method Implementation: simple
        this._actions = value;
        this._isActionsDefined = true;
    }

    public set messages(value: string[]) {
        // Method Implementation: simple
        this._messages = value;
        this._isMessagesDefined = true;
    }

    public set conflict(value: ConflictScenario) {
        // Method Implementation: simple
        this._conflict = value;
        this._isConflictDefined = true;
    }

    private _isActionsDefined: boolean;

    private _isMessagesDefined: boolean;

    private _isConflictDefined: boolean;

    public get isActionsDefined(): boolean {
        // Method Implementation: simple
        return this._isActionsDefined;
    }

    public get isMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isMessagesDefined;
    }

    public get isConflictDefined(): boolean {
        // Method Implementation: simple
        return this._isConflictDefined;
    }

    public constructor();
    public constructor(source: IUpdateResult);
    public constructor(source: Partial<Pick<UpdateResult, 'actions' | 'messages' | 'conflict'>>);
    public constructor(source?: IUpdateResult | Partial<Pick<UpdateResult, 'actions' | 'messages' | 'conflict'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isUpdateResultModel(source)) {
                if (source.isActionsDefined) {
                    this._actions = source.actions;
                    this._isActionsDefined = true;
                }
                else {
                    this._actions = [];
                    this._isActionsDefined = true;
                }
                
                if (source.isMessagesDefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
                
                if (source.isConflictDefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
            }
            else {
                if (source.actions !== undefined) {
                    this._actions = source.actions;
                    this._isActionsDefined = true;
                }
                else {
                    this._actions = [];
                    this._isActionsDefined = true;
                }
                
                if (source.messages !== undefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
                
                if (source.conflict !== undefined) {
                    this._conflict = source.conflict;
                    this._isConflictDefined = true;
                }
                else {
                    this._conflict = new ConflictScenario();
                    this._isConflictDefined = true;
                }
            }
        }
        else {
            this._actions = [];
            this._isActionsDefined = true;
        
            this._messages = [];
            this._isMessagesDefined = true;
        
            this._conflict = new ConflictScenario();
            this._isConflictDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return UpdateResult.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isActionsDefined && this.actions !== undefined)
            definedProperties.push('actions');
        if (this.isMessagesDefined && this.messages !== undefined)
            definedProperties.push('messages');
        if (this.isConflictDefined && this.conflict !== undefined)
            definedProperties.push('conflict');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'actions':
                return this.actions;
            case 'messages':
                return this.messages;
            case 'conflict':
                return this.conflict;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isActionsDefined && this.actions !== undefined) {
            if (this.actions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('actions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.actions.length));
                for (let i = 0; i < this.actions.length; i++) {
                    const item = this.actions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('actions', 'null'));
            }
        }
        
        if (this.isMessagesDefined && this.messages !== undefined) {
            if (this.messages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.messages.length));
                for (let i = 0; i < this.messages.length; i++) {
                    const item = this.messages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'null'));
            }
        }
        
        if (this.isConflictDefined && this.conflict !== undefined) {
            if (this.conflict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.conflict.getModelName(), this.conflict.getDefinedPropertyNames().length));
                this.conflict.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflict', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'actions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.actions = [];
                
                    let item: TransientAction = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === TransientAction.modelName) {
                                item = new TransientAction();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${TransientAction.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'actions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'actions'`);
                        }
                
                        this.actions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'actions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'actions'`);
                }
        
                return true;
            case 'messages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.messages = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'messages' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'messages'`);
                        }
                
                        this.messages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'messages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'messages'`);
                }
        
                return true;
            case 'conflict':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConflictScenario.modelName) {
                        this.conflict = new ConflictScenario();
                        this.conflict.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenario.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'conflict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'conflict'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // actions
        if (this.isActionsDefined && this.actions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.actions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.actions.length);
            
                for (const item of this.actions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // messages
        if (this.isMessagesDefined && this.messages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.messages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.messages.length);
            
                for (const item of this.messages) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // conflict
        if (this.isConflictDefined && this.conflict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.conflict === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.conflict.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // actions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: TransientAction;
            
                    this.actions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new TransientAction();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.actions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // messages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.messages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.messages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // conflict
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.conflict = new ConflictScenario();
                    this.conflict.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): UpdateResult {
        // Method Implementation: simple
        const schema = UpdateResultZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: UpdateResultZodSchemaShape): UpdateResult {
        // Method Implementation: parseZodSchema
        return new UpdateResult({
            actions: schema.actions?.map(i => TransientAction.parseZodSchema(i)),
            messages: schema.messages?.map(i => i),
            conflict: schema.conflict && ConflictScenario.parseZodSchema(schema.conflict)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            actions: this.isActionsDefined ? (this.actions?.map(item => item)) : undefined,
            messages: this.isMessagesDefined ? (this.messages?.map(item => (item === null ? undefined : item))) : undefined,
            conflict: this.isConflictDefined ? (this.conflict) : undefined,
        };
    }

    public clone(): UpdateResult {
        // Method Implementation: clone
        return new UpdateResult({
            actions: this.isActionsDefined ? this.actions?.map(i => i.clone()) : undefined,
            messages: this.isMessagesDefined ? this.messages?.map(i => i) : undefined,
            conflict: this.isConflictDefined ? this.conflict.clone() : undefined
        });
    }
}

export class TransientAction implements ITransientAction {
    public static readonly modelName: string = 'TransientAction';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'type', 'properties' ];

    public get modelName(): string {
        // Method Implementation: simple
        return TransientAction.modelName;
    }

    private _name: string;

    private _type: string;

    private _properties: Record<string, string>;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get type(): string {
        // Method Implementation: simple
        return this._type;
    }

    public get properties(): Record<string, string> {
        // Method Implementation: simple
        return this._properties;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set type(value: string) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set properties(value: Record<string, string>) {
        // Method Implementation: simple
        this._properties = value;
        this._isPropertiesDefined = true;
    }

    private _isNameDefined: boolean;

    private _isTypeDefined: boolean;

    private _isPropertiesDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isPropertiesDefined(): boolean {
        // Method Implementation: simple
        return this._isPropertiesDefined;
    }

    public constructor();
    public constructor(source: ITransientAction);
    public constructor(source: Partial<Pick<TransientAction, 'name' | 'type' | 'properties'>>);
    public constructor(source?: ITransientAction | Partial<Pick<TransientAction, 'name' | 'type' | 'properties'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTransientActionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.isPropertiesDefined) {
                    this._properties = source.properties;
                    this._isPropertiesDefined = true;
                }
                else {
                    this._properties = {};
                    this._isPropertiesDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = '';
                    this._isTypeDefined = false;
                }
                
                if (source.properties !== undefined) {
                    this._properties = source.properties;
                    this._isPropertiesDefined = true;
                }
                else {
                    this._properties = {};
                    this._isPropertiesDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._type = '';
            this._isTypeDefined = false;
        
            this._properties = {};
            this._isPropertiesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return TransientAction.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isPropertiesDefined && this.properties !== undefined)
            definedProperties.push('properties');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'type':
                return this.type;
            case 'properties':
                return this.properties;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isPropertiesDefined && this.properties !== undefined) {
            if (this.properties !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('properties', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.properties).length));
                for (let key in this.properties) {
                    const dictionaryValue = this.properties[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'string' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/string
                        writer.writeStringValue(dictionaryValue);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('properties', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.type = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'type' cannot be null`);
                    this.type = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'properties':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.properties = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: string = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'properties' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'properties'`);
                        }
                
                        // Value Implementation: string
                        if (dictionaryItemHeader.valueType === 'string') {
                            value = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'properties' cannot be null`);
                            value = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'properties'`);
                        }
                
                        this.properties[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'properties' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'properties'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.type === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.type);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // properties
        if (this.isPropertiesDefined && this.properties !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.properties === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.properties).length);
            
                for (let key in this.properties) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: string
                    if (this.properties[key] === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(this.properties[key]);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.type = <any>null;
                    break;
                case 1:
                    this.type = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // properties
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: string;
            
                    this.properties = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                value = <any>null;
                                break;
                            case 1:
                                value = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.properties[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): TransientAction {
        // Method Implementation: simple
        const schema = TransientActionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TransientActionZodSchemaShape): TransientAction {
        // Method Implementation: parseZodSchema
        return new TransientAction({
            name: schema.name,
            type: schema.type,
            properties: schema.properties
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            type: this.isTypeDefined ? ((this.type === null ? undefined : this.type)) : undefined,
            properties: this.isPropertiesDefined ? (this.properties) : undefined,
        };
    }

    public clone(): TransientAction {
        // Method Implementation: clone
        return new TransientAction({
            name: this.isNameDefined ? this.name : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            properties: this.isPropertiesDefined ? this.properties : undefined
        });
    }
}

export class ConflictScenario implements IConflictScenario {
    public static readonly modelName: string = 'ConflictScenario';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'changedVariables', 'conflictedVariables' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenario.modelName;
    }

    private _changedVariables: ConflictScenarioChangedVariable[];

    private _conflictedVariables: ConflictScenarioConflictedVariable[];

    public get changedVariables(): ConflictScenarioChangedVariable[] {
        // Method Implementation: simple
        return this._changedVariables;
    }

    public get conflictedVariables(): ConflictScenarioConflictedVariable[] {
        // Method Implementation: simple
        return this._conflictedVariables;
    }

    public set changedVariables(value: ConflictScenarioChangedVariable[]) {
        // Method Implementation: simple
        this._changedVariables = value;
        this._isChangedVariablesDefined = true;
    }

    public set conflictedVariables(value: ConflictScenarioConflictedVariable[]) {
        // Method Implementation: simple
        this._conflictedVariables = value;
        this._isConflictedVariablesDefined = true;
    }

    private _isChangedVariablesDefined: boolean;

    private _isConflictedVariablesDefined: boolean;

    public get isChangedVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isChangedVariablesDefined;
    }

    public get isConflictedVariablesDefined(): boolean {
        // Method Implementation: simple
        return this._isConflictedVariablesDefined;
    }

    public constructor();
    public constructor(source: IConflictScenario);
    public constructor(source: Partial<Pick<ConflictScenario, 'changedVariables' | 'conflictedVariables'>>);
    public constructor(source?: IConflictScenario | Partial<Pick<ConflictScenario, 'changedVariables' | 'conflictedVariables'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioModel(source)) {
                if (source.isChangedVariablesDefined) {
                    this._changedVariables = source.changedVariables;
                    this._isChangedVariablesDefined = true;
                }
                else {
                    this._changedVariables = [];
                    this._isChangedVariablesDefined = true;
                }
                
                if (source.isConflictedVariablesDefined) {
                    this._conflictedVariables = source.conflictedVariables;
                    this._isConflictedVariablesDefined = true;
                }
                else {
                    this._conflictedVariables = [];
                    this._isConflictedVariablesDefined = true;
                }
            }
            else {
                if (source.changedVariables !== undefined) {
                    this._changedVariables = source.changedVariables;
                    this._isChangedVariablesDefined = true;
                }
                else {
                    this._changedVariables = [];
                    this._isChangedVariablesDefined = true;
                }
                
                if (source.conflictedVariables !== undefined) {
                    this._conflictedVariables = source.conflictedVariables;
                    this._isConflictedVariablesDefined = true;
                }
                else {
                    this._conflictedVariables = [];
                    this._isConflictedVariablesDefined = true;
                }
            }
        }
        else {
            this._changedVariables = [];
            this._isChangedVariablesDefined = true;
        
            this._conflictedVariables = [];
            this._isConflictedVariablesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenario.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isChangedVariablesDefined && this.changedVariables !== undefined)
            definedProperties.push('changedVariables');
        if (this.isConflictedVariablesDefined && this.conflictedVariables !== undefined)
            definedProperties.push('conflictedVariables');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'changedVariables':
                return this.changedVariables;
            case 'conflictedVariables':
                return this.conflictedVariables;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isChangedVariablesDefined && this.changedVariables !== undefined) {
            if (this.changedVariables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('changedVariables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.changedVariables.length));
                for (let i = 0; i < this.changedVariables.length; i++) {
                    const item = this.changedVariables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('changedVariables', 'null'));
            }
        }
        
        if (this.isConflictedVariablesDefined && this.conflictedVariables !== undefined) {
            if (this.conflictedVariables !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflictedVariables', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.conflictedVariables.length));
                for (let i = 0; i < this.conflictedVariables.length; i++) {
                    const item = this.conflictedVariables[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('conflictedVariables', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'changedVariables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.changedVariables = [];
                
                    let item: ConflictScenarioChangedVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConflictScenarioChangedVariable.modelName) {
                                item = new ConflictScenarioChangedVariable();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenarioChangedVariable.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'changedVariables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'changedVariables'`);
                        }
                
                        this.changedVariables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'changedVariables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'changedVariables'`);
                }
        
                return true;
            case 'conflictedVariables':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.conflictedVariables = [];
                
                    let item: ConflictScenarioConflictedVariable = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ConflictScenarioConflictedVariable.modelName) {
                                item = new ConflictScenarioConflictedVariable();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConflictScenarioConflictedVariable.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'conflictedVariables' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'conflictedVariables'`);
                        }
                
                        this.conflictedVariables.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'conflictedVariables' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'conflictedVariables'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // changedVariables
        if (this.isChangedVariablesDefined && this.changedVariables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.changedVariables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.changedVariables.length);
            
                for (const item of this.changedVariables) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // conflictedVariables
        if (this.isConflictedVariablesDefined && this.conflictedVariables !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.conflictedVariables === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.conflictedVariables.length);
            
                for (const item of this.conflictedVariables) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // changedVariables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConflictScenarioChangedVariable;
            
                    this.changedVariables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConflictScenarioChangedVariable();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.changedVariables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // conflictedVariables
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ConflictScenarioConflictedVariable;
            
                    this.conflictedVariables = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ConflictScenarioConflictedVariable();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.conflictedVariables.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenario {
        // Method Implementation: simple
        const schema = ConflictScenarioZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioZodSchemaShape): ConflictScenario {
        // Method Implementation: parseZodSchema
        return new ConflictScenario({
            changedVariables: schema.changedVariables?.map(i => ConflictScenarioChangedVariable.parseZodSchema(i)),
            conflictedVariables: schema.conflictedVariables?.map(i => ConflictScenarioConflictedVariable.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            changedVariables: this.isChangedVariablesDefined ? (this.changedVariables?.map(item => item)) : undefined,
            conflictedVariables: this.isConflictedVariablesDefined ? (this.conflictedVariables?.map(item => item)) : undefined,
        };
    }

    public clone(): ConflictScenario {
        // Method Implementation: clone
        return new ConflictScenario({
            changedVariables: this.isChangedVariablesDefined ? this.changedVariables?.map(i => i.clone()) : undefined,
            conflictedVariables: this.isConflictedVariablesDefined ? this.conflictedVariables?.map(i => i.clone()) : undefined
        });
    }
}

export class ConflictScenarioChangedVariable implements IConflictScenarioChangedVariable {
    public static readonly modelName: string = 'ConflictScenarioChangedVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'oldValue', 'newValue' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenarioChangedVariable.modelName;
    }

    private _source: string;

    private _oldValue: string;

    private _newValue: string;

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get oldValue(): string {
        // Method Implementation: simple
        return this._oldValue;
    }

    public get newValue(): string {
        // Method Implementation: simple
        return this._newValue;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set oldValue(value: string) {
        // Method Implementation: simple
        this._oldValue = value;
        this._isOldValueDefined = true;
    }

    public set newValue(value: string) {
        // Method Implementation: simple
        this._newValue = value;
        this._isNewValueDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isOldValueDefined: boolean;

    private _isNewValueDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isOldValueDefined(): boolean {
        // Method Implementation: simple
        return this._isOldValueDefined;
    }

    public get isNewValueDefined(): boolean {
        // Method Implementation: simple
        return this._isNewValueDefined;
    }

    public constructor();
    public constructor(source: IConflictScenarioChangedVariable);
    public constructor(source: Partial<Pick<ConflictScenarioChangedVariable, 'source' | 'oldValue' | 'newValue'>>);
    public constructor(source?: IConflictScenarioChangedVariable | Partial<Pick<ConflictScenarioChangedVariable, 'source' | 'oldValue' | 'newValue'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioChangedVariableModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isOldValueDefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.isNewValueDefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.oldValue !== undefined) {
                    this._oldValue = source.oldValue;
                    this._isOldValueDefined = true;
                }
                else {
                    this._oldValue = '';
                    this._isOldValueDefined = false;
                }
                
                if (source.newValue !== undefined) {
                    this._newValue = source.newValue;
                    this._isNewValueDefined = true;
                }
                else {
                    this._newValue = '';
                    this._isNewValueDefined = false;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._oldValue = '';
            this._isOldValueDefined = false;
        
            this._newValue = '';
            this._isNewValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenarioChangedVariable.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isOldValueDefined && this.oldValue !== undefined)
            definedProperties.push('oldValue');
        if (this.isNewValueDefined && this.newValue !== undefined)
            definedProperties.push('newValue');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'oldValue':
                return this.oldValue;
            case 'newValue':
                return this.newValue;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            if (this.oldValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.oldValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('oldValue', 'null'));
            }
        }
        
        if (this.isNewValueDefined && this.newValue !== undefined) {
            if (this.newValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.newValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('newValue', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'oldValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.oldValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'oldValue' cannot be null`);
                    this.oldValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'oldValue'`);
                }
        
                return true;
            case 'newValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.newValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'newValue' cannot be null`);
                    this.newValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'newValue'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // oldValue
        if (this.isOldValueDefined && this.oldValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.oldValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.oldValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // newValue
        if (this.isNewValueDefined && this.newValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.newValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.newValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // oldValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.oldValue = <any>null;
                    break;
                case 1:
                    this.oldValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // newValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.newValue = <any>null;
                    break;
                case 1:
                    this.newValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenarioChangedVariable {
        // Method Implementation: simple
        const schema = ConflictScenarioChangedVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioChangedVariableZodSchemaShape): ConflictScenarioChangedVariable {
        // Method Implementation: parseZodSchema
        return new ConflictScenarioChangedVariable({
            source: schema.source,
            oldValue: schema.oldValue,
            newValue: schema.newValue
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            oldValue: this.isOldValueDefined ? ((this.oldValue === null ? undefined : this.oldValue)) : undefined,
            newValue: this.isNewValueDefined ? ((this.newValue === null ? undefined : this.newValue)) : undefined,
        };
    }

    public clone(): ConflictScenarioChangedVariable {
        // Method Implementation: clone
        return new ConflictScenarioChangedVariable({
            source: this.isSourceDefined ? this.source : undefined,
            oldValue: this.isOldValueDefined ? this.oldValue : undefined,
            newValue: this.isNewValueDefined ? this.newValue : undefined
        });
    }
}

export class ConflictScenarioConflictedVariable implements IConflictScenarioConflictedVariable {
    public static readonly modelName: string = 'ConflictScenarioConflictedVariable';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'messages' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ConflictScenarioConflictedVariable.modelName;
    }

    private _source: string;

    private _messages: string[];

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get messages(): string[] {
        // Method Implementation: simple
        return this._messages;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set messages(value: string[]) {
        // Method Implementation: simple
        this._messages = value;
        this._isMessagesDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isMessagesDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isMessagesDefined(): boolean {
        // Method Implementation: simple
        return this._isMessagesDefined;
    }

    public constructor();
    public constructor(source: IConflictScenarioConflictedVariable);
    public constructor(source: Partial<Pick<ConflictScenarioConflictedVariable, 'source' | 'messages'>>);
    public constructor(source?: IConflictScenarioConflictedVariable | Partial<Pick<ConflictScenarioConflictedVariable, 'source' | 'messages'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isConflictScenarioConflictedVariableModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isMessagesDefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.messages !== undefined) {
                    this._messages = source.messages;
                    this._isMessagesDefined = true;
                }
                else {
                    this._messages = [];
                    this._isMessagesDefined = true;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._messages = [];
            this._isMessagesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ConflictScenarioConflictedVariable.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isMessagesDefined && this.messages !== undefined)
            definedProperties.push('messages');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'messages':
                return this.messages;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isMessagesDefined && this.messages !== undefined) {
            if (this.messages !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.messages.length));
                for (let i = 0; i < this.messages.length; i++) {
                    const item = this.messages[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('string'));
                
                        // Implementation: axon/write/string
                        writer.writeStringValue(item);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('messages', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'messages':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.messages = [];
                
                    let item: string = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: string
                        if (arrayItemHeader.type === 'string') {
                            item = reader.readStringValue();
                        }
                        else if (arrayItemHeader.type === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'messages' cannot be null`);
                            item = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'messages'`);
                        }
                
                        this.messages.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'messages' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'messages'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // messages
        if (this.isMessagesDefined && this.messages !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.messages === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.messages.length);
            
                for (const item of this.messages) {
                    // Item Implementation: string
                    if (item === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(item);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // messages
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: string;
            
                    this.messages = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                item = <any>null;
                                break;
                            case 1:
                                item = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.messages.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): ConflictScenarioConflictedVariable {
        // Method Implementation: simple
        const schema = ConflictScenarioConflictedVariableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ConflictScenarioConflictedVariableZodSchemaShape): ConflictScenarioConflictedVariable {
        // Method Implementation: parseZodSchema
        return new ConflictScenarioConflictedVariable({
            source: schema.source,
            messages: schema.messages?.map(i => i)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            messages: this.isMessagesDefined ? (this.messages?.map(item => (item === null ? undefined : item))) : undefined,
        };
    }

    public clone(): ConflictScenarioConflictedVariable {
        // Method Implementation: clone
        return new ConflictScenarioConflictedVariable({
            source: this.isSourceDefined ? this.source : undefined,
            messages: this.isMessagesDefined ? this.messages?.map(i => i) : undefined
        });
    }
}

export class EntityStateManager implements IEntityStateManager {
    public static readonly modelName: string = 'EntityStateManager';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'currentStateIndex', 'states' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityStateManager.modelName;
    }

    private _currentStateIndex: number;

    private _states: EntityState[];

    public get currentStateIndex(): number {
        // Method Implementation: simple
        return this._currentStateIndex;
    }

    public get states(): EntityState[] {
        // Method Implementation: simple
        return this._states;
    }

    public set currentStateIndex(value: number) {
        // Method Implementation: simple
        this._currentStateIndex = value;
        this._isCurrentStateIndexDefined = true;
    }

    public set states(value: EntityState[]) {
        // Method Implementation: simple
        this._states = value;
        this._isStatesDefined = true;
    }

    private _isCurrentStateIndexDefined: boolean;

    private _isStatesDefined: boolean;

    public get isCurrentStateIndexDefined(): boolean {
        // Method Implementation: simple
        return this._isCurrentStateIndexDefined;
    }

    public get isStatesDefined(): boolean {
        // Method Implementation: simple
        return this._isStatesDefined;
    }

    public constructor();
    public constructor(source: IEntityStateManager);
    public constructor(source: Partial<Pick<EntityStateManager, 'currentStateIndex' | 'states'>>);
    public constructor(source?: IEntityStateManager | Partial<Pick<EntityStateManager, 'currentStateIndex' | 'states'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityStateManagerModel(source)) {
                if (source.isCurrentStateIndexDefined) {
                    this._currentStateIndex = source.currentStateIndex;
                    this._isCurrentStateIndexDefined = true;
                }
                else {
                    this._currentStateIndex = 0;
                    this._isCurrentStateIndexDefined = false;
                }
                
                if (source.isStatesDefined) {
                    this._states = source.states;
                    this._isStatesDefined = true;
                }
                else {
                    this._states = [];
                    this._isStatesDefined = true;
                }
            }
            else {
                if (source.currentStateIndex !== undefined) {
                    this._currentStateIndex = source.currentStateIndex;
                    this._isCurrentStateIndexDefined = true;
                }
                else {
                    this._currentStateIndex = 0;
                    this._isCurrentStateIndexDefined = false;
                }
                
                if (source.states !== undefined) {
                    this._states = source.states;
                    this._isStatesDefined = true;
                }
                else {
                    this._states = [];
                    this._isStatesDefined = true;
                }
            }
        }
        else {
            this._currentStateIndex = 0;
            this._isCurrentStateIndexDefined = false;
        
            this._states = [];
            this._isStatesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityStateManager.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isCurrentStateIndexDefined && this.currentStateIndex !== undefined)
            definedProperties.push('currentStateIndex');
        if (this.isStatesDefined && this.states !== undefined)
            definedProperties.push('states');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'currentStateIndex':
                return this.currentStateIndex;
            case 'states':
                return this.states;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isCurrentStateIndexDefined && this.currentStateIndex !== undefined) {
            if (this.currentStateIndex !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('currentStateIndex', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.currentStateIndex);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('currentStateIndex', 'null'));
            }
        }
        
        if (this.isStatesDefined && this.states !== undefined) {
            if (this.states !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('states', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.states.length));
                for (let i = 0; i < this.states.length; i++) {
                    const item = this.states[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('states', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'currentStateIndex':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.currentStateIndex = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'currentStateIndex' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'currentStateIndex'`);
                }
        
                return true;
            case 'states':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.states = [];
                
                    let item: EntityState = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === EntityState.modelName) {
                                item = new EntityState();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${EntityState.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'states' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'states'`);
                        }
                
                        this.states.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'states' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'states'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // currentStateIndex
        if (this.isCurrentStateIndexDefined && this.currentStateIndex !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.currentStateIndex);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // states
        if (this.isStatesDefined && this.states !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.states === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.states.length);
            
                for (const item of this.states) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // currentStateIndex
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.currentStateIndex = reader.readIntegerValue();
        }
        
        // states
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: EntityState;
            
                    this.states = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new EntityState();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.states.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): EntityStateManager {
        // Method Implementation: simple
        const schema = EntityStateManagerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityStateManagerZodSchemaShape): EntityStateManager {
        // Method Implementation: parseZodSchema
        return new EntityStateManager({
            currentStateIndex: schema.currentStateIndex,
            states: schema.states?.map(i => EntityState.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            currentStateIndex: this.isCurrentStateIndexDefined ? (this.currentStateIndex) : undefined,
            states: this.isStatesDefined ? (this.states?.map(item => item)) : undefined,
        };
    }

    public clone(): EntityStateManager {
        // Method Implementation: clone
        return new EntityStateManager({
            currentStateIndex: this.isCurrentStateIndexDefined ? this.currentStateIndex : undefined,
            states: this.isStatesDefined ? this.states?.map(i => i.clone()) : undefined
        });
    }
}

export class EntityState implements IEntityState {
    public static readonly modelName: string = 'EntityState';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'key' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityState.modelName;
    }

    private _key: string;

    public get key(): string {
        // Method Implementation: simple
        return this._key;
    }

    public set key(value: string) {
        // Method Implementation: simple
        this._key = value;
        this._isKeyDefined = true;
    }

    private _isKeyDefined: boolean;

    public get isKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isKeyDefined;
    }

    public constructor();
    public constructor(source: IEntityState);
    public constructor(source: Partial<Pick<EntityState, 'key'>>);
    public constructor(source?: IEntityState | Partial<Pick<EntityState, 'key'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityStateModel(source)) {
                if (source.isKeyDefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
            }
            else {
                if (source.key !== undefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
            }
        }
        else {
            this._key = '';
            this._isKeyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityState.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isKeyDefined && this.key !== undefined)
            definedProperties.push('key');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'key':
                return this.key;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isKeyDefined && this.key !== undefined) {
            if (this.key !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.key);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'key':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.key = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'key' cannot be null`);
                    this.key = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'key'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // key
        if (this.isKeyDefined && this.key !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.key === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.key);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // key
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.key = <any>null;
                    break;
                case 1:
                    this.key = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): EntityState {
        // Method Implementation: simple
        const schema = EntityStateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityStateZodSchemaShape): EntityState {
        // Method Implementation: parseZodSchema
        return new EntityState({
            key: schema.key
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            key: this.isKeyDefined ? ((this.key === null ? undefined : this.key)) : undefined,
        };
    }

    public clone(): EntityState {
        // Method Implementation: clone
        return new EntityState({
            key: this.isKeyDefined ? this.key : undefined
        });
    }
}

export class EntityDefault implements IEntityDefault {
    public static readonly modelName: string = 'EntityDefault';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'variable', 'value', 'restrict', 'userAssign' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityDefault.modelName;
    }

    private _variable: string;

    private _value: string;

    private _restrict: boolean;

    private _userAssign: boolean;

    public get variable(): string {
        // Method Implementation: simple
        return this._variable;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get restrict(): boolean {
        // Method Implementation: simple
        return this._restrict;
    }

    public get userAssign(): boolean {
        // Method Implementation: simple
        return this._userAssign;
    }

    public set variable(value: string) {
        // Method Implementation: simple
        this._variable = value;
        this._isVariableDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set restrict(value: boolean) {
        // Method Implementation: simple
        this._restrict = value;
        this._isRestrictDefined = true;
    }

    public set userAssign(value: boolean) {
        // Method Implementation: simple
        this._userAssign = value;
        this._isUserAssignDefined = true;
    }

    private _isVariableDefined: boolean;

    private _isValueDefined: boolean;

    private _isRestrictDefined: boolean;

    private _isUserAssignDefined: boolean;

    public get isVariableDefined(): boolean {
        // Method Implementation: simple
        return this._isVariableDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isRestrictDefined(): boolean {
        // Method Implementation: simple
        return this._isRestrictDefined;
    }

    public get isUserAssignDefined(): boolean {
        // Method Implementation: simple
        return this._isUserAssignDefined;
    }

    public constructor();
    public constructor(source: IEntityDefault);
    public constructor(source: Partial<Pick<EntityDefault, 'variable' | 'value' | 'restrict' | 'userAssign'>>);
    public constructor(source?: IEntityDefault | Partial<Pick<EntityDefault, 'variable' | 'value' | 'restrict' | 'userAssign'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityDefaultModel(source)) {
                if (source.isVariableDefined) {
                    this._variable = source.variable;
                    this._isVariableDefined = true;
                }
                else {
                    this._variable = '';
                    this._isVariableDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isRestrictDefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.isUserAssignDefined) {
                    this._userAssign = source.userAssign;
                    this._isUserAssignDefined = true;
                }
                else {
                    this._userAssign = false;
                    this._isUserAssignDefined = false;
                }
            }
            else {
                if (source.variable !== undefined) {
                    this._variable = source.variable;
                    this._isVariableDefined = true;
                }
                else {
                    this._variable = '';
                    this._isVariableDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.restrict !== undefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.userAssign !== undefined) {
                    this._userAssign = source.userAssign;
                    this._isUserAssignDefined = true;
                }
                else {
                    this._userAssign = false;
                    this._isUserAssignDefined = false;
                }
            }
        }
        else {
            this._variable = '';
            this._isVariableDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        
            this._restrict = false;
            this._isRestrictDefined = false;
        
            this._userAssign = false;
            this._isUserAssignDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityDefault.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isVariableDefined && this.variable !== undefined)
            definedProperties.push('variable');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isRestrictDefined && this.restrict !== undefined)
            definedProperties.push('restrict');
        if (this.isUserAssignDefined && this.userAssign !== undefined)
            definedProperties.push('userAssign');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'variable':
                return this.variable;
            case 'value':
                return this.value;
            case 'restrict':
                return this.restrict;
            case 'userAssign':
                return this.userAssign;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isVariableDefined && this.variable !== undefined) {
            if (this.variable !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variable', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.variable);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('variable', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isRestrictDefined && this.restrict !== undefined) {
            if (this.restrict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.restrict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'null'));
            }
        }
        
        if (this.isUserAssignDefined && this.userAssign !== undefined) {
            if (this.userAssign !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('userAssign', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.userAssign);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('userAssign', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'variable':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.variable = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'variable' cannot be null`);
                    this.variable = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'variable'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'restrict':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.restrict = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'restrict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'restrict'`);
                }
        
                return true;
            case 'userAssign':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.userAssign = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'userAssign' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'userAssign'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // variable
        if (this.isVariableDefined && this.variable !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.variable === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.variable);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // restrict
        if (this.isRestrictDefined && this.restrict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.restrict);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // userAssign
        if (this.isUserAssignDefined && this.userAssign !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.userAssign);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // variable
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.variable = <any>null;
                    break;
                case 1:
                    this.variable = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // restrict
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.restrict = reader.readBooleanValue();
        }
        
        // userAssign
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.userAssign = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): EntityDefault {
        // Method Implementation: simple
        const schema = EntityDefaultZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityDefaultZodSchemaShape): EntityDefault {
        // Method Implementation: parseZodSchema
        return new EntityDefault({
            variable: schema.variable,
            value: schema.value,
            restrict: schema.restrict,
            userAssign: schema.userAssign
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            variable: this.isVariableDefined ? ((this.variable === null ? undefined : this.variable)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            restrict: this.isRestrictDefined ? (this.restrict) : undefined,
            userAssign: this.isUserAssignDefined ? (this.userAssign) : undefined,
        };
    }

    public clone(): EntityDefault {
        // Method Implementation: clone
        return new EntityDefault({
            variable: this.isVariableDefined ? this.variable : undefined,
            value: this.isValueDefined ? this.value : undefined,
            restrict: this.isRestrictDefined ? this.restrict : undefined,
            userAssign: this.isUserAssignDefined ? this.userAssign : undefined
        });
    }
}

export class MacroTestResult implements IMacroTestResult {
    public static readonly modelName: string = 'MacroTestResult';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'resolvedValue', 'duration' ];

    public get modelName(): string {
        // Method Implementation: simple
        return MacroTestResult.modelName;
    }

    private _resolvedValue: string;

    private _duration: number;

    public get resolvedValue(): string {
        // Method Implementation: simple
        return this._resolvedValue;
    }

    public get duration(): number {
        // Method Implementation: simple
        return this._duration;
    }

    public set resolvedValue(value: string) {
        // Method Implementation: simple
        this._resolvedValue = value;
        this._isResolvedValueDefined = true;
    }

    public set duration(value: number) {
        // Method Implementation: simple
        this._duration = value;
        this._isDurationDefined = true;
    }

    private _isResolvedValueDefined: boolean;

    private _isDurationDefined: boolean;

    public get isResolvedValueDefined(): boolean {
        // Method Implementation: simple
        return this._isResolvedValueDefined;
    }

    public get isDurationDefined(): boolean {
        // Method Implementation: simple
        return this._isDurationDefined;
    }

    public constructor();
    public constructor(source: IMacroTestResult);
    public constructor(source: Partial<Pick<MacroTestResult, 'resolvedValue' | 'duration'>>);
    public constructor(source?: IMacroTestResult | Partial<Pick<MacroTestResult, 'resolvedValue' | 'duration'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isMacroTestResultModel(source)) {
                if (source.isResolvedValueDefined) {
                    this._resolvedValue = source.resolvedValue;
                    this._isResolvedValueDefined = true;
                }
                else {
                    this._resolvedValue = '';
                    this._isResolvedValueDefined = false;
                }
                
                if (source.isDurationDefined) {
                    this._duration = source.duration;
                    this._isDurationDefined = true;
                }
                else {
                    this._duration = 0;
                    this._isDurationDefined = false;
                }
            }
            else {
                if (source.resolvedValue !== undefined) {
                    this._resolvedValue = source.resolvedValue;
                    this._isResolvedValueDefined = true;
                }
                else {
                    this._resolvedValue = '';
                    this._isResolvedValueDefined = false;
                }
                
                if (source.duration !== undefined) {
                    this._duration = source.duration;
                    this._isDurationDefined = true;
                }
                else {
                    this._duration = 0;
                    this._isDurationDefined = false;
                }
            }
        }
        else {
            this._resolvedValue = '';
            this._isResolvedValueDefined = false;
        
            this._duration = 0;
            this._isDurationDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return MacroTestResult.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isResolvedValueDefined && this.resolvedValue !== undefined)
            definedProperties.push('resolvedValue');
        if (this.isDurationDefined && this.duration !== undefined)
            definedProperties.push('duration');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'resolvedValue':
                return this.resolvedValue;
            case 'duration':
                return this.duration;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isResolvedValueDefined && this.resolvedValue !== undefined) {
            if (this.resolvedValue !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('resolvedValue', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.resolvedValue);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('resolvedValue', 'null'));
            }
        }
        
        if (this.isDurationDefined && this.duration !== undefined) {
            if (this.duration !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('duration', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.duration);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('duration', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'resolvedValue':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.resolvedValue = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'resolvedValue' cannot be null`);
                    this.resolvedValue = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'resolvedValue'`);
                }
        
                return true;
            case 'duration':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.duration = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.duration = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'duration' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'duration'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // resolvedValue
        if (this.isResolvedValueDefined && this.resolvedValue !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.resolvedValue === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.resolvedValue);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // duration
        if (this.isDurationDefined && this.duration !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.duration);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // resolvedValue
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.resolvedValue = <any>null;
                    break;
                case 1:
                    this.resolvedValue = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // duration
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.duration = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): MacroTestResult {
        // Method Implementation: simple
        const schema = MacroTestResultZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MacroTestResultZodSchemaShape): MacroTestResult {
        // Method Implementation: parseZodSchema
        return new MacroTestResult({
            resolvedValue: schema.resolvedValue,
            duration: schema.duration
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            resolvedValue: this.isResolvedValueDefined ? ((this.resolvedValue === null ? undefined : this.resolvedValue)) : undefined,
            duration: this.isDurationDefined ? (this.duration) : undefined,
        };
    }

    public clone(): MacroTestResult {
        // Method Implementation: clone
        return new MacroTestResult({
            resolvedValue: this.isResolvedValueDefined ? this.resolvedValue : undefined,
            duration: this.isDurationDefined ? this.duration : undefined
        });
    }
}

export interface IProviderService {
    createEntity(entity: ModelEntity.Entity): Promise<Entity>;

    createEntity(entity: ModelEntity.Entity, defaults: EntityDefault[]): Promise<Entity>;

    createChildEntity(entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes): Promise<Entity>;

    createChildEntity(entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes, defaults: EntityDefault[]): Promise<Entity>;

    createChildEntity(entity: Entity, childTemplateName: string, constraintModels: LinkedConstraintModel[], createdChildType: CreatedChildTypes): Promise<Entity>;
}

export interface ILifecycleService {
    sanitize(entity: Entity): Promise<UpdateResult>;

    update(entity: Entity, updates: string[]): Promise<UpdateResult>;

    applyExport(entity: Entity, sourceExport: Export.Export, exportModel: ModelExport.Export): Promise<UpdateResult>;

    applyIntegration(entity: Entity, integrationEntity: ProjectionIntegration.Entity, path: string): Promise<UpdateResult>;

    addChild(entity: Entity, childTemplateName: string): Promise<UpdateResult>;

    snapshotState(entity: Entity): Promise<void>;

    restoreState(entity: Entity, deltaIndex: number): Promise<UpdateResult>;

    resolveStateManager(entity: Entity): Promise<EntityStateManager>;
}

export interface ICacheService {
    load(key: string): Promise<Entity>;

    save(entity: Entity, key: string): Promise<string>;

    delete(key: string): Promise<void>;
}

export interface IDiagnosticService {
    resolveMacro(entity: Entity, macro: string): Promise<MacroTestResult>;

    resolveMacro(entity: Entity, macro: string, qualifier: string): Promise<MacroTestResult>;
}

export class ProviderServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public createEntity(entity: ModelEntity.Entity): Promise<Entity>;
    public createEntity(actionContext: Wellspring.ActionContext, entity: ModelEntity.Entity): Promise<Entity>;
    public createEntity(entity: ModelEntity.Entity, defaults: EntityDefault[]): Promise<Entity>;
    public createEntity(actionContext: Wellspring.ActionContext, entity: ModelEntity.Entity, defaults: EntityDefault[]): Promise<Entity>;
    public async createEntity(...args: unknown[]): Promise<Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"ModelEntity.Entity"}
            Zod.instanceof(ModelEntity.Entity)
        ]).check(args)) {
            const [ entity ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("349e78be16586f7a968cbfa1d2b8098e75907fa2c4b3c2c8fb347a0c198f3552");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"ModelEntity.Entity"}
            Zod.instanceof(ModelEntity.Entity), 
            // {"template":"array","itemImplementation":{"template":"model","className":"EntityDefault"}}
            Zod.array(Zod.instanceof(EntityDefault))
        ]).check(args)) {
            const [ entity, defaults ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("446c9c527b531265657a7b4394a80064f0632b88adc7f6c51864b1fbfdd11121");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (defaults && defaults !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (defaults === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(defaults.length);
                        
                            for (const item of defaults) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                defaults: JSON.parse(JSON.stringify(defaults))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createChildEntity(entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes): Promise<Entity>;
    public createChildEntity(actionContext: Wellspring.ActionContext, entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes): Promise<Entity>;
    public createChildEntity(entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes, defaults: EntityDefault[]): Promise<Entity>;
    public createChildEntity(actionContext: Wellspring.ActionContext, entity: Entity, childTemplateName: string, createdChildType: CreatedChildTypes, defaults: EntityDefault[]): Promise<Entity>;
    public createChildEntity(entity: Entity, childTemplateName: string, constraintModels: LinkedConstraintModel[], createdChildType: CreatedChildTypes): Promise<Entity>;
    public createChildEntity(actionContext: Wellspring.ActionContext, entity: Entity, childTemplateName: string, constraintModels: LinkedConstraintModel[], createdChildType: CreatedChildTypes): Promise<Entity>;
    public async createChildEntity(...args: unknown[]): Promise<Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"enum","enumName":"CreatedChildTypes","values":["Descendant","Orphan"]}
            Zod.string()
        ]).check(args)) {
            const [ entity, childTemplateName, createdChildType ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createChildEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createChildEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("87f70fec50dddd4860bdfa84ea0bc343fb24198007a774f41ee93e9e4ff38052");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (childTemplateName && childTemplateName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (childTemplateName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(childTemplateName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (createdChildType && createdChildType !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/enum
                        writer.writeEnumValue(createdChildType);
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                                createdChildType: JSON.parse(JSON.stringify(createdChildType))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createChildEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"enum","enumName":"CreatedChildTypes","values":["Descendant","Orphan"]}
            Zod.string(), 
            // {"template":"array","itemImplementation":{"template":"model","className":"EntityDefault"}}
            Zod.array(Zod.instanceof(EntityDefault))
        ]).check(args)) {
            const [ entity, childTemplateName, createdChildType, defaults ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createChildEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createChildEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("90a691f10ada62306bce98b2878a70027958820fac63677fcc621550a741090c");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (childTemplateName && childTemplateName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (childTemplateName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(childTemplateName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (createdChildType && createdChildType !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/enum
                        writer.writeEnumValue(createdChildType);
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (defaults && defaults !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (defaults === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(defaults.length);
                        
                            for (const item of defaults) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                                createdChildType: JSON.parse(JSON.stringify(createdChildType)),
                                defaults: JSON.parse(JSON.stringify(defaults))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createChildEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType)),
                        defaults: JSON.parse(JSON.stringify(defaults))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"array","itemImplementation":{"template":"model","className":"LinkedConstraintModel"}}
            Zod.array(Zod.instanceof(LinkedConstraintModel)), 
            // {"template":"enum","enumName":"CreatedChildTypes","values":["Descendant","Orphan"]}
            Zod.string()
        ]).check(args)) {
            const [ entity, childTemplateName, constraintModels, createdChildType ] = args;
        
            const activity = new Wellspring.DistributedActivity('provider.createChildEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createChildEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('provider', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("6594ae11ae910e5c5a7fea1ed1bf59819d59802c895a02422ea22c1da485f311");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (childTemplateName && childTemplateName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (childTemplateName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(childTemplateName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (constraintModels && constraintModels !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (constraintModels === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(constraintModels.length);
                        
                            for (const item of constraintModels) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (createdChildType && createdChildType !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/enum
                        writer.writeEnumValue(createdChildType);
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                                constraintModels: JSON.parse(JSON.stringify(constraintModels)),
                                createdChildType: JSON.parse(JSON.stringify(createdChildType))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "provider/createChildEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        constraintModels: JSON.parse(JSON.stringify(constraintModels)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `provider[${this.identifier}].createChildEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName)),
                        constraintModels: JSON.parse(JSON.stringify(constraintModels)),
                        createdChildType: JSON.parse(JSON.stringify(createdChildType))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class LifecycleServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public sanitize(entity: Entity): Promise<UpdateResult>;
    public sanitize(actionContext: Wellspring.ActionContext, entity: Entity): Promise<UpdateResult>;
    public async sanitize(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity)
        ]).check(args)) {
            const [ entity ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.sanitize');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('sanitize', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("443005dff2483844f80e90568990499d9485d7b26cc15c6028e96f72409579db");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].sanitize`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/sanitize" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].sanitize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].sanitize`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(entity: Entity, updates: string[]): Promise<UpdateResult>;
    public update(actionContext: Wellspring.ActionContext, entity: Entity, updates: string[]): Promise<UpdateResult>;
    public async update(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"array","itemImplementation":{"template":"string"}}
            Zod.array(Zod.string())
        ]).check(args)) {
            const [ entity, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("a7f7ad94cfe1ff9bcaf29712683e5721d1fad7867cef122cd63611b3c18f8599");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: string
                                if (item === null)
                                {
                                    writer.writeByteValue(0);
                                }
                                else
                                {
                                    writer.writeByteValue(1);
                                    writer.writeStringValue(item);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public applyExport(entity: Entity, sourceExport: Export.Export, exportModel: ModelExport.Export): Promise<UpdateResult>;
    public applyExport(actionContext: Wellspring.ActionContext, entity: Entity, sourceExport: Export.Export, exportModel: ModelExport.Export): Promise<UpdateResult>;
    public async applyExport(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"model","className":"Export.Export"}
            Zod.instanceof(Export.Export), 
            // {"template":"model","className":"ModelExport.Export"}
            Zod.instanceof(ModelExport.Export)
        ]).check(args)) {
            const [ entity, sourceExport, exportModel ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.applyExport');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('applyExport', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("650640504c49b62c28cbe2d9df3b60288169cd6e837c1e18a30e13bed2bcc81e");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (sourceExport && sourceExport !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (sourceExport === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            sourceExport.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (exportModel && exportModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (exportModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            exportModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].applyExport`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                sourceExport: JSON.parse(JSON.stringify(sourceExport)),
                                exportModel: JSON.parse(JSON.stringify(exportModel))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/applyExport" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].applyExport`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        sourceExport: JSON.parse(JSON.stringify(sourceExport)),
                        exportModel: JSON.parse(JSON.stringify(exportModel))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].applyExport`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        sourceExport: JSON.parse(JSON.stringify(sourceExport)),
                        exportModel: JSON.parse(JSON.stringify(exportModel))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public applyIntegration(entity: Entity, integrationEntity: ProjectionIntegration.Entity, path?: string): Promise<UpdateResult>;
    public applyIntegration(actionContext: Wellspring.ActionContext, entity: Entity, integrationEntity: ProjectionIntegration.Entity, path?: string): Promise<UpdateResult>;
    public async applyIntegration(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.union([
            Zod.tuple([
                // {"template":"model","className":"Entity"}
                Zod.instanceof(Entity), 
                // {"template":"model","className":"ProjectionIntegration.Entity"}
                Zod.instanceof(ProjectionIntegration.Entity)
            ]), 
            Zod.tuple([
                // {"template":"model","className":"Entity"}
                Zod.instanceof(Entity), 
                // {"template":"model","className":"ProjectionIntegration.Entity"}
                Zod.instanceof(ProjectionIntegration.Entity), 
                // {"template":"string"}
                Zod.string().optional()
            ])
        ]).check(args)) {
            const [ entity, integrationEntity, path ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.applyIntegration');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('applyIntegration', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("39df6d4907d67db02046ddca799b82df0ab48529b37cfb9090018a1da83b3a4d");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (integrationEntity && integrationEntity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (integrationEntity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            integrationEntity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (path && path !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (path === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(path);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].applyIntegration`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                integrationEntity: JSON.parse(JSON.stringify(integrationEntity)),
                                path: JSON.parse(JSON.stringify(path))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/applyIntegration" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].applyIntegration`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        integrationEntity: JSON.parse(JSON.stringify(integrationEntity)),
                        path: JSON.parse(JSON.stringify(path))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].applyIntegration`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        integrationEntity: JSON.parse(JSON.stringify(integrationEntity)),
                        path: JSON.parse(JSON.stringify(path))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public addChild(entity: Entity, childTemplateName: string): Promise<UpdateResult>;
    public addChild(actionContext: Wellspring.ActionContext, entity: Entity, childTemplateName: string): Promise<UpdateResult>;
    public async addChild(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ entity, childTemplateName ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.addChild');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('addChild', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("cb34c9d077e59a128c1dacccff8faaf2a968a8af61f1c491b960d7c2893637c8");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (childTemplateName && childTemplateName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (childTemplateName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(childTemplateName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].addChild`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                childTemplateName: JSON.parse(JSON.stringify(childTemplateName))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/addChild" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].addChild`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].addChild`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        childTemplateName: JSON.parse(JSON.stringify(childTemplateName))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public snapshotState(entity: Entity): Promise<void>;
    public snapshotState(actionContext: Wellspring.ActionContext, entity: Entity): Promise<void>;
    public async snapshotState(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity)
        ]).check(args)) {
            const [ entity ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.snapshotState');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('snapshotState', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("cab9442db637224d24de02e2421c98ea6cce7e57000f34f11650a0480a32b382");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].snapshotState`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/snapshotState" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].snapshotState`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].snapshotState`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public restoreState(entity: Entity, deltaIndex: number): Promise<UpdateResult>;
    public restoreState(actionContext: Wellspring.ActionContext, entity: Entity, deltaIndex: number): Promise<UpdateResult>;
    public async restoreState(...args: unknown[]): Promise<UpdateResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"number"}
            Zod.number()
        ]).check(args)) {
            const [ entity, deltaIndex ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.restoreState');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('restoreState', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(6);
                    writer.writeStringValue("b33462279f086e1329acb11d21527c39d25ec4d7e58ae25f825729fdb3e8646b");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (deltaIndex && deltaIndex !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/int32
                        writer.writeIntegerValue(deltaIndex);
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<UpdateResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].restoreState`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/restoreState" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: UpdateResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new UpdateResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].restoreState`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].restoreState`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public resolveStateManager(entity: Entity): Promise<EntityStateManager>;
    public resolveStateManager(actionContext: Wellspring.ActionContext, entity: Entity): Promise<EntityStateManager>;
    public async resolveStateManager(...args: unknown[]): Promise<EntityStateManager> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity)
        ]).check(args)) {
            const [ entity ] = args;
        
            const activity = new Wellspring.DistributedActivity('lifecycle.resolveStateManager');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('resolveStateManager', 'utf8'));
                requestMetadata.add('service', Buffer.from('lifecycle', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(7);
                    writer.writeStringValue("2618d74670bde4f72ff1e4e5950c30ed7a397675ead450e65351fa17ce24bda5");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<EntityStateManager>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].resolveStateManager`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "lifecycle/resolveStateManager" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: EntityStateManager;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new EntityStateManager();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `lifecycle[${this.identifier}].resolveStateManager`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `lifecycle[${this.identifier}].resolveStateManager`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class CacheServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public load(key: string): Promise<Entity>;
    public load(actionContext: Wellspring.ActionContext, key: string): Promise<Entity>;
    public async load(...args: unknown[]): Promise<Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ key ] = args;
        
            const activity = new Wellspring.DistributedActivity('cache.load');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('load', 'utf8'));
                requestMetadata.add('service', Buffer.from('cache', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("af1aa18011d81297b7a2a12ef7294aa8a1e63ccd168a9c8ae5ee6a253cd9ddc4");
        
                    if (key && key !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (key === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(key);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].load`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                key: JSON.parse(JSON.stringify(key))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "cache/load" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `cache[${this.identifier}].load`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        key: JSON.parse(JSON.stringify(key))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].load`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        key: JSON.parse(JSON.stringify(key))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public save(entity: Entity, key?: string): Promise<string>;
    public save(actionContext: Wellspring.ActionContext, entity: Entity, key?: string): Promise<string>;
    public async save(...args: unknown[]): Promise<string> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.union([
            Zod.tuple([
                // {"template":"model","className":"Entity"}
                Zod.instanceof(Entity)
            ]), 
            Zod.tuple([
                // {"template":"model","className":"Entity"}
                Zod.instanceof(Entity), 
                // {"template":"string"}
                Zod.string().optional()
            ])
        ]).check(args)) {
            const [ entity, key ] = args;
        
            const activity = new Wellspring.DistributedActivity('cache.save');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('save', 'utf8'));
                requestMetadata.add('service', Buffer.from('cache', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("146c22a61e0630999f13f6cd7e3ac19cac15a0cb794ec65bed604a4d18a8192b");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (key && key !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (key === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(key);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<string>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].save`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                key: JSON.parse(JSON.stringify(key))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "cache/save" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: string;
                    // Implementation: slipstream/read/string
                    switch (reader.readByteValue())
                    {
                        case 0:
                            result = <any>null;
                            break;
                        case 1:
                            result = reader.readStringValue();
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `cache[${this.identifier}].save`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        key: JSON.parse(JSON.stringify(key))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].save`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        key: JSON.parse(JSON.stringify(key))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public delete(key: string): Promise<void>;
    public delete(actionContext: Wellspring.ActionContext, key: string): Promise<void>;
    public async delete(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ key ] = args;
        
            const activity = new Wellspring.DistributedActivity('cache.delete');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('delete', 'utf8'));
                requestMetadata.add('service', Buffer.from('cache', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("ddadf7e151232a98b5257ed4e11cbdcd09e33616e29c7f4c12fccccf642e0389");
        
                    if (key && key !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (key === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(key);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].delete`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                key: JSON.parse(JSON.stringify(key))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "cache/delete" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `cache[${this.identifier}].delete`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        key: JSON.parse(JSON.stringify(key))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `cache[${this.identifier}].delete`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        key: JSON.parse(JSON.stringify(key))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class DiagnosticServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public resolveMacro(entity: Entity, macro: string): Promise<MacroTestResult>;
    public resolveMacro(actionContext: Wellspring.ActionContext, entity: Entity, macro: string): Promise<MacroTestResult>;
    public resolveMacro(entity: Entity, macro: string, qualifier: string): Promise<MacroTestResult>;
    public resolveMacro(actionContext: Wellspring.ActionContext, entity: Entity, macro: string, qualifier: string): Promise<MacroTestResult>;
    public async resolveMacro(...args: unknown[]): Promise<MacroTestResult> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ entity, macro ] = args;
        
            const activity = new Wellspring.DistributedActivity('diagnostic.resolveMacro');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('resolveMacro', 'utf8'));
                requestMetadata.add('service', Buffer.from('diagnostic', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("d6287d851d6fe647cc279ef053fc6834fc05f65b9175e877242cdcb42524a4ce");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (macro && macro !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (macro === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(macro);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<MacroTestResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `diagnostic[${this.identifier}].resolveMacro`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                macro: JSON.parse(JSON.stringify(macro))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "diagnostic/resolveMacro" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: MacroTestResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new MacroTestResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `diagnostic[${this.identifier}].resolveMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        macro: JSON.parse(JSON.stringify(macro))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `diagnostic[${this.identifier}].resolveMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        macro: JSON.parse(JSON.stringify(macro))
                    },
                    error
                });
        
                throw error;
            });
        }
        else if (Zod.tuple([
            // {"template":"model","className":"Entity"}
            Zod.instanceof(Entity), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ entity, macro, qualifier ] = args;
        
            const activity = new Wellspring.DistributedActivity('diagnostic.resolveMacro');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('resolveMacro', 'utf8'));
                requestMetadata.add('service', Buffer.from('diagnostic', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("2858d2ba7f1912cb341421399055ae6150684431976032a7191a7276b2d617e6");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (macro && macro !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (macro === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(macro);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (qualifier && qualifier !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (qualifier === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(qualifier);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<MacroTestResult>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `diagnostic[${this.identifier}].resolveMacro`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                macro: JSON.parse(JSON.stringify(macro)),
                                qualifier: JSON.parse(JSON.stringify(qualifier))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "diagnostic/resolveMacro" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: MacroTestResult;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new MacroTestResult();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `diagnostic[${this.identifier}].resolveMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        macro: JSON.parse(JSON.stringify(macro)),
                        qualifier: JSON.parse(JSON.stringify(qualifier))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `diagnostic[${this.identifier}].resolveMacro`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        macro: JSON.parse(JSON.stringify(macro)),
                        qualifier: JSON.parse(JSON.stringify(qualifier))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Entity':
                return new Entity();
            case 'Child':
                return new Child();
            case 'LinkedMapper':
                return new LinkedMapper();
            case 'LinkedConstraintModel':
                return new LinkedConstraintModel();
            case 'ConstraintOptionState':
                return new ConstraintOptionState();
            case 'StringConstraintValue':
                return new StringConstraintValue();
            case 'NumericConstraintValue':
                return new NumericConstraintValue();
            case 'BooleanConstraintValue':
                return new BooleanConstraintValue();
            case 'MultiStringConstraintValue':
                return new MultiStringConstraintValue();
            case 'MultiNumericConstraintValue':
                return new MultiNumericConstraintValue();
            case 'ConstraintValidationMessage':
                return new ConstraintValidationMessage();
            case 'StringVariable':
                return new StringVariable();
            case 'NumericVariable':
                return new NumericVariable();
            case 'BooleanVariable':
                return new BooleanVariable();
            case 'StringEnumerableVariable':
                return new StringEnumerableVariable();
            case 'NumericEnumerableVariable':
                return new NumericEnumerableVariable();
            case 'MultiStringVariable':
                return new MultiStringVariable();
            case 'MultiNumericVariable':
                return new MultiNumericVariable();
            case 'StringVariableOption':
                return new StringVariableOption();
            case 'NumericVariableOption':
                return new NumericVariableOption();
            case 'StringField':
                return new StringField();
            case 'NumericField':
                return new NumericField();
            case 'BooleanField':
                return new BooleanField();
            case 'StringEnumerableField':
                return new StringEnumerableField();
            case 'NumericEnumerableField':
                return new NumericEnumerableField();
            case 'StringFieldOption':
                return new StringFieldOption();
            case 'NumericFieldOption':
                return new NumericFieldOption();
            case 'Validator':
                return new Validator();
            case 'UpdateResult':
                return new UpdateResult();
            case 'TransientAction':
                return new TransientAction();
            case 'ConflictScenario':
                return new ConflictScenario();
            case 'ConflictScenarioChangedVariable':
                return new ConflictScenarioChangedVariable();
            case 'ConflictScenarioConflictedVariable':
                return new ConflictScenarioConflictedVariable();
            case 'EntityStateManager':
                return new EntityStateManager();
            case 'EntityState':
                return new EntityState();
            case 'EntityDefault':
                return new EntityDefault();
            case 'MacroTestResult':
                return new MacroTestResult();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Entity':
                return new Entity(source);
            case 'Child':
                return new Child(source);
            case 'LinkedMapper':
                return new LinkedMapper(source);
            case 'LinkedConstraintModel':
                return new LinkedConstraintModel(source);
            case 'ConstraintOptionState':
                return new ConstraintOptionState(source);
            case 'StringConstraintValue':
                return new StringConstraintValue(source);
            case 'NumericConstraintValue':
                return new NumericConstraintValue(source);
            case 'BooleanConstraintValue':
                return new BooleanConstraintValue(source);
            case 'MultiStringConstraintValue':
                return new MultiStringConstraintValue(source);
            case 'MultiNumericConstraintValue':
                return new MultiNumericConstraintValue(source);
            case 'ConstraintValidationMessage':
                return new ConstraintValidationMessage(source);
            case 'StringVariable':
                return new StringVariable(source);
            case 'NumericVariable':
                return new NumericVariable(source);
            case 'BooleanVariable':
                return new BooleanVariable(source);
            case 'StringEnumerableVariable':
                return new StringEnumerableVariable(source);
            case 'NumericEnumerableVariable':
                return new NumericEnumerableVariable(source);
            case 'MultiStringVariable':
                return new MultiStringVariable(source);
            case 'MultiNumericVariable':
                return new MultiNumericVariable(source);
            case 'StringVariableOption':
                return new StringVariableOption(source);
            case 'NumericVariableOption':
                return new NumericVariableOption(source);
            case 'StringField':
                return new StringField(source);
            case 'NumericField':
                return new NumericField(source);
            case 'BooleanField':
                return new BooleanField(source);
            case 'StringEnumerableField':
                return new StringEnumerableField(source);
            case 'NumericEnumerableField':
                return new NumericEnumerableField(source);
            case 'StringFieldOption':
                return new StringFieldOption(source);
            case 'NumericFieldOption':
                return new NumericFieldOption(source);
            case 'Validator':
                return new Validator(source);
            case 'UpdateResult':
                return new UpdateResult(source);
            case 'TransientAction':
                return new TransientAction(source);
            case 'ConflictScenario':
                return new ConflictScenario(source);
            case 'ConflictScenarioChangedVariable':
                return new ConflictScenarioChangedVariable(source);
            case 'ConflictScenarioConflictedVariable':
                return new ConflictScenarioConflictedVariable(source);
            case 'EntityStateManager':
                return new EntityStateManager(source);
            case 'EntityState':
                return new EntityState(source);
            case 'EntityDefault':
                return new EntityDefault(source);
            case 'MacroTestResult':
                return new MacroTestResult(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Entity':
                return Entity.parse(source);
            case 'Child':
                return Child.parse(source);
            case 'LinkedMapper':
                return LinkedMapper.parse(source);
            case 'LinkedConstraintModel':
                return LinkedConstraintModel.parse(source);
            case 'ConstraintOptionState':
                return ConstraintOptionState.parse(source);
            case 'StringConstraintValue':
                return StringConstraintValue.parse(source);
            case 'NumericConstraintValue':
                return NumericConstraintValue.parse(source);
            case 'BooleanConstraintValue':
                return BooleanConstraintValue.parse(source);
            case 'MultiStringConstraintValue':
                return MultiStringConstraintValue.parse(source);
            case 'MultiNumericConstraintValue':
                return MultiNumericConstraintValue.parse(source);
            case 'ConstraintValidationMessage':
                return ConstraintValidationMessage.parse(source);
            case 'StringVariable':
                return StringVariable.parse(source);
            case 'NumericVariable':
                return NumericVariable.parse(source);
            case 'BooleanVariable':
                return BooleanVariable.parse(source);
            case 'StringEnumerableVariable':
                return StringEnumerableVariable.parse(source);
            case 'NumericEnumerableVariable':
                return NumericEnumerableVariable.parse(source);
            case 'MultiStringVariable':
                return MultiStringVariable.parse(source);
            case 'MultiNumericVariable':
                return MultiNumericVariable.parse(source);
            case 'StringVariableOption':
                return StringVariableOption.parse(source);
            case 'NumericVariableOption':
                return NumericVariableOption.parse(source);
            case 'StringField':
                return StringField.parse(source);
            case 'NumericField':
                return NumericField.parse(source);
            case 'BooleanField':
                return BooleanField.parse(source);
            case 'StringEnumerableField':
                return StringEnumerableField.parse(source);
            case 'NumericEnumerableField':
                return NumericEnumerableField.parse(source);
            case 'StringFieldOption':
                return StringFieldOption.parse(source);
            case 'NumericFieldOption':
                return NumericFieldOption.parse(source);
            case 'Validator':
                return Validator.parse(source);
            case 'UpdateResult':
                return UpdateResult.parse(source);
            case 'TransientAction':
                return TransientAction.parse(source);
            case 'ConflictScenario':
                return ConflictScenario.parse(source);
            case 'ConflictScenarioChangedVariable':
                return ConflictScenarioChangedVariable.parse(source);
            case 'ConflictScenarioConflictedVariable':
                return ConflictScenarioConflictedVariable.parse(source);
            case 'EntityStateManager':
                return EntityStateManager.parse(source);
            case 'EntityState':
                return EntityState.parse(source);
            case 'EntityDefault':
                return EntityDefault.parse(source);
            case 'MacroTestResult':
                return MacroTestResult.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
